import React, { useState, useEffect, useRef, useCallback, forwardRef, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faSearch, faTimes, faSave, faTrash, faEdit,
    faChevronLeft, faChevronRight, faDollarSign, faCalendarAlt, faBuilding,
    faWallet, faStickyNote, faCheckCircle, faExclamationTriangle,
    faReceipt, faPrint, faSpinner, faArrowDown, faArrowUp,
    faRefresh, faTag, faStore, faChevronDown, faShoppingCart, faCartPlus,
    faUndo, faUserTie, faUsers, faTruck, faHandshake,
    faFileInvoice, faExchangeAlt, faBalanceScale,
    faHistory as faHistoryIcon, faFilter, faClock,
    faChartLine, faChartPie, faBoxes, faCashRegister,
    faHandHoldingUsd, faThLarge, faLayerGroup, faFileLines,
    faMinus, faArrowsAlt, faExpand, faChartBar, faPiggyBank,
    faPercentage, faUserCog, faUsersCog, faLink, faUnlink, faDivide,
    faMoneyCheckDollar, faCheck, faListAlt, faColumns,
    faTrendUp, faTrendDown, faArrowTrendUp, faArrowTrendDown, faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ==============================================
// CHART.JS IMPORTS
// ==============================================
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    RadialLinearScale,
    PolarAreaController,
    Decimation
} from 'chart.js';
import { Line, Bar, Pie, Doughnut, PolarArea, Radar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    RadialLinearScale,
    PolarAreaController,
    Decimation,
    ChartDataLabels
);

// ==============================================
// API ENDPOINTS
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
// New version forces the browser/service worker to load the FIFO payment API.
const TRANSACTIONS_API = `${API_BASE}/cosmetics_transactions.php?v=20260825-supplier-purchase-split-v1`;
const ACCOUNTS_API = `${API_BASE}/mechanics_account.php`;
const CUSTOMERS_API = `${API_BASE}/mechanics_customers.php`;
const SUPPLIERS_API = `${API_BASE}/mechanics_suppliers.php`;
const EMPLOYEES_API = `${API_BASE}/mechanics_employees.php`;
const PARTNERS_API = 'https://ourzestapp.online/oz_backend/solar_module/solar_partners.php';
const PRODUCTS_API = `https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php`;
const SALES_API = `${API_BASE}/cosmetics_sell.php`;
const PURCHASES_API = 'https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_purchases.php';
const RETURNS_API = `${API_BASE}/cosmetics_return.php`;
const SALES_PRINT_API = `${API_BASE}/mechanics_sell.php`;
const PURCHASES_PRINT_API = `${API_BASE}/mechanics_purchases.php`;
const RETURNS_PRINT_API = 'https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_return.php';
const PURCHASE_RETURNS_PRINT_API = 'https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_purchasereturn.php';
const BUSINESS_INFO_PRINT_API = 'https://ourzestapp.online/oz_backend/root_users.php';

const paymentMethodToType = (method) => ({
    cash: 'cash',
    bank_transfer: 'bank_account',
    bank_account: 'bank_account',
    mobile_payment: 'digital_wallet',
    digital_wallet: 'digital_wallet'
}[String(method || '').toLowerCase()] || 'cash');

const resolveTransactionPaymentDetails = (transaction = {}, accounts = []) => {
    const paymentType = transaction.payment_type || paymentMethodToType(transaction.payment_method);
    if (paymentType === 'cash') {
        return {
            paymentType: 'cash',
            provider: 'Cash',
            accountTitle: '',
            accountNumber: '',
            pdfText: 'Cash'
        };
    }

    const accountId = String(
        transaction.payment_account_id ?? transaction.account_id ?? ''
    ).trim();
    const account = accounts.find(item =>
        String(item.account_id ?? item.id ?? '').trim() === accountId
    );
    const provider = String(
        account?.provider_name ||
        transaction.payment_provider ||
        transaction.provider_name ||
        (paymentType === 'bank_account' ? 'Bank Account' : 'Mobile Payment')
    ).trim();
    const accountTitle = String(
        account?.account_title || transaction.payment_account_title || transaction.account_title || ''
    ).trim();
    const accountNumber = String(
        account?.account_number || transaction.payment_account_number || transaction.account_number || ''
    ).trim();
    const heading = [provider, accountTitle].filter(Boolean).join(' - ');

    return {
        paymentType,
        provider,
        accountTitle,
        accountNumber,
        pdfText: [heading, accountNumber].filter(Boolean).join('\n')
    };
};

const paymentTypeToMethod = (type) => ({
    cash: 'cash',
    bank_account: 'bank_transfer',
    digital_wallet: 'mobile_payment'
}[String(type || '').toLowerCase()] || 'cash');

// ==============================================
// HELPER FUNCTIONS
// ==============================================
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

const PAKISTAN_BANKS = [
    'Allied Bank Limited', 'Askari Bank Limited', 'Bank Alfalah Limited',
    'Bank AL Habib Limited', 'BankIslami Pakistan Limited', 'Dubai Islamic Bank Pakistan Limited',
    'Faysal Bank Limited', 'Habib Bank Limited (HBL)', 'Habib Metropolitan Bank Limited',
    'JS Bank Limited', 'MCB Bank Limited', 'MCB Islamic Bank Limited',
    'Meezan Bank Limited', 'National Bank of Pakistan', 'Sindh Bank Limited',
    'Samba Bank Limited', 'Soneri Bank Limited', 'Standard Chartered Bank (Pakistan) Limited',
    'The Bank of Khyber', 'The Bank of Punjab', 'United Bank Limited (UBL)',
    'Al Baraka Bank (Pakistan) Limited', 'Bank of China Limited - Pakistan',
    'Citibank N.A. - Pakistan', 'Deutsche Bank AG - Pakistan',
    'Industrial and Commercial Bank of China - Pakistan', 'First Women Bank Limited',
    'Bank Makramah Limited',
    'Zarai Taraqiati Bank Limited', 'Punjab Provincial Cooperative Bank Limited',
    'Easypaisa Bank Limited', 'Mashreq Bank Pakistan Limited',
    'Raqami Islamic Digital Bank Limited', 'HBL Microfinance Bank Limited',
    'NRSP Microfinance Bank Limited', 'Khushhali Microfinance Bank Limited',
    'LOLC Microfinance Bank Limited', 'U Microfinance Bank Limited',
    'Mobilink Microfinance Bank Limited', 'APNA Microfinance Bank Limited',
    'Halan Microfinance Bank Limited', 'Sindh Microfinance Bank Limited',
    'ABHI Microfinance Bank Limited', 'ASA Microfinance Bank (Pakistan) Limited',
    'Other / Custom Bank'
];

const PAKISTAN_DIGITAL_WALLETS = [
    'Easypaisa', 'JazzCash', 'UPaisa', 'HBL Konnect', 'UBL Omni', 'Zindigi',
    'NayaPay', 'SadaPay', 'OPay', 'Digitt+', 'OneZapp', 'Keenu', 'Hubpay',
    'YAP Pakistan', 'Other / Custom Wallet'
];

const createDailyAccount = (type, overrides = {}) => ({
    id: overrides.id || `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    provider_name: '',
    custom_provider: '',
    account_label: '',
    account_number: '',
    opening: '',
    closing: '',
    ...overrides
});

const normalizeDailyAccounts = (accounts, summary = {}) => {
    if (Array.isArray(accounts) && accounts.length > 0) {
        return accounts.map((account, index) => {
            const type = account.type === 'wallet' ? 'wallet' : 'bank';
            const catalog = type === 'wallet' ? PAKISTAN_DIGITAL_WALLETS : PAKISTAN_BANKS;
            const savedProvider = String(account.provider_name || '').trim();
            const isCatalogProvider = catalog.includes(savedProvider);
            return createDailyAccount(type, {
                ...account,
                id: account.id || `saved-${index}`,
                provider_name: isCatalogProvider ? savedProvider : (type === 'wallet' ? 'Other / Custom Wallet' : 'Other / Custom Bank'),
                custom_provider: isCatalogProvider ? '' : (account.custom_provider || savedProvider),
                opening: Number.parseFloat(account.opening) || 0,
                closing: Number.parseFloat(account.closing) || 0
            });
        });
    }
    const legacy = [];
    if ((Number.parseFloat(summary.bank_opening) || 0) !== 0 || (Number.parseFloat(summary.bank_closing) || 0) !== 0) {
        legacy.push(createDailyAccount('bank', {
            id: 'legacy-bank', provider_name: 'Other / Custom Bank', custom_provider: 'Legacy bank total',
            account_label: 'Legacy bank total', opening: Number.parseFloat(summary.bank_opening) || 0,
            closing: Number.parseFloat(summary.bank_closing) || 0
        }));
    }
    if ((Number.parseFloat(summary.wallet_opening) || 0) !== 0 || (Number.parseFloat(summary.wallet_closing) || 0) !== 0) {
        legacy.push(createDailyAccount('wallet', {
            id: 'legacy-wallet', provider_name: 'Other / Custom Wallet', custom_provider: 'Legacy wallet total',
            account_label: 'Legacy wallet total', opening: Number.parseFloat(summary.wallet_opening) || 0,
            closing: Number.parseFloat(summary.wallet_closing) || 0
        }));
    }
    return legacy;
};

const getDailyTotals = (dailyData = {}) => {
    const accounts = Array.isArray(dailyData.accounts) ? dailyData.accounts : [];
    const accountsOpening = accounts.reduce((sum, account) => sum + (Number.parseFloat(account.opening) || 0), 0);
    const accountsClosing = accounts.reduce((sum, account) => sum + (Number.parseFloat(account.closing) || 0), 0);
    const opening = (Number.parseFloat(dailyData.cash_opening) || 0) +
        (Number.parseFloat(dailyData.cheque_opening) || 0) + accountsOpening;
    const closing = (Number.parseFloat(dailyData.cash_closing) || 0) +
        (Number.parseFloat(dailyData.cheque_closing) || 0) + accountsClosing;
    return { opening, closing, net: closing - opening };
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric'
    });
};

const escapeReceiptHtml = (value) => String(value ?? '').replace(
    /[&<>"']/g,
    character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    }[character])
);

const normalizeReceiptLogo = (value) => {
    const logo = String(value || '').trim();
    if (!logo || /^(https?:|data:|blob:)/i.test(logo)) return logo;
    return `https://ourzestapp.online/oz_backend/${logo.replace(/^\/+/, '')}`;
};

const normalizeReceiptKey = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const extractTransactionInvoiceReference = (transaction = {}) => {
    const directReference = [
        transaction.invoice_number,
        transaction.bill_number,
        transaction.bill_no,
        transaction.receipt_number,
        transaction.return_number,
        transaction.reference_number,
        transaction.source_reference
    ].find(value => value !== undefined && value !== null && String(value).trim() !== '');
    if (directReference) return String(directReference).trim();

    const description = String(transaction.description || '');
    const match = description.match(
        /(?:bill|invoice|receipt|return|purchase|sale)\s*(?:no\.?|number)?\s*#?\s*([a-z0-9][a-z0-9\/-]*)/i
    );
    return match?.[1] || '';
};

const receiptMoney = (value) => {
    const amount = Number.parseFloat(value);
    return `Rs. ${(Number.isFinite(amount) ? amount : 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

const formatReceiptDateTime = (value) => {
    const date = value ? new Date(value) : new Date();
    const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
    return safeDate.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const getTransactionReceiptTitle = (type) => ({
    sale: 'Sales Receipt',
    purchase: 'Purchase Receipt',
    return: 'Return Receipt',
    purchase_return: 'Purchase Return Receipt',
    supplier_return: 'Purchase Return Receipt',
    due: 'Customer Due Receipt',
    customer_due: 'Customer Due Receipt',
    payment_received: 'Customer Payment Receipt',
    customer_payment: 'Customer Payment Receipt',
    payment_to_supplier: 'Supplier Payment Receipt',
    supplier_payment: 'Supplier Payment Receipt',
    supplier_due: 'Supplier Due Receipt',
    partner_investment: 'Partner Investment Receipt',
    partner_credit: 'Partner Investment Receipt',
    partner_payment: 'Partner Payment Receipt',
    partner_debit: 'Partner Payment Receipt',
    employee_salary: 'Employee Salary Receipt',
    employee_payment: 'Employee Payment Receipt',
    salary_advance: 'Salary Advance Receipt',
    salary_payment: 'Salary Payment Receipt',
    expense: 'Expense Receipt',
    shop_expense: 'Expense Receipt',
    advance_received: 'Advance Payment Receipt',
    advance_received_customer: 'Customer Advance Receipt',
    advance_paid_supplier: 'Supplier Advance Payment Receipt',
    advance_return_customer: 'Customer Advance Return Receipt',
    advance_return_supplier: 'Supplier Advance Returned Back Receipt'
}[String(type || '').toLowerCase()] || 'Transaction Receipt');

const buildThermalReceiptHtml = ({
    kind,
    record = null,
    transaction = {},
    paymentMethodDetails = {},
    paymentAccounts = [],
    businessInfo = {},
    user = {},
    currentShop = {}
}) => {
    const source = record || transaction;
    const transactionType = String(transaction.transaction_type || kind || '').toLowerCase();
    const isItemReceipt = ['sale', 'purchase', 'return', 'purchase_return'].includes(kind) &&
        Array.isArray(source?.items) && source.items.length > 0;

    const businessName = businessInfo.businessname || user.business_name || user.fullname ||
        currentShop.shop_name || 'OurZest Store';
    const businessLogo = normalizeReceiptLogo(
        businessInfo.bussinesslogo_url || businessInfo.bussinesslogo ||
        user.bussinesslogo_url || user.bussinesslogo || ''
    );
    const businessAddress = businessInfo.businessaddress || user.business_address || '';
    const businessPhone = businessInfo.phonenumber || user.phonenumber || '';
    const businessEmail = businessInfo.email || user.email || '';
    const businessGst = businessInfo.gst_number || user.gst_number || '';

    const title = isItemReceipt
        ? ({ sale: 'Sales Receipt', purchase: 'Purchase Receipt', return: 'Return Receipt', purchase_return: 'Purchase Return Receipt' }[kind])
        : getTransactionReceiptTitle(transactionType);
    const partyLabel = kind === 'purchase' || kind === 'purchase_return' || [
        'supplier_due', 'payment_to_supplier', 'supplier_payment',
        'advance_paid_supplier', 'advance_return_supplier'
    ].includes(transactionType)
        ? 'Supplier'
        : ['partner_investment', 'partner_payment', 'partner_credit', 'partner_debit'].includes(transactionType)
            ? 'Partner'
            : ['employee_salary', 'salary_payment', 'employee_payment', 'salary_advance'].includes(transactionType)
                ? 'Employee'
                : ['expense', 'shop_expense'].includes(transactionType)
                    ? 'Expense'
                    : 'Customer';
    const partyName = source?.customer_name || source?.supplier_name || source?.partner_name ||
        source?.employee_name || transaction.reference_name || transaction.expense_name ||
        (partyLabel === 'Customer' ? 'Walk-in Customer' : '-');
    const partyPhone = source?.customer_phone || source?.supplier_phone || source?.phone || '';

    const receiptNumber = isItemReceipt
        ? (['return', 'purchase_return'].includes(kind)
            ? (source.return_number || source.id || transaction.return_id)
            : (source.bill_number || source.bill_no || source.invoice_number || source.id))
        : (transaction.transaction_id || transaction.id || transaction.receipt_number || '-');
    const linkedInvoice = extractTransactionInvoiceReference(transaction) ||
        source?.bill_number || source?.bill_no || source?.invoice_number ||
        source?.return_number || source?.original_bill_number || '';
    const receiptDate = formatReceiptDateTime(isItemReceipt
        ? (source?.date || source?.transaction_date || source?.created_at)
        : (transaction.transaction_date || transaction.date || transaction.created_at)
    );

    const items = isItemReceipt ? source.items : [];
    const itemSubtotal = items.reduce((sum, item) => {
        const price = Number.parseFloat(item.price ?? item.unit_price ?? item.purchase_price) || 0;
        const quantity = Number.parseFloat(item.quantity ?? item.qty) || 0;
        const lineTotal = Number.parseFloat(item.total ?? item.line_total);
        return sum + (Number.isFinite(lineTotal) ? lineTotal : price * quantity);
    }, 0);
    const subtotal = source?.subtotal ?? itemSubtotal;
    const tax = Number.parseFloat(source?.tax) || 0;
    const discount = Number.parseFloat(source?.discount) || 0;
    const grandTotal = source?.grand_total ?? source?.total ??
        (Number(subtotal) + tax - discount);
    const paidAmount = source?.paid_amount ?? source?.paid ?? transaction._display_paid ??
        transaction.amount_paid ?? transaction.paid_amount ?? 0;
    const itemBalance = source?.balance ?? source?.amount_due ??
        (Number(grandTotal) - Number(paidAmount));

    const previousBalance = transaction._display_previous_balance ?? transaction._previous_balance ??
        transaction.previous_dues ?? transaction.previous_balance ?? 0;
    const transactionAmount = transaction._transaction_amount ?? transaction.amount ??
        transaction.transaction_amount ?? transaction._display_grand_total ?? transaction.grand_total ?? 0;
    const transactionPaid = transaction._display_paid ?? transaction.amount_paid ??
        transaction.paid_amount ?? transaction.paid ?? 0;
    const transactionPaidLabel = ({
        advance_received_customer: 'Received in Advance',
        advance_paid_supplier: 'Paid in Advance',
        advance_return_customer: 'Returned to Customer',
        advance_return_supplier: 'Returned Back by Supplier'
    }[transactionType] || 'Paid / Received');
    const transactionBalance = transaction._display_balance ?? transaction.amount_due ??
        transaction.balance ?? (Number(previousBalance) + Number(transactionAmount) - Number(transactionPaid));
    const numericPreviousBalance = Number.parseFloat(previousBalance) || 0;
    const hasPreviousDues = Math.abs(numericPreviousBalance) > 0.005;
    const totalPayableWithPrevious = numericPreviousBalance + (Number.parseFloat(grandTotal) || 0);
    const hasCascadedBalance = transaction._display_balance !== undefined ||
        transaction._previous_balance !== undefined;
    const receiptBalanceDue = hasCascadedBalance ? transactionBalance : itemBalance;
    const incomingTypes = new Set([
        'sale', 'sales', 'payment_received', 'customer_payment',
        'partner_investment', 'partner_credit', 'advance_received',
        'advance_received_customer', 'advance_return_supplier', 'purchase_return', 'supplier_return'
    ]);
    const direction = incomingTypes.has(transactionType) ? 'Incoming' : 'Outgoing';
    const paymentMethod = String(transaction.payment_method || source?.payment_method || 'cash')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, character => character.toUpperCase());
    const paymentFlowLabel = direction === 'Incoming' ? 'Money Received Into' : 'Money Paid From';
    const paymentFlowAmount = isItemReceipt ? paidAmount : transactionPaid;
    const parsePaymentLines = value => {
        if (Array.isArray(value)) return value;
        if (!value || typeof value !== 'string') return [];
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed)
                ? parsed
                : (Array.isArray(parsed?.payments) ? parsed.payments : []);
        } catch (error) {
            return [];
        }
    };
    const savedPaymentLines = parsePaymentLines(
        source?.payment_breakdown ?? transaction.payment_breakdown
    );
    const receiptPaymentLines = savedPaymentLines.length
        ? savedPaymentLines.map(line => ({
            details: resolveTransactionPaymentDetails({
                ...line,
                payment_account_id: line.payment_account_id ?? line.account_id,
                payment_method: line.payment_method ?? line.payment_type
            }, paymentAccounts),
            amount: Number.parseFloat(line.amount ?? line.paid_amount) || 0
        }))
        : [{ details: paymentMethodDetails, amount: Number.parseFloat(paymentFlowAmount) || 0 }];
    const paymentFlowMarkup = `
        <section class="payment-flow-block">
            <div class="payment-flow-title">Payment Flow</div>
            <div class="payment-flow-direction">${escapeReceiptHtml(paymentFlowLabel)}</div>
            ${receiptPaymentLines.map(({ details, amount }, index) => {
                const provider = String(details?.provider || paymentMethod || 'Cash').trim();
                const title = String(details?.accountTitle || '').trim();
                const number = String(details?.accountNumber || '').trim();
                const heading = [provider, title].filter(Boolean).join(' - ');
                return `<div class="payment-flow-line">
                    ${receiptPaymentLines.length > 1 ? `<div class="payment-flow-line-number">Payment ${index + 1}</div>` : ''}
                    <div class="payment-flow-account">
                        <span>Account / Method</span>
                        <strong>${escapeReceiptHtml(heading)}</strong>
                        ${number ? `<small>${escapeReceiptHtml(number)}</small>` : ''}
                    </div>
                    <div class="payment-flow-amount">
                        <span>Amount</span><strong>${receiptMoney(amount)}</strong>
                    </div>
                </div>`;
            }).join('')}
        </section>`;
    const description = transaction.description || source?.description || '-';
    const returnReason = source?.return_reason || transaction.return_reason || '';
    const originalInvoice = source?.original_bill_number || transaction.original_bill_number || '';

    const itemMarkup = items.map((item, index) => {
        const name = item.product_name || item.name || 'Product';
        const quantity = Number.parseFloat(item.quantity ?? item.qty) || 0;
        const price = Number.parseFloat(item.price ?? item.unit_price ?? item.purchase_price) || 0;
        const lineTotal = Number.parseFloat(item.total ?? item.line_total);
        const finalLineTotal = Number.isFinite(lineTotal) ? lineTotal : price * quantity;
        const itemMeta = [item.unit, item.warranty].filter(Boolean).join(' / ');
        return `<div class="item">
            <div class="item-heading"><span>${index + 1}. ${escapeReceiptHtml(name)}</span><strong>${receiptMoney(finalLineTotal)}</strong></div>
            <div class="item-detail">${escapeReceiptHtml(quantity)} x ${receiptMoney(price)}${itemMeta ? ` - ${escapeReceiptHtml(itemMeta)}` : ''}</div>
        </div>`;
    }).join('');

    const inventoryDetails = `
        <section class="party-block">
            <div><span>${partyLabel}</span><strong>${escapeReceiptHtml(partyName)}</strong></div>
            ${partyPhone ? `<div><span>Phone</span><strong>${escapeReceiptHtml(partyPhone)}</strong></div>` : ''}
        </section>
        ${(['return', 'purchase_return'].includes(kind) && (originalInvoice || returnReason)) ? `<section class="reference-block">
            ${originalInvoice ? `<div><span>Original Invoice</span><strong>${escapeReceiptHtml(originalInvoice)}</strong></div>` : ''}
            ${returnReason ? `<div><span>Return Reason</span><strong>${escapeReceiptHtml(returnReason)}</strong></div>` : ''}
        </section>` : ''}
        ${paymentFlowMarkup}
        <section class="items-block">
            <div class="section-title">Items</div>
            ${itemMarkup}
        </section>
        <section class="totals">
            <div><span>Subtotal</span><strong>${receiptMoney(subtotal)}</strong></div>
            ${tax > 0 ? `<div><span>Tax</span><strong>${receiptMoney(tax)}</strong></div>` : ''}
            ${discount > 0 ? `<div><span>Discount</span><strong>-${receiptMoney(discount)}</strong></div>` : ''}
            <div class="grand"><span>Grand Total</span><strong>${receiptMoney(grandTotal)}</strong></div>
            ${hasPreviousDues ? `
                <div><span>Previous Dues</span><strong>${receiptMoney(numericPreviousBalance)}</strong></div>
                ${!['return', 'purchase_return'].includes(kind) ? `<div><span>Total Payable</span><strong>${receiptMoney(totalPayableWithPrevious)}</strong></div>` : ''}
            ` : ''}
            ${['return', 'purchase_return'].includes(kind)
                ? `<div class="emphasis"><span>${kind === 'purchase_return' ? 'Supplier Refund Received' : 'Refund Amount'}</span><strong>${receiptMoney(source?.refund_amount ?? grandTotal)}</strong></div>`
                : `<div><span>Paid</span><strong>${receiptMoney(paidAmount)}</strong></div>
                   <div class="emphasis"><span>Balance (Dues)</span><strong>${receiptMoney(receiptBalanceDue)}</strong></div>`}
        </section>`;

    const transactionDetails = `
        <section class="party-block">
            <div><span>${partyLabel}</span><strong>${escapeReceiptHtml(partyName)}</strong></div>
            <div><span>Direction</span><strong>${direction}</strong></div>
        </section>
        ${paymentFlowMarkup}
        <section class="reference-block">
            <div><span>Invoice / Bill Reference</span><strong>${escapeReceiptHtml(linkedInvoice || 'Not linked')}</strong></div>
            <div><span>Description</span><strong>${escapeReceiptHtml(description)}</strong></div>
        </section>
        <section class="totals transaction-totals">
            <div><span>Previous Dues</span><strong>${receiptMoney(previousBalance)}</strong></div>
            <div class="grand"><span>Transaction Amount</span><strong>${receiptMoney(transactionAmount)}</strong></div>
            <div><span>${transactionPaidLabel}</span><strong>${receiptMoney(transactionPaid)}</strong></div>
            <div class="emphasis"><span>Balance (Dues)</span><strong>${receiptMoney(transactionBalance)}</strong></div>
        </section>`;

    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${escapeReceiptHtml(title)} ${escapeReceiptHtml(receiptNumber)}</title>
    <style>
        * { box-sizing: border-box; }
        html, body { margin: 0; min-height: 100%; }
        body { background: #e5e7eb; color: #111; font: 12px/1.45 Arial, Helvetica, sans-serif; }
        .preview-actions { position: sticky; top: 0; z-index: 5; display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 10px 16px; color: #fff; background: #111827; box-shadow: 0 2px 12px rgba(15, 23, 42, .25); }
        .page-size-switcher, .action-buttons { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; }
        .page-size-label { margin-right: 3px; color: #cbd5e1; font-size: 11px; font-weight: 800; letter-spacing: .45px; text-transform: uppercase; }
        .preview-actions button { padding: 8px 13px; color: #e2e8f0; background: #334155; border: 1px solid #475569; border-radius: 7px; font: 700 12px Arial; cursor: pointer; transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease; }
        .preview-actions button:hover { color: #fff; background: #475569; }
        .preview-actions .page-size-button.active { color: #fff; background: #2563eb; border-color: #60a5fa; }
        .preview-actions .print-button { color: #fff; background: #16a34a; border-color: #22c55e; }
        .preview-actions .print-button:hover { background: #15803d; }
        .preview-actions .close-button { color: #fff; background: #b91c1c; border-color: #ef4444; }
        .preview-actions .close-button:hover { background: #991b1b; }
        .page-stage { min-width: 100%; padding: 20px; overflow: auto; }
        .receipt { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 12mm 14mm; overflow: hidden; background: #fff; box-shadow: 0 8px 30px rgba(15, 23, 42, .2); transition: width 180ms ease, min-height 180ms ease, padding 180ms ease; }
        body[data-page-size="legal"] .receipt { width: 216mm; min-height: 356mm; }
        .brand { text-align: center; }
        .logo { display: block; max-width: 42mm; max-height: 25mm; margin: 0 auto 2.5mm; object-fit: contain; filter: grayscale(100%) contrast(115%); }
        .business-name { font-size: 25px; font-weight: 900; letter-spacing: .25px; overflow-wrap: anywhere; }
        .business-detail { margin-top: 3px; color: #333; font-size: 11px; overflow-wrap: anywhere; }
        .document-title { margin: 6mm 0 4mm; padding: 3mm 2mm; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; font-size: 18px; font-weight: 900; letter-spacing: 1.8px; text-align: center; text-transform: uppercase; }
        .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 5mm; padding-bottom: 4mm; border-bottom: 1px dashed #777; }
        .meta div:last-child { text-align: right; }
        .meta span, .party-block span, .reference-block span { display: block; color: #555; font-size: 9px; font-weight: 700; letter-spacing: .45px; text-transform: uppercase; }
        .meta strong, .party-block strong, .reference-block strong { display: block; margin-top: 2px; font-size: 12px; overflow-wrap: anywhere; }
        .party-block, .reference-block { margin-top: 4mm; padding: 3.5mm 4mm; border: 1px solid #aaa; border-radius: 3px; }
        .party-block > div, .reference-block > div { display: flex; align-items: baseline; justify-content: space-between; gap: 5mm; padding: 1.5mm 0; }
        .party-block > div + div, .reference-block > div + div { border-top: 1px dotted #bbb; }
        .party-block strong, .reference-block strong { max-width: 125mm; text-align: right; }
        .payment-flow-block { margin-top: 4mm; padding: 4mm; border: 1.5px solid #111; border-left-width: 4px; border-radius: 3px; page-break-inside: avoid; }
        .payment-flow-title { margin-bottom: 2.5mm; font-size: 10px; font-weight: 900; letter-spacing: .9px; text-transform: uppercase; }
        .payment-flow-direction { margin-bottom: 3mm; padding: 2mm 3mm; color: #fff; background: #111; font-size: 13px; font-weight: 900; text-align: center; text-transform: uppercase; }
        .payment-flow-line + .payment-flow-line { margin-top: 2.5mm; padding-top: 2.5mm; border-top: 1px dashed #777; }
        .payment-flow-line-number { margin-bottom: 1mm; font-size: 9px; font-weight: 900; letter-spacing: .45px; text-transform: uppercase; }
        .payment-flow-account, .payment-flow-amount { display: grid; grid-template-columns: 36mm minmax(0, 1fr); align-items: center; gap: 3mm; padding: 1.5mm 0; }
        .payment-flow-account > span, .payment-flow-amount > span { color: #555; font-size: 9px; font-weight: 800; letter-spacing: .4px; text-transform: uppercase; }
        .payment-flow-account strong, .payment-flow-amount strong { font-size: 13px; overflow-wrap: anywhere; text-align: right; }
        .payment-flow-account small { grid-column: 2; margin-top: -2mm; color: #444; font-size: 11px; font-weight: 700; letter-spacing: .25px; text-align: right; overflow-wrap: anywhere; }
        .payment-flow-amount { margin-top: 1mm; border-top: 1px dotted #999; }
        .section-title { padding: 5mm 0 2mm; font-size: 11px; font-weight: 900; letter-spacing: 1px; text-transform: uppercase; }
        .item { padding: 2.5mm 0; border-bottom: 1px dashed #aaa; page-break-inside: avoid; }
        .item-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 4mm; font-size: 12px; font-weight: 700; }
        .item-heading span { max-width: 130mm; overflow-wrap: anywhere; }
        .item-heading strong { white-space: nowrap; }
        .item-detail { margin-top: 1mm; color: #444; font-size: 10px; }
        .totals { width: 90mm; margin: 5mm 0 0 auto; padding-top: 2mm; border-top: 1.5px solid #000; page-break-inside: avoid; }
        .totals > div { display: flex; justify-content: space-between; gap: 5mm; padding: 1.2mm 0; }
        .totals .grand { margin: 1.5mm 0; padding: 2mm 0; border-top: 1px solid #000; border-bottom: 1px solid #000; font-size: 15px; font-weight: 900; }
        .totals .emphasis { font-size: 13px; font-weight: 900; }
        .transaction-totals { margin-top: 6mm; }
        .signature { display: flex; justify-content: space-between; gap: 18mm; margin-top: 18mm; color: #444; font-size: 9px; }
        .signature span { flex: 1; padding-top: 2mm; border-top: 1px solid #777; text-align: center; }
        .footer { margin-top: 10mm; padding-top: 3mm; border-top: 1px dashed #777; text-align: center; }
        .thanks { font-size: 13px; font-weight: 900; }
        .powered { margin-top: 1mm; color: #555; font-size: 9px; }

        body[data-page-size="thermal"] { font-size: 11px; }
        body[data-page-size="thermal"] .page-stage { padding: 14px 8px; }
        body[data-page-size="thermal"] .receipt { width: 80mm; min-height: 100mm; padding: 4mm 3.5mm; }
        body[data-page-size="thermal"] .logo { max-width: 28mm; max-height: 18mm; margin-bottom: 1.5mm; }
        body[data-page-size="thermal"] .business-name { font-size: 17px; }
        body[data-page-size="thermal"] .business-detail { margin-top: 2px; font-size: 9px; }
        body[data-page-size="thermal"] .document-title { margin: 3mm 0 2mm; padding: 2mm 1mm; font-size: 14px; letter-spacing: 1.2px; }
        body[data-page-size="thermal"] .meta { gap: 1.5mm; padding-bottom: 2mm; }
        body[data-page-size="thermal"] .meta span,
        body[data-page-size="thermal"] .party-block span,
        body[data-page-size="thermal"] .reference-block span { font-size: 8px; letter-spacing: .35px; }
        body[data-page-size="thermal"] .meta strong,
        body[data-page-size="thermal"] .party-block strong,
        body[data-page-size="thermal"] .reference-block strong { margin-top: 1px; font-size: 10px; }
        body[data-page-size="thermal"] .party-block,
        body[data-page-size="thermal"] .reference-block { margin-top: 2mm; padding: 2mm; border-radius: 2px; }
        body[data-page-size="thermal"] .party-block > div,
        body[data-page-size="thermal"] .reference-block > div { gap: 3mm; padding: 1mm 0; }
        body[data-page-size="thermal"] .party-block strong,
        body[data-page-size="thermal"] .reference-block strong { max-width: 45mm; }
        body[data-page-size="thermal"] .payment-flow-block { margin-top: 2mm; padding: 2mm; border-left-width: 3px; }
        body[data-page-size="thermal"] .payment-flow-title { margin-bottom: 1.5mm; font-size: 8px; }
        body[data-page-size="thermal"] .payment-flow-direction { margin-bottom: 1.5mm; padding: 1.5mm 1mm; font-size: 10px; }
        body[data-page-size="thermal"] .payment-flow-line + .payment-flow-line { margin-top: 1.5mm; padding-top: 1.5mm; }
        body[data-page-size="thermal"] .payment-flow-line-number { font-size: 7.5px; }
        body[data-page-size="thermal"] .payment-flow-account,
        body[data-page-size="thermal"] .payment-flow-amount { grid-template-columns: 22mm minmax(0, 1fr); gap: 2mm; padding: 1mm 0; }
        body[data-page-size="thermal"] .payment-flow-account > span,
        body[data-page-size="thermal"] .payment-flow-amount > span { font-size: 7.5px; }
        body[data-page-size="thermal"] .payment-flow-account strong,
        body[data-page-size="thermal"] .payment-flow-amount strong { font-size: 10px; }
        body[data-page-size="thermal"] .payment-flow-account small { grid-column: 2; margin-top: -1mm; font-size: 9px; }
        body[data-page-size="thermal"] .section-title { padding: 2.5mm 0 1mm; font-size: 9px; letter-spacing: .8px; }
        body[data-page-size="thermal"] .item { padding: 1.5mm 0; }
        body[data-page-size="thermal"] .item-heading { gap: 2mm; font-size: 10px; }
        body[data-page-size="thermal"] .item-heading span { max-width: 45mm; }
        body[data-page-size="thermal"] .item-detail { margin-top: .7mm; font-size: 9px; }
        body[data-page-size="thermal"] .totals { width: 100%; margin-top: 2.5mm; padding-top: 1.5mm; }
        body[data-page-size="thermal"] .totals > div { gap: 3mm; padding: .9mm 0; }
        body[data-page-size="thermal"] .totals .grand { margin: 1mm 0; padding: 1.6mm 0; font-size: 13px; }
        body[data-page-size="thermal"] .totals .emphasis { font-size: 12px; }
        body[data-page-size="thermal"] .transaction-totals { margin-top: 3mm; }
        body[data-page-size="thermal"] .signature { gap: 6mm; margin-top: 8mm; font-size: 8px; }
        body[data-page-size="thermal"] .signature span { padding-top: 1.5mm; }
        body[data-page-size="thermal"] .footer { margin-top: 4mm; padding-top: 2mm; }
        body[data-page-size="thermal"] .thanks { font-size: 11px; }
        body[data-page-size="thermal"] .powered { font-size: 8px; }

        @media screen and (max-width: 900px) {
            .preview-actions { align-items: stretch; flex-direction: column; }
            .page-size-switcher, .action-buttons { justify-content: center; }
            body:not([data-page-size="thermal"]) .receipt { max-width: calc(100vw - 24px); padding: 7mm; }
            .page-stage { padding: 12px; }
        }

        @media print {
            html, body { width: auto; min-height: 0; background: #fff; }
            .preview-actions { display: none !important; }
            .page-stage { padding: 0; overflow: visible; }
            .receipt { width: 100% !important; min-height: 0 !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important; transition: none; }
            body[data-page-size="thermal"] .receipt { width: 74mm !important; }
        }
    </style>
    <style id="receipt-page-rule">@page { size: A4 portrait; margin: 10mm; }</style>
</head>
<body data-page-size="a4">
    <div class="preview-actions">
        <div class="page-size-switcher" role="group" aria-label="Receipt page size">
            <span class="page-size-label">Page size</span>
            <button type="button" class="page-size-button active" data-size="a4" aria-pressed="true" onclick="setReceiptPageSize('a4')">A4</button>
            <button type="button" class="page-size-button" data-size="legal" aria-pressed="false" onclick="setReceiptPageSize('legal')">Legal</button>
            <button type="button" class="page-size-button" data-size="thermal" aria-pressed="false" onclick="setReceiptPageSize('thermal')">3-inch Thermal</button>
        </div>
        <div class="action-buttons">
            <button type="button" class="print-button" onclick="window.print()">Print</button>
            <button type="button" class="close-button" onclick="window.close()">Close</button>
        </div>
    </div>
    <div class="page-stage"><main class="receipt">
        <header class="brand">
            ${businessLogo ? `<img class="logo" src="${escapeReceiptHtml(businessLogo)}" alt="${escapeReceiptHtml(businessName)}" onerror="this.style.display='none'">` : ''}
            <div class="business-name">${escapeReceiptHtml(businessName)}</div>
            ${businessAddress ? `<div class="business-detail">${escapeReceiptHtml(businessAddress)}</div>` : ''}
            ${(businessPhone || businessEmail) ? `<div class="business-detail">${[businessPhone, businessEmail].filter(Boolean).map(escapeReceiptHtml).join(' | ')}</div>` : ''}
            ${businessGst ? `<div class="business-detail">GST: ${escapeReceiptHtml(businessGst)}</div>` : ''}
        </header>
        <div class="document-title">${escapeReceiptHtml(title)}</div>
        <section class="meta">
            <div><span>Receipt No.</span><strong>#${escapeReceiptHtml(receiptNumber)}</strong></div>
            <div><span>Date &amp; Time</span><strong>${escapeReceiptHtml(receiptDate)}</strong></div>
        </section>
        ${isItemReceipt ? inventoryDetails : transactionDetails}
        <div class="signature"><span>Customer / Receiver</span><span>Authorized Signature</span></div>
        <footer class="footer">
            <div class="thanks">Thank you for your business!</div>
            <div class="powered">Powered by OurZest PVT LTD.</div>
        </footer>
    </main></div>
    <script>
        window.setReceiptPageSize = function (size) {
            var pageSizes = {
                a4: '@page { size: A4 portrait; margin: 10mm; }',
                legal: '@page { size: legal portrait; margin: 10mm; }',
                thermal: '@page { size: 80mm auto; margin: 3mm; }'
            };
            var selectedSize = pageSizes[size] ? size : 'a4';
            document.body.setAttribute('data-page-size', selectedSize);
            document.getElementById('receipt-page-rule').textContent = pageSizes[selectedSize];
            Array.from(document.querySelectorAll('.page-size-button')).forEach(function (button) {
                var isActive = button.getAttribute('data-size') === selectedSize;
                button.classList.toggle('active', isActive);
                button.setAttribute('aria-pressed', String(isActive));
            });
        };

        window.onload = async function () {
            await Promise.all(Array.from(document.images).map(function (image) {
                return image.complete ? Promise.resolve() : new Promise(function (resolve) {
                    image.onload = resolve;
                    image.onerror = resolve;
                });
            }));
            if (document.fonts && document.fonts.ready) await document.fonts.ready;
            window.setReceiptPageSize('a4');
        };
    </script>
</body>
</html>`;
};

const parseTransactionPaymentLines = (transaction = {}) => {
    const directLines = transaction.payment_lines || transaction.payments;
    if (Array.isArray(directLines)) return directLines;
    const breakdown = transaction.payment_breakdown;
    if (Array.isArray(breakdown)) return breakdown;
    if (breakdown && typeof breakdown === 'object') {
        if (Array.isArray(breakdown.payments)) return breakdown.payments;
        if (Array.isArray(breakdown.lines)) return breakdown.lines;
    }
    if (typeof breakdown === 'string' && breakdown.trim()) {
        try {
            const parsed = JSON.parse(breakdown);
            if (Array.isArray(parsed)) return parsed;
            if (Array.isArray(parsed?.payments)) return parsed.payments;
            if (Array.isArray(parsed?.lines)) return parsed.lines;
        } catch (error) {
            // Legacy free-text breakdowns use the single-method fallback.
        }
    }
    return [];
};

const resolveTransactionPaymentPresentation = (transaction = {}, accounts = []) => {
    const savedLines = parseTransactionPaymentLines(transaction)
        .filter(line => (Number.parseFloat(line?.amount ?? line?.paid_amount) || 0) > 0.005)
        .map(line => resolveTransactionPaymentDetails({
            ...line,
            payment_account_id: line.payment_account_id ?? line.account_id,
            payment_method: line.payment_method ?? line.payment_type
        }, accounts));
    const lines = savedLines.length
        ? savedLines
        : [resolveTransactionPaymentDetails(transaction, accounts)];
    const methodNames = [...new Set(lines.map(line => line.provider).filter(Boolean))];
    const isSplit = lines.length > 1;
    const label = isSplit
        ? `Split (${methodNames.join(' + ')})`
        : (methodNames[0] || 'Cash');
    const details = lines.map(line => {
        const heading = [line.provider, line.accountTitle].filter(Boolean).join(' - ');
        return [heading, line.accountNumber].filter(Boolean).join('\n');
    }).filter(Boolean);
    return {
        isSplit,
        label,
        lines,
        pdfText: isSplit
            ? [label, ...details].filter(Boolean).join('\n')
            : (details[0] || label)
    };
};

const normalizeEntityType = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized.includes('customer') || normalized.includes('client')) return 'customer';
    if (normalized.includes('supplier') || normalized.includes('vendor')) return 'supplier';
    if (normalized.includes('partner') || normalized.includes('investor')) return 'partner';
    if (normalized.includes('employee') || normalized.includes('staff') || normalized.includes('worker')) return 'employee';
    return '';
};

const normalizeEntityId = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const normalized = String(value).trim();
    return /^\d+$/.test(normalized) ? String(Number(normalized)) : normalized.toLowerCase();
};

const normalizeEntityName = (value) => String(value || '').trim().toLowerCase();

const ENTITY_TRANSACTION_TYPES = {
    customer: new Set([
        'sale', 'sales', 'return', 'sale_return', 'sales_return', 'customer_return',
        'due', 'customer_due', 'payment_received', 'customer_payment', 'customer_refund',
        'advance_received_customer', 'advance_return_customer'
    ]),
    supplier: new Set([
        'purchase', 'supplier_purchase', 'purchase_return', 'supplier_return',
        'supplier_due', 'payment_to_supplier', 'supplier_payment',
        'advance_paid_supplier', 'advance_return_supplier'
    ]),
    partner: new Set([
        'partner_investment', 'partner_payment', 'partner_credit', 'partner_debit',
        'partner_profit_share', 'partner_loss_share'
    ]),
    employee: new Set([
        'employee_salary', 'salary_payment', 'employee_payment', 'salary_advance', 'payroll'
    ])
};

const transactionMatchesEntity = (transaction, entityType, entityId, entityName = '') => {
    const selectedType = normalizeEntityType(entityType);
    const selectedId = normalizeEntityId(entityId);
    if (!selectedType) return true;

    const transactionType = String(transaction.transaction_type || '').trim().toLowerCase();
    const referenceType = normalizeEntityType(transaction.reference_type);
    const inferredType = Object.keys(ENTITY_TRANSACTION_TYPES).find(type =>
        ENTITY_TRANSACTION_TYPES[type].has(transactionType)
    ) || '';
    const resolvedType = referenceType || inferredType;

    if (resolvedType !== selectedType) return false;
    if (!selectedId) return true;

    const idFields = {
        customer: ['customer_id', 'customerid', 'customerId', 'client_id', 'party_id', 'entity_id', 'account_id', 'reference_id', 'ref_id'],
        supplier: ['supplier_id', 'supplierid', 'supplierId', 'vendor_id', 'party_id', 'entity_id', 'account_id', 'reference_id', 'ref_id'],
        partner: ['partner_id', 'partnerid', 'partnerId', 'party_id', 'entity_id', 'account_id', 'reference_id', 'ref_id'],
        employee: ['employee_id', 'employeeid', 'employeeId', 'staff_id', 'entity_id', 'account_id', 'reference_id', 'ref_id']
    };
    const fieldsToCheck = transaction._reference_was_corrected
        ? ['_resolved_reference_id']
        : idFields[selectedType];
    const idMatches = fieldsToCheck.some(field =>
        normalizeEntityId(transaction[field]) === selectedId
    );
    if (idMatches) return true;

    const selectedName = normalizeEntityName(entityName);
    if (!selectedName) return false;
    const nameFields = {
        customer: ['customer_name', 'customerName', 'customername', 'client_name', 'party_name', 'entity_name', 'account_name', 'reference_name', 'name'],
        supplier: ['supplier_name', 'supplierName', 'suppliername', 'vendor_name', 'party_name', 'entity_name', 'account_name', 'reference_name', 'name'],
        partner: ['partner_name', 'partnerName', 'partnername', 'party_name', 'entity_name', 'account_name', 'reference_name', 'name'],
        employee: ['employee_name', 'employeeName', 'employeename', 'staff_name', 'entity_name', 'account_name', 'reference_name', 'name']
    };
    const namesToCheck = transaction._reference_was_corrected
        ? ['_resolved_reference_name']
        : nameFields[selectedType];
    return namesToCheck.some(field =>
        normalizeEntityName(transaction[field]) === selectedName
    );
};

const buildEntityOptions = (records, entityType, icon, allLabel) => {
    const fields = {
        customer: {
            ids: ['customer_id', 'customerid', 'customerId', 'id', 'value'],
            names: ['customer_name', 'customerName', 'customername', 'full_name', 'display_name', 'name', 'label']
        },
        supplier: {
            ids: ['supplier_id', 'supplierid', 'supplierId', 'id', 'value'],
            names: ['supplier_name', 'supplierName', 'suppliername', 'company_name', 'display_name', 'name', 'label']
        },
        partner: {
            ids: ['partner_id', 'partnerid', 'partnerId', 'id', 'value'],
            names: ['partner_name', 'partnerName', 'partnername', 'full_name', 'display_name', 'name', 'label']
        },
        employee: {
            ids: ['employee_id', 'employeeid', 'employeeId', 'id', 'value'],
            names: ['employee_name', 'employeeName', 'employeename', 'full_name', 'display_name', 'name', 'label']
        }
    }[entityType];
    const seen = new Set();
    const options = (records || []).reduce((result, record) => {
        const rawId = fields.ids.map(field => record[field]).find(value =>
            value !== undefined && value !== null && value !== ''
        );
        const value = normalizeEntityId(rawId);
        if (!value || seen.has(value)) return result;

        const rawName = fields.names.map(field => record[field]).find(value =>
            value !== undefined && value !== null && String(value).trim() !== ''
        );
        seen.add(value);
        result.push({
            value,
            label: String(rawName || `${entityType} #${rawId}`),
            icon
        });
        return result;
    }, []);

    options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return [{ value: '', label: allLabel, icon }, ...options];
};

const extractApiRecords = (payload, preferredKeys = []) => {
    const candidates = [
        payload,
        payload?.data,
        payload?.records,
        payload?.result,
        payload?.data?.records,
        payload?.data?.result,
        payload?.data?.data
    ];
    preferredKeys.forEach(key => {
        candidates.push(payload?.[key], payload?.data?.[key]);
    });
    return candidates.find(candidate => Array.isArray(candidate)) || [];
};

const getEntityRecordKey = (record) => {
    const idFields = [
        'customer_id', 'customerid', 'customerId',
        'supplier_id', 'supplierid', 'supplierId',
        'partner_id', 'partnerid', 'partnerId',
        'employee_id', 'employeeid', 'employeeId',
        'id', 'value'
    ];
    const id = idFields.map(field => record?.[field]).find(value =>
        value !== undefined && value !== null && value !== ''
    );
    if (id !== undefined) return `id:${normalizeEntityId(id)}`;

    const name = record?.customer_name || record?.supplier_name ||
        record?.partner_name || record?.employee_name || record?.name || '';
    const shopId = record?.shop_id ?? record?.shopid ?? record?.shopId ?? '';
    return `fallback:${normalizeEntityName(name)}:${normalizeEntityId(shopId)}:${JSON.stringify(record)}`;
};

const fetchAllApiRecords = async ({ api, action, shopId, username, keys }) => {
    const pageSize = 100;
    const maxPages = 500;
    const allRecords = [];
    const seen = new Set();

    for (let page = 1; page <= maxPages; page += 1) {
        const response = await fetch(api, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action,
                shop_id: shopId,
                username: username || '',
                page,
                limit: pageSize,
                per_page: pageSize,
                offset: (page - 1) * pageSize
            })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const payload = await response.json();
        const pageRecords = extractApiRecords(payload, keys);
        let addedThisPage = 0;

        pageRecords.forEach(record => {
            const key = getEntityRecordKey(record);
            if (seen.has(key)) return;
            seen.add(key);
            allRecords.push(record);
            addedThisPage += 1;
        });

        const pagination = payload?.pagination || payload?.data?.pagination || {};
        const totalPages = Number(
            pagination.total_pages ?? pagination.last_page ?? pagination.page_count ?? 0
        );
        const totalRecords = Number(
            pagination.total ?? pagination.total_records ?? pagination.total_count ?? 0
        );

        if (totalPages > 0 && page >= totalPages) break;
        if (totalRecords > 0 && allRecords.length >= totalRecords) break;
        if (pageRecords.length === 0 || addedThisPage === 0) break;
    }

    return allRecords;
};

// ==============================================
// TRANSLATIONS
// ==============================================
const translations = {
    'en': {
        transactions: 'Transactions',
        addTransaction: 'Add Transaction',
        allTransactions: 'All Transactions',
        entity: 'Entity',
        allEntities: 'All Entities',
        customers: 'Customers',
        suppliers: 'Suppliers',
        employees: 'Employees',
        allCustomers: 'All Customers',
        allSuppliers: 'All Suppliers',
        allEmployees: 'All Employees',
        sales: 'Sales',
        purchases: 'Purchases',
        returns: 'Returns',
        salaries: 'Salaries',
        expenses: 'Expenses',
        partners: 'Partners',
        profitLoss: 'Profit / Loss',
        customerDue: 'Customer Due',
        customerPayment: 'Customer Payment',
        supplierDue: 'Supplier Due',
        supplierPayment: 'Supplier Payment',
        partnerInvestment: 'Partner Investment',
        partnerPayment: 'Partner Payment',
        employeeSalary: 'Employee Salary',
        shopExpense: 'Shop Expense',
        amount: 'Amount',
        date: 'Date',
        description: 'Description',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        cheque: 'Cheque',
        creditCard: 'Credit Card',
        mobilePayment: 'Mobile Payment',
        invoiceNumber: 'Invoice Number',
        save: 'Save',
        cancel: 'Cancel',
        close: 'Close',
        loading: 'Loading...',
        noTransactions: 'No transactions found',
        refresh: 'Refresh',
        search: 'Search',
        totalSales: 'Total Sales',
        totalExpenses: 'Total Expenses',
        netBalance: 'Net Balance',
        selectType: 'Select Type',
        selectCustomer: 'Select Customer',
        selectSupplier: 'Select Supplier',
        selectEmployee: 'Select Employee',
        selectPartner: 'Select Partner',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        cashReceived: 'Cash Received',
        balanceDue: 'Balance Due',
        status: 'Status',
        actions: 'Actions',
        edit: 'Edit',
        delete: 'Delete',
        confirmDelete: 'Confirm Delete',
        thisActionCannotBeUndone: 'This action cannot be undone',
        fullPaid: 'Full Paid',
        partialPaid: 'Partial Paid',
        unpaid: 'Unpaid',
        incoming: 'Incoming',
        outgoing: 'Outgoing',
        reference: 'Reference',
        type: 'Type',
        allTypes: 'All Types',
        startDate: 'Start Date',
        endDate: 'End Date',
        filter: 'Filter',
        resetFilters: 'Reset Filters',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        page: 'Page',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        deleteSuccess: 'Deleted successfully',
        updateSuccess: 'Updated successfully',
        profitLossStatement: 'Profit & Loss Statement',
        stockValue: 'Stock Value',
        stockCost: 'Stock Cost',
        estimatedProfit: 'Estimated Profit',
        actualProfit: 'Actual Profit',
        totalProfit: 'Total Profit',
        totalLoss: 'Total Loss',
        stockItems: 'Stock Items',
        profitAnalysis: 'Profit Analysis',
        stockProfitAnalysis: 'Stock Profit Analysis',
        salesProfitAnalysis: 'Sales Profit Analysis',
        profitFlowChart: 'Profit Flow Chart',
        netProfitLoss: 'Net Profit/Loss',
        salesRevenue: 'Sales Revenue',
        netSalesRevenue: 'Net Sales Revenue',
        costOfGoodsSold: 'Cost of Goods Sold',
        netCostOfGoodsSold: 'Net Cost of Goods Sold',
        totalReturns: 'Total Returns',
        returnedCost: 'Returned Cost',
        refundPaid: 'Refund Paid',
        salaryPayments: 'Salary Payments',
        viewLedger: 'View Ledger',
        searchNameSupplier: 'Search Name / Supplier',
        summary: 'Summary',
        detailed: 'Detailed',
        revenue: 'Revenue',
        deductions: 'Deductions',
        profitFlow: 'Profit Flow',
        partnerProfitLoss: 'Partner Profit / Loss',
        totalPartners: 'Total Partners',
        totalInvestment: 'Total Investment',
        totalProfitShare: 'Total Profit Share',
        totalLossShare: 'Total Loss Share',
        netPartnerProfit: 'Net Partner Profit',
        partnerType: 'Partner Type',
        overallPartners: 'Overall Partners',
        productBased: 'Product Based',
        allPartners: 'All Partners',
        selectedProducts: 'Selected Products',
        share: 'Share',
        stockProfit: 'Stock Profit',
        partnerShare: 'Partner Share',
        payable: 'Payable',
        balance: 'Balance',
        saleBased: 'Sale Based',
        overall: 'Overall',
        salesCount: 'Sales Count',
        partnerSaleBased: 'Partner Sale Based',
        totalSalesProfit: 'Total Sales Profit',
        salesProfit: 'Sales Profit',
        dailySummary: 'Daily Summary',
        dailyOC: 'Daily O/C',
        dailyOCHistory: 'Daily Opening / Closing History',
        costsIncluded: 'Costs Included',
        costsIncludedYes: 'Yes',
        costsIncludedNo: 'No',
        expensesIncluded: 'Expenses Included',
        salariesIncluded: 'Salaries Included',
        notIncluded: 'Not included',
        expenseShare: 'Expense Share',
        salaryShare: 'Salary Share',
        totalCostShare: 'Total Cost Share',
        netPayableAfterCosts: 'Net Payable After Costs',
        partnerSalesByProduct: 'Partner Sales by Product',
        productName: 'Product Name',
        saleRevenue: 'Sale Revenue',
        saleProfit: 'Sale Profit',
        productBasedPartners: 'Product Based Partners',
        overallPartners: 'Overall Partners',
        selectDateRange: 'Select Date Range',
        totalRevenue: 'Total Revenue',
        totalProfit: 'Total Profit',
        productSales: 'Product Sales',
        partnerSales: 'Partner Sales',
        profitAnalysisCharts: 'Profit Analysis Charts',
        monthlyRevenueProfitExpenses: 'Monthly Revenue, Profit & Expenses',
        monthlyBreakdown: 'Monthly Breakdown',
        expensesTrend: 'Expenses Trend',
        partnerRevenueDistribution: 'Partner Revenue Distribution',
        partnerDistribution: 'Partner Distribution',
        expenseBreakdown: 'Expense Breakdown',
        salaryBreakdown: 'Salary Breakdown',
        expensesSalariesTrend: 'Expenses & Salaries Trend',
        productRevenueDistribution: 'Product Revenue Distribution',
        productSalesShare: 'Product Sales Share',
        productPerformance: 'Product Performance',
        netProfit: 'Net Profit',
        charts: 'Charts',
        noChartData: 'No data available for charts',
        noPartnerData: 'No partner data available',
        noExpenseData: 'No expense data available',
        noProductData: 'No product data available',
        noSalesData: 'No sales data available',
        noProducts: 'No products available',
        profitMargin: 'Profit Margin',
        totalItems: 'Total Items',
        products: 'Products',
        allProducts: 'All Products',
        category: 'Category',
        company: 'Company',
        selectCategory: 'Select Category',
        selectCompany: 'Select Company',
        all: 'All',
        totalStockCost: 'Total Stock Cost',
        totalStockValue: 'Total Stock Value',
        estimatedProfit: 'Estimated Profit',
        categoryBreakdown: 'Category Breakdown',
        companyBreakdown: 'Company Breakdown',
        cogs: 'COGS',
        customerBreakdown: 'Customer Breakdown',
        transactions: 'Transactions',
        monthlyBreakdown: 'Monthly Breakdown',
        contractDays: 'Contract days',
        stockProfitShare: 'Stock Profit Share',
        invBalance: 'Inv. Balance',
        netPayableAfterCosts: 'Net Payable',
        active: 'Active',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        clear: 'Clear',
        selected: 'selected',
        noProducts: 'No products found',
        searchProducts: 'Search products...'
    }
};

// ==============================================
// TRANSLATOR HOOK
// ==============================================
const useTranslator = () => {
    const [language, setLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [dict, setDict] = useState(() => translations[language] || translations['en']);

    useEffect(() => {
        const handleChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            setLanguage(lang);
            setDict(translations[lang] || translations['en']);
        };
        window.addEventListener('storage', handleChange);
        window.addEventListener('languageChanged', handleChange);
        window.addEventListener('languageUpdated', handleChange);

        const interval = setInterval(() => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== language) {
                setLanguage(lang);
                setDict(translations[lang] || translations['en']);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleChange);
            window.removeEventListener('languageChanged', handleChange);
            window.removeEventListener('languageUpdated', handleChange);
            clearInterval(interval);
        };
    }, [language]);

    const t = useCallback((key) => dict[key] || key, [dict]);
    const isRTL = language === 'ur' || language === 'ar';

    return { t, language, isRTL };
};

// ==============================================
// STAT CARD COMPONENT
// ==============================================
const StatCard = ({ icon, title, value, subtitle, trend, trendLabel, color = 'primary' }) => {
    const isPositive = trend >= 0;
    return (
        <div className={`oztransui-stat-card oztransui-stat-color-${color}`}>
            <div className="oztransui-stat-card-icon">
                <FontAwesomeIcon icon={icon} />
            </div>
            <div className="oztransui-stat-card-content">
                <span className="oztransui-stat-card-title">{title}</span>
                <span className="oztransui-stat-card-value">{value}</span>
                {subtitle && <span className="oztransui-stat-card-subtitle">{subtitle}</span>}
                {trend !== undefined && (
                    <div className={`oztransui-stat-card-trend ${isPositive ? 'positive' : 'negative'}`}>
                        <FontAwesomeIcon icon={isPositive ? faArrowTrendUp : faArrowTrendDown} />
                        <span>{Math.abs(trend)}% {trendLabel || (isPositive ? 'Growth' : 'Decline')}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==============================================
// TOAST NOTIFICATION
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };
    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };
    if (!toast.show) return null;
    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon"><FontAwesomeIcon icon={getIconForType(toast.type)} /></div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};
// ==============================================
// CUSTOM DROPDOWN
// ==============================================
const FilterDropdown = ({ label, icon, value, onChange, options, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);
    const menuRef = useRef(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || label;

    const filteredOptions = searchTerm.trim() === ''
        ? options
        : options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (opt.subLabel && opt.subLabel.toLowerCase().includes(searchTerm.toLowerCase()))
        );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm('');
        setIsOpen(false);
        setHighlightedIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
            }
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => prev < filteredOptions.length - 1 ? prev + 1 : prev);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.oztransui-filter-option');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    return (
        <div className="oztransui-filter-dropdown" ref={wrapperRef}>
            <div
                className={`oztransui-filter-trigger ${isOpen ? 'oztransui-open' : ''} ${disabled ? 'oztransui-disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                tabIndex={disabled ? -1 : 0}
                role="combobox"
                aria-expanded={isOpen}
            >
                {icon && <FontAwesomeIcon icon={icon} className="oztransui-filter-icon" />}
                <span className="oztransui-filter-label">{displayValue}</span>
                <FontAwesomeIcon icon={faChevronDown} className={`oztransui-filter-arrow ${isOpen ? 'oztransui-rotate' : ''}`} />
            </div>
            {isOpen && (
                <div className="oztransui-filter-menu" ref={menuRef}>
                    <div className="oztransui-filter-search">
                        <FontAwesomeIcon icon={faSearch} />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                    {filteredOptions.length === 0 ? (
                        <div className="oztransui-filter-empty">No options found</div>
                    ) : (
                        filteredOptions.map((opt, index) => (
                            <div
                                key={opt.value}
                                className={`oztransui-filter-option ${value === opt.value ? 'oztransui-selected' : ''} ${highlightedIndex === index ? 'oztransui-highlighted' : ''}`}
                                onClick={() => handleSelect(opt)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                <div className="oztransui-filter-option-left">
                                    {opt.icon && <FontAwesomeIcon icon={opt.icon} className="oztransui-option-icon" />}
                                    <span className="oztransui-option-label">{opt.label}</span>
                                </div>
                                {opt.subLabel && (
                                    <span className="oztransui-option-sublabel">{opt.subLabel}</span>
                                )}
                                {opt.arrow && (
                                    <FontAwesomeIcon
                                        icon={opt.arrow}
                                        className={`oztransui-option-arrow ${opt.arrowType === 'incoming' ? 'oztransui-incoming' : 'oztransui-outgoing'}`}
                                    />
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// ==============================================
// DAILY SUMMARY COMPONENT
// ==============================================
const DailySummary = ({ currentShop, t, formatCurrency, formatDate, showToast, user, showHistory = false }) => {
    const emptySummary = () => ({ cash_opening: 0, cash_closing: 0, cheque_opening: 0, cheque_closing: 0, accounts: [] });
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [data, setData] = useState(emptySummary);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const fetchHistory = useCallback(async () => {
        if (!currentShop || !showHistory) return;
        setHistoryLoading(true);
        try {
            const res = await fetch(TRANSACTIONS_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'get_daily_summary_history', shop_id: currentShop.shop_id, limit: 100 }) });
            const result = await res.json();
            if (result.status === 'success') setHistory(Array.isArray(result.data) ? result.data : (result.data?.summaries || []));
            else showToast('error', result.message || 'Failed to load Daily O/C history');
        } catch (err) {
            showToast('error', 'Failed to load Daily O/C history');
        } finally {
            setHistoryLoading(false);
        }
    }, [currentShop, showHistory, showToast]);

    const fetchSummary = useCallback(async () => {
        if (!currentShop) return;
        setLoading(true);
        try {
            const res = await fetch(TRANSACTIONS_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'get_daily_summary', shop_id: currentShop.shop_id, summary_date: selectedDate }) });
            const result = await res.json();
            if (result.status === 'success' && result.data?.summary_id) {
                setData({
                    cash_opening: Number.parseFloat(result.data.cash_opening) || 0,
                    cash_closing: Number.parseFloat(result.data.cash_closing) || 0,
                    cheque_opening: Number.parseFloat(result.data.cheque_opening) || 0,
                    cheque_closing: Number.parseFloat(result.data.cheque_closing) || 0,
                    accounts: normalizeDailyAccounts(result.data.accounts, result.data)
                });
                setEditingId(result.data.summary_id);
            } else {
                setData(emptySummary());
                setEditingId(null);
            }
        } catch (err) {
            showToast('error', 'Failed to load the selected Daily O/C');
        } finally {
            setLoading(false);
        }
    }, [currentShop, selectedDate, showToast]);

    const addAccount = useCallback(type => setData(prev => ({ ...prev, accounts: [...(prev.accounts || []), createDailyAccount(type)] })), []);
    const updateAccount = useCallback((id, field, value) => setData(prev => ({ ...prev, accounts: (prev.accounts || []).map(account => account.id === id ? { ...account, [field]: value } : account) })), []);
    const removeAccount = useCallback(id => setData(prev => ({ ...prev, accounts: (prev.accounts || []).filter(account => account.id !== id) })), []);

    const handleSave = useCallback(async () => {
        if (!currentShop) return;
        const invalid = (data.accounts || []).find(account => !String(account.provider_name || '').trim() || (/^Other \/ Custom/.test(account.provider_name) && !String(account.custom_provider || '').trim()));
        if (invalid) {
            showToast('error', `Please select or enter the ${invalid.type === 'wallet' ? 'wallet' : 'bank'} name`);
            return;
        }
        const accounts = (data.accounts || []).map(account => ({
            id: account.id, type: account.type,
            provider_name: /^Other \/ Custom/.test(account.provider_name) ? String(account.custom_provider || '').trim() : String(account.provider_name || '').trim(),
            account_label: String(account.account_label || '').trim(), account_number: String(account.account_number || '').trim(),
            opening: Number.parseFloat(account.opening) || 0, closing: Number.parseFloat(account.closing) || 0
        }));
        const banks = accounts.filter(account => account.type === 'bank');
        const wallets = accounts.filter(account => account.type === 'wallet');
        setSaving(true);
        try {
            const res = await fetch(TRANSACTIONS_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
                action: 'save_daily_summary', shop_id: currentShop.shop_id, summary_date: selectedDate,
                cash_opening: Number.parseFloat(data.cash_opening) || 0, cash_closing: Number.parseFloat(data.cash_closing) || 0,
                bank_opening: banks.reduce((sum, account) => sum + account.opening, 0), bank_closing: banks.reduce((sum, account) => sum + account.closing, 0),
                wallet_opening: wallets.reduce((sum, account) => sum + account.opening, 0), wallet_closing: wallets.reduce((sum, account) => sum + account.closing, 0),
                cheque_opening: Number.parseFloat(data.cheque_opening) || 0, cheque_closing: Number.parseFloat(data.cheque_closing) || 0,
                accounts, username: user?.username || 'system'
            }) });
            const result = await res.json();
            if (result.status === 'success') {
                showToast('success', result.message || 'Daily summary saved');
                setEditingId(result.data?.summary_id || null);
                await fetchSummary();
                await fetchHistory();
            } else showToast('error', result.message || 'Failed to save');
        } catch (err) {
            showToast('error', 'Error saving daily summary');
        } finally {
            setSaving(false);
        }
    }, [currentShop, selectedDate, data, user, showToast, fetchSummary, fetchHistory]);

    const handleEditHistory = useCallback(summary => {
        const date = summary.summary_date || summary.date;
        if (date) {
            setSelectedDate(String(date).split('T')[0]);
            window.requestAnimationFrame(() => document.querySelector('.oztransui-daily-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        }
    }, []);

    const handleDeleteHistory = useCallback(async summary => {
        const summaryId = summary.summary_id || summary.id;
        if (!summaryId || !window.confirm(`Delete Daily O/C for ${formatDate(summary.summary_date || summary.date)}?`)) return;
        try {
            const res = await fetch(TRANSACTIONS_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'delete_daily_summary', shop_id: currentShop.shop_id, summary_id: summaryId, username: user?.username || 'system' }) });
            const result = await res.json();
            if (result.status === 'success') {
                showToast('success', result.message || 'Daily O/C deleted successfully');
                if (String(editingId) === String(summaryId)) await fetchSummary();
                await fetchHistory();
            } else showToast('error', result.message || 'Failed to delete Daily O/C');
        } catch (err) {
            showToast('error', 'Failed to delete Daily O/C');
        }
    }, [currentShop, user, editingId, formatDate, showToast, fetchSummary, fetchHistory]);

    useEffect(() => { fetchSummary(); }, [selectedDate, fetchSummary]);
    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    const totals = getDailyTotals(data);
    const renderAccountEditor = (type, title, icon) => {
        const providerOptions = type === 'bank' ? PAKISTAN_BANKS : PAKISTAN_DIGITAL_WALLETS;
        const dropdownOptions = providerOptions.map(provider => ({ value: provider, label: provider, icon }));
        const customValue = type === 'bank' ? 'Other / Custom Bank' : 'Other / Custom Wallet';
        const rows = (data.accounts || []).filter(account => account.type === type);
        return (
            <section className="oztransui-oc-account-section">
                <header><div><FontAwesomeIcon icon={icon} /><strong>{title}</strong><small>{rows.length} account{rows.length === 1 ? '' : 's'}</small></div><button type="button" onClick={() => addAccount(type)}><FontAwesomeIcon icon={faPlus} /> Add {type === 'bank' ? 'Bank' : 'Wallet'}</button></header>
                {rows.length === 0 ? <p className="oztransui-oc-empty">No {title.toLowerCase()} added.</p> : rows.map((account, index) => (
                    <div className={`oztransui-oc-account-row ${account.provider_name === customValue ? 'has-custom' : ''}`} key={account.id}>
                        <span className="row-no">{index + 1}</span>
                        <div className="oztransui-oc-provider-field"><FilterDropdown label={type === 'bank' ? 'Select Bank' : 'Select Digital Wallet'} icon={icon} value={account.provider_name || ''} onChange={value => updateAccount(account.id, 'provider_name', value)} options={dropdownOptions} /></div>
                        {account.provider_name === customValue && <label><span>Provider name</span><input value={account.custom_provider || ''} onChange={event => updateAccount(account.id, 'custom_provider', event.target.value)} placeholder="Enter provider" /></label>}
                        <label><span>Account label</span><input value={account.account_label || ''} onChange={event => updateAccount(account.id, 'account_label', event.target.value)} placeholder="Main account" /></label>
                        <label><span>{type === 'bank' ? 'Account / IBAN' : 'Mobile / Account'}</span><input value={account.account_number || ''} onChange={event => updateAccount(account.id, 'account_number', event.target.value)} placeholder="Optional" /></label>
                        <label className="money"><span>Opening</span><input type="number" step="0.01" value={account.opening ?? ''} onChange={event => updateAccount(account.id, 'opening', event.target.value)} placeholder="0.00" /></label>
                        <label className="money"><span>Closing</span><input type="number" step="0.01" value={account.closing ?? ''} onChange={event => updateAccount(account.id, 'closing', event.target.value)} placeholder="0.00" /></label>
                        <button type="button" className="remove" onClick={() => removeAccount(account.id)} title="Remove"><FontAwesomeIcon icon={faTrash} /></button>
                    </div>
                ))}
            </section>
        );
    };

    const getHistoryRows = summary => {
        const accounts = normalizeDailyAccounts(summary.accounts, summary);
        const rows = [
            { key: 'cash', type: 'Cash', provider: 'Cash Drawer', reference: 'Shop cash', opening: Number.parseFloat(summary.cash_opening) || 0, closing: Number.parseFloat(summary.cash_closing) || 0 },
            ...accounts.map((account, index) => ({ key: `${account.type}-${account.id || index}`, type: account.type === 'wallet' ? 'Digital Wallet' : 'Bank', provider: account.custom_provider || account.provider_name || '-', reference: [account.account_label, account.account_number].filter(Boolean).join(' / ') || '-', opening: Number.parseFloat(account.opening) || 0, closing: Number.parseFloat(account.closing) || 0 })),
            { key: 'cheque', type: 'Cheque', provider: 'Cheques', reference: 'Shop cheques', opening: Number.parseFloat(summary.cheque_opening) || 0, closing: Number.parseFloat(summary.cheque_closing) || 0 }
        ];
        const summaryTotals = getDailyTotals({ ...summary, accounts });
        rows.push({ key: 'total', type: 'Daily Total', provider: `${accounts.filter(account => account.type === 'bank').length} bank(s), ${accounts.filter(account => account.type === 'wallet').length} wallet(s)`, reference: 'All methods', opening: summaryTotals.opening, closing: summaryTotals.closing, total: true });
        return rows;
    };

    return (
        <div className="oztransui-daily-summary">
            <section className="oztransui-daily-editor">
                <div className="oztransui-oc-editor-header"><div><h3><FontAwesomeIcon icon={faClock} /> Daily Opening / Closing</h3><p>Cash, cheque, unlimited bank accounts and digital wallets.</p></div><div className="oztransui-oc-editor-actions"><label>Date<input type="date" value={selectedDate} onChange={event => setSelectedDate(event.target.value)} /></label><button type="button" onClick={handleSave} disabled={loading || saving}><FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} /> {editingId ? 'Update Daily O/C' : 'Save Daily O/C'}</button></div></div>
                <div className="oztransui-oc-fixed-grid">
                    <div className="oztransui-oc-fixed"><strong><FontAwesomeIcon icon={faDollarSign} /> Cash</strong><label><span>Opening</span><input type="number" step="0.01" value={data.cash_opening ?? ''} onChange={event => setData(prev => ({ ...prev, cash_opening: event.target.value }))} /></label><label><span>Closing</span><input type="number" step="0.01" value={data.cash_closing ?? ''} onChange={event => setData(prev => ({ ...prev, cash_closing: event.target.value }))} /></label></div>
                    <div className="oztransui-oc-fixed"><strong><FontAwesomeIcon icon={faMoneyCheckDollar} /> Cheque</strong><label><span>Opening</span><input type="number" step="0.01" value={data.cheque_opening ?? ''} onChange={event => setData(prev => ({ ...prev, cheque_opening: event.target.value }))} /></label><label><span>Closing</span><input type="number" step="0.01" value={data.cheque_closing ?? ''} onChange={event => setData(prev => ({ ...prev, cheque_closing: event.target.value }))} /></label></div>
                </div>
                {renderAccountEditor('bank', 'Bank Accounts', faBuilding)}
                {renderAccountEditor('wallet', 'Digital Wallets', faWallet)}
                <div className="oztransui-oc-totals"><div><span>Total Opening</span><strong>Rs. {formatCurrency(totals.opening)}</strong></div><div><span>Total Closing</span><strong>Rs. {formatCurrency(totals.closing)}</strong></div><div><span>Net Change</span><strong className={totals.net >= 0 ? 'oztransui-positive' : 'oztransui-negative'}>{totals.net >= 0 ? '+' : ''}Rs. {formatCurrency(totals.net)}</strong></div></div>
            </section>

            {showHistory && <div className="oztransui-daily-history">
                <div className="oztransui-daily-header"><div className="oztransui-daily-title"><FontAwesomeIcon icon={faHistoryIcon} /><span>{t('dailyOCHistory') || 'Daily Opening / Closing History'}</span></div><button className="oztransui-btn-refresh" onClick={fetchHistory} disabled={historyLoading}><FontAwesomeIcon icon={faRefresh} spin={historyLoading} /></button></div>
                {historyLoading && history.length === 0 ? <div className="oztransui-loading-state"><FontAwesomeIcon icon={faSpinner} spin /> <span>{t('loading')}</span></div> : history.length === 0 ? <div className="oztransui-empty-state"><FontAwesomeIcon icon={faClock} /><p>No Daily O/C history found</p></div> :
                    <div className="oztransui-table-wrapper"><table className="oztransui-table"><thead><tr><th>#</th><th>Date</th><th>Method</th><th>Account / Provider</th><th>Reference</th><th className="oztransui-amount-cell">Opening</th><th className="oztransui-amount-cell">Closing</th><th className="oztransui-amount-cell">Net Change</th><th className="oztransui-actions-cell">Actions</th></tr></thead><tbody>
                        {history.map((summary, summaryIndex) => {
                            const rows = getHistoryRows(summary);
                            return rows.map((row, rowIndex) => <tr key={`${summary.summary_id || summaryIndex}-${row.key}`} className={row.total ? 'oztransui-oc-total-row' : ''}>
                                {rowIndex === 0 && <td rowSpan={rows.length}>{summaryIndex + 1}</td>}
                                {rowIndex === 0 && <td rowSpan={rows.length}>{formatDate(summary.summary_date || summary.date)}</td>}
                                <td>{row.type}</td><td>{row.provider}</td><td className="oztransui-oc-account-ref">{row.reference}</td>
                                <td className="oztransui-amount-cell">Rs. {formatCurrency(row.opening)}</td><td className="oztransui-amount-cell">Rs. {formatCurrency(row.closing)}</td><td className={`oztransui-amount-cell ${(row.closing - row.opening) >= 0 ? 'oztransui-positive' : 'oztransui-negative'}`}>{(row.closing - row.opening) >= 0 ? '+' : ''}Rs. {formatCurrency(row.closing - row.opening)}</td>
                                {rowIndex === 0 && <td rowSpan={rows.length} className="oztransui-actions-cell"><div className="oztransui-actions-group"><button className="oztransui-action-btn oztransui-edit-btn" onClick={() => handleEditHistory(summary)} title="Edit"><FontAwesomeIcon icon={faEdit} /></button><button className="oztransui-action-btn oztransui-delete-btn" onClick={() => handleDeleteHistory(summary)} title="Delete"><FontAwesomeIcon icon={faTrash} /></button></div></td>}
                            </tr>);
                        })}
                    </tbody></table></div>}
            </div>}
        </div>
    );
};

// ==============================================
// TRANSACTION TYPE CONFIG
// ==============================================
const TRANSACTION_TYPES = {
    sale: { label: 'Sale', icon: faShoppingCart, class: 'type-sale', arrow: faArrowDown, arrowType: 'incoming' },
    purchase: { label: 'Purchase', icon: faCartPlus, class: 'type-purchase', arrow: faArrowUp, arrowType: 'outgoing' },
    due: { label: 'Customer Due', icon: faUsers, class: 'type-due', arrow: faArrowUp, arrowType: 'outgoing' },
    payment_received: { label: 'Customer Payment', icon: faUsers, class: 'type-payment-received', arrow: faArrowDown, arrowType: 'incoming' },
    payment_to_supplier: { label: 'Supplier Payment', icon: faTruck, class: 'type-payment-to-supplier', arrow: faArrowUp, arrowType: 'outgoing' },
    supplier_due: { label: 'Supplier Due', icon: faTruck, class: 'type-supplier-due', arrow: faArrowUp, arrowType: 'outgoing' },
    salary_payment: { label: 'Salary Payment', icon: faUserTie, class: 'type-salary', arrow: faArrowUp, arrowType: 'outgoing' },
    expense: { label: 'Expense', icon: faReceipt, class: 'type-expense', arrow: faArrowUp, arrowType: 'outgoing' },
    return: { label: 'Return', icon: faUndo, class: 'type-return', arrow: faArrowUp, arrowType: 'outgoing' },
    purchase_return: { label: 'Purchase Return', icon: faUndo, class: 'type-payment-received', arrow: faArrowDown, arrowType: 'incoming' },
    supplier_return: { label: 'Purchase Return', icon: faUndo, class: 'type-payment-received', arrow: faArrowDown, arrowType: 'incoming' },
    partner_investment: { label: 'Partner Investment', icon: faHandshake, class: 'type-partner-investment', arrow: faArrowDown, arrowType: 'incoming' },
    partner_payment: { label: 'Partner Payment', icon: faHandshake, class: 'type-partner-payment', arrow: faArrowUp, arrowType: 'outgoing' },
    employee_salary: { label: 'Employee Salary', icon: faUserTie, class: 'type-employee-salary', arrow: faArrowUp, arrowType: 'outgoing' },
    advance_received: { label: 'Advance Received', icon: faHandHoldingUsd, class: 'type-payment-received', arrow: faArrowDown, arrowType: 'incoming' },
    advance_received_customer: { label: 'Advance Received from Customer', icon: faHandHoldingUsd, class: 'type-payment-received', arrow: faArrowDown, arrowType: 'incoming' },
    advance_paid_supplier: { label: 'Advance Paid to Supplier', icon: faTruck, class: 'type-payment-to-supplier', arrow: faArrowUp, arrowType: 'outgoing' },
    advance_return_customer: { label: 'Advance Return to Customer', icon: faUndo, class: 'type-return', arrow: faArrowUp, arrowType: 'outgoing' },
    advance_return_supplier: { label: 'Advance Returned Back by Supplier', icon: faUndo, class: 'type-payment-received', arrow: faArrowDown, arrowType: 'incoming' }
};

// ==============================================
// ENHANCED CHART COLORS
// ==============================================
const chartColors = {
    green: '#10b981',
    greenLight: 'rgba(16, 185, 129, 0.2)',
    greenGradient: 'rgba(16, 185, 129, 0.4)',
    blue: '#3b82f6',
    blueLight: 'rgba(59, 130, 246, 0.2)',
    blueGradient: 'rgba(59, 130, 246, 0.4)',
    purple: '#8b5cf6',
    purpleLight: 'rgba(139, 92, 246, 0.2)',
    purpleGradient: 'rgba(139, 92, 246, 0.4)',
    orange: '#f59e0b',
    orangeLight: 'rgba(245, 158, 11, 0.2)',
    orangeGradient: 'rgba(245, 158, 11, 0.4)',
    red: '#ef4444',
    redLight: 'rgba(239, 68, 68, 0.2)',
    redGradient: 'rgba(239, 68, 68, 0.4)',
    pink: '#ec4899',
    pinkLight: 'rgba(236, 72, 153, 0.2)',
    teal: '#14b8a6',
    tealLight: 'rgba(20, 184, 166, 0.2)',
    cyan: '#06b6d4',
    cyanLight: 'rgba(6, 182, 212, 0.2)',
    indigo: '#6366f1',
    indigoLight: 'rgba(99, 102, 241, 0.2)',
    gray: '#64748b',
    grayLight: 'rgba(100, 116, 139, 0.2)'
};

const chartColorPalette = [
    '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444',
    '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#06b6d4'
];

const chartColorPaletteLight = [
    'rgba(16, 185, 129, 0.7)', 'rgba(59, 130, 246, 0.7)', 'rgba(139, 92, 246, 0.7)',
    'rgba(245, 158, 11, 0.7)', 'rgba(239, 68, 68, 0.7)',
    'rgba(236, 72, 153, 0.7)', 'rgba(20, 184, 166, 0.7)', 'rgba(249, 115, 22, 0.7)',
    'rgba(99, 102, 241, 0.7)', 'rgba(6, 182, 212, 0.7)'
];

// ==============================================
// ENHANCED CHART COMPONENTS
// ==============================================

// ── Enhanced Gradient Line Chart ──
const EnhancedLineChart = ({ data, labels, title, formatCurrencyFn, height = 280, showLegend = true }) => {
    const gradientRef = useRef(null);

    const createGradient = (ctx, color1, color2) => {
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, color1);
        gradient.addColorStop(1, color2);
        return gradient;
    };

    const chartData = {
        labels: labels || data.map((_, i) => `Period ${i + 1}`),
        datasets: [
            {
                label: 'Revenue',
                data: data.map(d => d.revenue || 0),
                borderColor: chartColors.green,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return chartColors.greenLight;
                    return createGradient(ctx, chartColors.green + '80', chartColors.greenLight);
                },
                fill: true,
                tension: 0.4,
                pointBackgroundColor: chartColors.green,
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 8,
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: chartColors.green,
                pointHoverBorderWidth: 3
            },
            {
                label: 'Profit',
                data: data.map(d => d.profit || 0),
                borderColor: chartColors.blue,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return chartColors.blueLight;
                    return createGradient(ctx, chartColors.blue + '80', chartColors.blueLight);
                },
                fill: true,
                tension: 0.4,
                pointBackgroundColor: chartColors.blue,
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 8,
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: chartColors.blue,
                pointHoverBorderWidth: 3
            },
            {
                label: 'Expenses',
                data: data.map(d => d.expenses || 0),
                borderColor: chartColors.red,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return chartColors.redLight;
                    return createGradient(ctx, chartColors.red + '80', chartColors.redLight);
                },
                fill: true,
                tension: 0.4,
                pointBackgroundColor: chartColors.red,
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 8,
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: chartColors.red,
                pointHoverBorderWidth: 3
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1200,
            easing: 'easeInOutQuart'
        },
        plugins: {
            legend: {
                display: showLegend,
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: { size: 12, weight: '600' },
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a'
                }
            },
            tooltip: {
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-bg') || '#ffffff',
                titleColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a',
                bodyColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text-secondary') || '#64748b',
                borderColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: Rs. ${formatCurrencyFn(context.parsed.y)}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        return 'Rs. ' + formatCurrencyFn(value);
                    },
                    font: { size: 11 }
                },
                grid: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb',
                    drawBorder: false
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: { size: 11 }
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        }
    };

    return (
        <div className="oztransui-chart-wrapper" style={{ height: `${height}px` }}>
            {title && <div className="oztransui-chart-title"><FontAwesomeIcon icon={faChartLine} /> {title}</div>}
            <Line data={chartData} options={options} />
        </div>
    );
};

// ── Enhanced Animated Pie Chart ──
const EnhancedPieChart = ({ data, labels, title, formatCurrencyFn, height = 280, isDoughnut = false }) => {
    const chartData = {
        labels: labels || data.map((_, i) => `Item ${i + 1}`),
        datasets: [
            {
                data: data.map(d => d.value || 0),
                backgroundColor: chartColorPaletteLight.slice(0, data.length),
                borderColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-bg') || '#ffffff',
                borderWidth: 3,
                hoverOffset: 12
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: isDoughnut ? '55%' : '0%',
        animation: {
            animateRotate: true,
            duration: 1500,
            easing: 'easeInOutQuart'
        },
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    usePointStyle: true,
                    padding: 16,
                    font: { size: 12, weight: '500' },
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a'
                }
            },
            tooltip: {
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-bg') || '#ffffff',
                titleColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a',
                bodyColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text-secondary') || '#64748b',
                borderColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                callbacks: {
                    label: function (context) {
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                        return `${context.label}: Rs. ${formatCurrencyFn(context.parsed)} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                display: false
            }
        }
    };

    const ChartComponent = isDoughnut ? Doughnut : Pie;

    return (
        <div className="oztransui-chart-wrapper" style={{ height: `${height}px` }}>
            {title && <div className="oztransui-chart-title"><FontAwesomeIcon icon={faChartPie} /> {title}</div>}
            <ChartComponent data={chartData} options={options} />
        </div>
    );
};

// ── Enhanced Animated Bar Chart ──
const EnhancedBarChart = ({ data, labels, title, formatCurrencyFn, height = 280, isStacked = false }) => {
    const chartData = {
        labels: labels || data.map((_, i) => `Period ${i + 1}`),
        datasets: [
            {
                label: 'Revenue',
                data: data.map(d => d.revenue || 0),
                backgroundColor: chartColors.green,
                borderColor: chartColors.green,
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.8,
                categoryPercentage: 0.7
            },
            {
                label: 'Profit',
                data: data.map(d => d.profit || 0),
                backgroundColor: chartColors.blue,
                borderColor: chartColors.blue,
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.8,
                categoryPercentage: 0.7
            },
            {
                label: 'Expenses',
                data: data.map(d => d.expenses || 0),
                backgroundColor: chartColors.red,
                borderColor: chartColors.red,
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.8,
                categoryPercentage: 0.7
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1000,
            easing: 'easeInOutQuart'
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 16,
                    font: { size: 12, weight: '600' },
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a'
                }
            },
            tooltip: {
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-bg') || '#ffffff',
                titleColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a',
                bodyColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text-secondary') || '#64748b',
                borderColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: Rs. ${formatCurrencyFn(context.parsed.y)}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                stacked: isStacked,
                ticks: {
                    callback: function (value) {
                        return 'Rs. ' + formatCurrencyFn(value);
                    },
                    font: { size: 11 }
                },
                grid: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb',
                    drawBorder: false
                }
            },
            x: {
                stacked: isStacked,
                grid: {
                    display: false
                },
                ticks: {
                    font: { size: 11 }
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        }
    };

    return (
        <div className="oztransui-chart-wrapper" style={{ height: `${height}px` }}>
            {title && <div className="oztransui-chart-title"><FontAwesomeIcon icon={faChartBar} /> {title}</div>}
            <Bar data={chartData} options={options} />
        </div>
    );
};

// ── Enhanced Area Chart (Vacuum Style) ──
const EnhancedAreaChart = ({ data, labels, title, formatCurrencyFn, height = 280 }) => {
    const chartData = {
        labels: labels || data.map((_, i) => `Period ${i + 1}`),
        datasets: [
            {
                label: 'Expenses',
                data: data.map(d => d.expenses || 0),
                borderColor: chartColors.red,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return chartColors.redLight;
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, chartColors.red + '60');
                    gradient.addColorStop(1, chartColors.red + '05');
                    return gradient;
                },
                fill: true,
                tension: 0.4,
                pointBackgroundColor: chartColors.red,
                pointRadius: 3,
                pointHoverRadius: 7,
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: chartColors.red,
                pointHoverBorderWidth: 3
            },
            {
                label: 'Salaries',
                data: data.map(d => d.salaries || 0),
                borderColor: chartColors.orange,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return chartColors.orangeLight;
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, chartColors.orange + '60');
                    gradient.addColorStop(1, chartColors.orange + '05');
                    return gradient;
                },
                fill: true,
                tension: 0.4,
                pointBackgroundColor: chartColors.orange,
                pointRadius: 3,
                pointHoverRadius: 7,
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: chartColors.orange,
                pointHoverBorderWidth: 3
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1200,
            easing: 'easeInOutQuart'
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 16,
                    font: { size: 12, weight: '600' },
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a'
                }
            },
            tooltip: {
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-bg') || '#ffffff',
                titleColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a',
                bodyColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text-secondary') || '#64748b',
                borderColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: Rs. ${formatCurrencyFn(context.parsed.y)}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        return 'Rs. ' + formatCurrencyFn(value);
                    },
                    font: { size: 11 }
                },
                grid: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb',
                    drawBorder: false
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: { size: 11 }
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        }
    };

    return (
        <div className="oztransui-chart-wrapper" style={{ height: `${height}px` }}>
            {title && <div className="oztransui-chart-title"><FontAwesomeIcon icon={faChartLine} /> {title}</div>}
            <Line data={chartData} options={options} />
        </div>
    );
};

// ── Enhanced Polar Area Chart ──
const EnhancedPolarChart = ({ data, labels, title, formatCurrencyFn, height = 280 }) => {
    const chartData = {
        labels: labels || data.map((_, i) => `Item ${i + 1}`),
        datasets: [
            {
                data: data.map(d => d.value || 0),
                backgroundColor: chartColorPaletteLight.slice(0, data.length),
                borderColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-bg') || '#ffffff',
                borderWidth: 2,
                hoverOffset: 8
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1200,
            easing: 'easeInOutQuart'
        },
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 14,
                    font: { size: 11, weight: '500' },
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a'
                }
            },
            tooltip: {
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-bg') || '#ffffff',
                titleColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a',
                bodyColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text-secondary') || '#64748b',
                borderColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                callbacks: {
                    label: function (context) {
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                        return `${context.label}: Rs. ${formatCurrencyFn(context.parsed)} (${percentage}%)`;
                    }
                }
            }
        },
        scales: {
            r: {
                beginAtZero: true,
                grid: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb'
                },
                ticks: {
                    font: { size: 10 },
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text-secondary') || '#64748b'
                }
            }
        }
    };

    return (
        <div className="oztransui-chart-wrapper" style={{ height: `${height}px` }}>
            {title && <div className="oztransui-chart-title"><FontAwesomeIcon icon={faChartPie} /> {title}</div>}
            <PolarArea data={chartData} options={options} />
        </div>
    );
};

// ── Enhanced Radar Chart ──
const EnhancedRadarChart = ({ data, labels, title, formatCurrencyFn, height = 280 }) => {
    const chartData = {
        labels: labels || ['Revenue', 'Profit', 'Expenses', 'Salaries', 'Returns', 'COGS'],
        datasets: data.map((dataset, index) => ({
            label: dataset.label || `Dataset ${index + 1}`,
            data: dataset.values || [],
            borderColor: chartColorPalette[index % chartColorPalette.length],
            backgroundColor: chartColorPalette[index % chartColorPalette.length] + '25',
            pointBackgroundColor: chartColorPalette[index % chartColorPalette.length],
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 8,
            borderWidth: 2
        }))
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1200,
            easing: 'easeInOutQuart'
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 16,
                    font: { size: 12, weight: '600' },
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a'
                }
            },
            tooltip: {
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-bg') || '#ffffff',
                titleColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a',
                bodyColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text-secondary') || '#64748b',
                borderColor: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: ${formatCurrencyFn(context.parsed.r)}`;
                    }
                }
            }
        },
        scales: {
            r: {
                beginAtZero: true,
                grid: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-border') || '#e5e7eb'
                },
                ticks: {
                    font: { size: 10 },
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text-secondary') || '#64748b'
                },
                pointLabels: {
                    font: { size: 11, weight: '500' },
                    color: getComputedStyle(document.documentElement).getPropertyValue('--oztransui-text') || '#0f172a'
                }
            }
        }
    };

    return (
        <div className="oztransui-chart-wrapper" style={{ height: `${height}px` }}>
            {title && <div className="oztransui-chart-title"><FontAwesomeIcon icon={faChartPie} /> {title}</div>}
            <Radar data={chartData} options={options} />
        </div>
    );
};

// ── Stats Card with Trend ──
const StatsTrendCard = ({ icon, label, value, trend, trendLabel, tone = 'default', formatCurrencyFn }) => {
    const isPositive = trend >= 0;
    return (
        <div className={`oztransui-stats-trend-card oztransui-stats-tone-${tone}`}>
            <div className="oztransui-stats-trend-icon">
                <FontAwesomeIcon icon={icon} />
            </div>
            <div className="oztransui-stats-trend-content">
                <span className="oztransui-stats-trend-label">{label}</span>
                <span className="oztransui-stats-trend-value">Rs. {formatCurrencyFn(value)}</span>
                <div className={`oztransui-stats-trend-indicator ${isPositive ? 'positive' : 'negative'}`}>
                    <FontAwesomeIcon icon={isPositive ? faArrowTrendUp : faArrowTrendDown} />
                    <span>{Math.abs(trend).toFixed(1)}% {trendLabel || (isPositive ? 'Growth' : 'Decline')}</span>
                </div>
            </div>
        </div>
    );
};

// ── Mini Progress Bar ──
const MiniProgressBar = ({ label, value, total, color, formatCurrencyFn }) => {
    const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
    return (
        <div className="oztransui-mini-progress">
            <div className="oztransui-mini-progress-header">
                <span className="oztransui-mini-progress-label">{label}</span>
                <span className="oztransui-mini-progress-value">Rs. {formatCurrencyFn(value)}</span>
            </div>
            <div className="oztransui-mini-progress-track">
                <div
                    className="oztransui-mini-progress-fill"
                    style={{ width: `${pct}%`, background: color }}
                />
            </div>
            <span className="oztransui-mini-progress-pct">{pct.toFixed(1)}%</span>
        </div>
    );
};

// ==============================================
// PROFIT & LOSS COMPONENTS
// ==============================================

// ── Stock Profit Card Component ──
const StockProfitCard = ({ icon, label, value, subValue, subItems = [], tone = 'default', subLabel = '', formatCurrencyFn }) => (
    <div className={`oztransui-pl-card oztransui-pl-tone-${tone}`}>
        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={icon} /></div>
        <div className="oztransui-pl-card-content">
            <span className="oztransui-pl-card-label">{label}</span>
            <span className="oztransui-pl-card-value">Rs. {formatCurrencyFn(value)}</span>
            {subValue && <span className="oztransui-pl-card-sub">{subLabel}: {formatCurrencyFn(subValue)}</span>}
            {subItems.length > 0 && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                    marginTop: '7px',
                    paddingTop: '7px',
                    borderTop: '1px solid rgba(16, 185, 129, 0.22)'
                }}>
                    {subItems.map(item => (
                        <span
                            key={item.label}
                            className="oztransui-pl-card-sub"
                            style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}
                        >
                            <span>{item.label}</span>
                            <strong>{item.currency !== false ? 'Rs. ' : ''}{formatCurrencyFn(item.value)}</strong>
                        </span>
                    ))}
                </div>
            )}
        </div>
    </div>
);

// ── PL Bar Row Component ──
const PLBarRow = ({ icon, label, amount, total, formatCurrencyFn, colorClass }) => {
    const pct = total > 0 ? Math.min(100, (amount / total) * 100) : 0;
    return (
        <div className="oztransui-pl-barrow">
            <div className="oztransui-pl-barrow-top">
                <span className="oztransui-pl-barrow-label"><FontAwesomeIcon icon={icon} /> {label}</span>
                <span className="oztransui-pl-barrow-value">Rs. {formatCurrencyFn(amount)}</span>
            </div>
            <div className="oztransui-pl-barrow-track">
                <div className={`oztransui-pl-barrow-fill oztransui-pl-fill-${colorClass}`} style={{ width: `${pct}%` }}></div>
            </div>
            <span className="oztransui-pl-barrow-pct">{pct.toFixed(1)}%</span>
        </div>
    );
};

// ── PL Flow Chart Component ──
const PLFlowChart = ({ steps, formatCurrencyFn }) => {
    const max = Math.max(1, ...steps.map(s => Math.abs(s.value)));
    return (
        <div className="oztransui-pl-flow">
            {steps.map((step, idx) => {
                const widthPct = Math.max(4, (Math.abs(step.value) / max) * 100);
                return (
                    <div className="oztransui-pl-flow-row" key={idx}>
                        <span className="oztransui-pl-flow-label">{step.label}</span>
                        <div className="oztransui-pl-flow-track">
                            <div className={`oztransui-pl-flow-fill oztransui-pl-fill-${step.tone}`} style={{ width: `${widthPct}%` }}></div>
                        </div>
                        <span className={`oztransui-pl-flow-value oztransui-pl-text-${step.tone}`}>
                            {step.value < 0 ? '- ' : ''}Rs. {formatCurrencyFn(Math.abs(step.value))}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

// ── Product Filter Component ──
const ProductFilter = ({ products, selectedProducts, onSelect, onClear, t }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    const filteredProducts = searchTerm.trim() === ''
        ? products
        : products.filter(p =>
            (p.product_name || p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.company || p.brand || '').toLowerCase().includes(searchTerm.toLowerCase())
        );

    const toggleProduct = (product) => {
        const id = product.id || product.product_id;
        if (selectedProducts.some(p => (p.id || p.product_id) === id)) {
            onSelect(selectedProducts.filter(p => (p.id || p.product_id) !== id));
        } else {
            onSelect([...selectedProducts, product]);
        }
    };

    const selectAll = () => {
        if (selectedProducts.length === products.length) {
            onSelect([]);
        } else {
            onSelect([...products]);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="oztransui-product-filter" ref={wrapperRef}>
            <div className="oztransui-product-filter-header">
                <div className="oztransui-product-filter-search">
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                        type="text"
                        placeholder={t('searchProducts') || 'Search products...'}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => setIsOpen(true)}
                    />
                </div>
                <button className="oztransui-product-filter-toggle" onClick={() => setIsOpen(!isOpen)}>
                    <FontAwesomeIcon icon={faChevronDown} className={isOpen ? 'oztransui-rotate' : ''} />
                </button>
            </div>
            <div className={`oztransui-product-filter-dropdown ${isOpen ? 'oztransui-open' : ''}`}>
                <div className="oztransui-product-filter-actions">
                    <button onClick={selectAll}>
                        {selectedProducts.length === products.length ? t('deselectAll') || 'Deselect All' : t('selectAll') || 'Select All'}
                    </button>
                    <button onClick={onClear}>{t('clear') || 'Clear'}</button>
                    <span className="oztransui-product-count">{selectedProducts.length} {t('selected') || 'selected'}</span>
                </div>
                <div className="oztransui-product-list">
                    {filteredProducts.length === 0 ? (
                        <div className="oztransui-no-products">{t('noProducts') || 'No products found'}</div>
                    ) : (
                        filteredProducts.map((product, index) => {
                            const id = product.id || product.product_id;
                            const isSelected = selectedProducts.some(p => (p.id || p.product_id) === id);
                            return (
                                <div
                                    key={id || index}
                                    className={`oztransui-product-item ${isSelected ? 'oztransui-selected' : ''}`}
                                    onClick={() => toggleProduct(product)}
                                >
                                    <div className="oztransui-product-item-info">
                                        <span className="oztransui-product-item-name">{product.product_name || product.name || 'Unknown'}</span>
                                        {product.category && <span className="oztransui-product-item-category">{product.category}</span>}
                                        {product.company && <span className="oztransui-product-item-company">{product.company}</span>}
                                    </div>
                                    <div className="oztransui-product-item-stats">
                                        <span>Qty: {product.quantity || 0}</span>
                                        <span>Cost: Rs. {formatCurrency(product.cost_price || 0)}</span>
                                        <span>Sell: Rs. {formatCurrency(product.sell_price || 0)}</span>
                                    </div>
                                    {isSelected && <FontAwesomeIcon icon={faCheckCircle} className="oztransui-product-check" />}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Stock-based Profit/Loss Component ──
const StockProfitLoss = ({ products, formatCurrencyFn, t }) => {
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [filterType, setFilterType] = useState('all');
    const [filterValue, setFilterValue] = useState('');
    const [viewMode, setViewMode] = useState('summary');

    const categories = useMemo(() => {
        const cats = new Set();
        products.forEach(p => {
            if (p.category) cats.add(p.category);
        });
        return Array.from(cats);
    }, [products]);

    const companies = useMemo(() => {
        const comps = new Set();
        products.forEach(p => {
            if (p.company || p.brand) comps.add(p.company || p.brand);
        });
        return Array.from(comps);
    }, [products]);

    const filteredProducts = useMemo(() => {
        let result = products;

        if (selectedProducts.length > 0) {
            const selectedIds = new Set(selectedProducts.map(p => p.id || p.product_id));
            result = result.filter(p => selectedIds.has(p.id || p.product_id));
        }

        if (filterType === 'category' && filterValue) {
            result = result.filter(p => p.category === filterValue);
        } else if (filterType === 'company' && filterValue) {
            result = result.filter(p => (p.company || p.brand) === filterValue);
        }

        return result;
    }, [products, selectedProducts, filterType, filterValue]);

    const stockData = useMemo(() => {
        let totalCost = 0;
        let totalValue = 0;
        let totalProfit = 0;
        let totalQuantity = 0;

        filteredProducts.forEach(product => {
            const quantity = parseFloat(product.quantity) || 0;
            const costPrice = parseFloat(product.cost_price) || parseFloat(product.cost_per_unit) || 0;
            const sellPrice = parseFloat(product.sell_price) || parseFloat(product.sell_price_per_unit) || 0;

            if (quantity > 0 && costPrice > 0 && sellPrice > 0) {
                const cost = quantity * costPrice;
                const value = quantity * sellPrice;
                const profit = quantity * (sellPrice - costPrice);

                totalCost += cost;
                totalValue += value;
                totalProfit += profit;
                totalQuantity += quantity;
            }
        });

        return {
            totalCost,
            totalValue,
            totalProfit,
            totalQuantity,
            productCount: filteredProducts.length,
            profitMargin: totalValue > 0 ? (totalProfit / totalValue) * 100 : 0
        };
    }, [filteredProducts]);

    const categoryData = useMemo(() => {
        const groups = {};
        filteredProducts.forEach(product => {
            const category = product.category || 'Uncategorized';
            if (!groups[category]) {
                groups[category] = { cost: 0, value: 0, profit: 0, quantity: 0, count: 0 };
            }
            const quantity = parseFloat(product.quantity) || 0;
            const costPrice = parseFloat(product.cost_price) || 0;
            const sellPrice = parseFloat(product.sell_price) || 0;
            if (quantity > 0 && costPrice > 0 && sellPrice > 0) {
                groups[category].cost += quantity * costPrice;
                groups[category].value += quantity * sellPrice;
                groups[category].profit += quantity * (sellPrice - costPrice);
                groups[category].quantity += quantity;
                groups[category].count += 1;
            }
        });
        return Object.entries(groups).map(([name, data]) => ({ name, ...data }));
    }, [filteredProducts]);

    const companyData = useMemo(() => {
        const groups = {};
        filteredProducts.forEach(product => {
            const company = product.company || product.brand || 'Unknown';
            if (!groups[company]) {
                groups[company] = { cost: 0, value: 0, profit: 0, quantity: 0, count: 0 };
            }
            const quantity = parseFloat(product.quantity) || 0;
            const costPrice = parseFloat(product.cost_price) || 0;
            const sellPrice = parseFloat(product.sell_price) || 0;
            if (quantity > 0 && costPrice > 0 && sellPrice > 0) {
                groups[company].cost += quantity * costPrice;
                groups[company].value += quantity * sellPrice;
                groups[company].profit += quantity * (sellPrice - costPrice);
                groups[company].quantity += quantity;
                groups[company].count += 1;
            }
        });
        return Object.entries(groups).map(([name, data]) => ({ name, ...data }));
    }, [filteredProducts]);

    if (!products || products.length === 0) {
        return (
            <div className="oztransui-no-data">
                <FontAwesomeIcon icon={faBoxes} />
                <p>{t('noProducts') || 'No products available'}</p>
            </div>
        );
    }

    return (
        <div className="oztransui-stock-pl">
            <div className="oztransui-stock-pl-header">
                <h4><FontAwesomeIcon icon={faBoxes} /> {t('stockProfitAnalysis') || 'Stock Profit Analysis'}</h4>
                <div className="oztransui-stock-pl-controls">
                    <div className="oztransui-view-toggle">
                        <button className={`oztransui-view-btn ${viewMode === 'summary' ? 'active' : ''}`} onClick={() => setViewMode('summary')}>
                            <FontAwesomeIcon icon={faThLarge} /> {t('summary') || 'Summary'}
                        </button>
                        <button className={`oztransui-view-btn ${viewMode === 'detailed' ? 'active' : ''}`} onClick={() => setViewMode('detailed')}>
                            <FontAwesomeIcon icon={faChartPie} /> {t('detailed') || 'Detailed'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="oztransui-stock-pl-filters">
                <div className="oztransui-stock-pl-filter-row">
                    <div className="oztransui-filter-group">
                        <label>{t('filterBy') || 'Filter By'}:</label>
                        <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setFilterValue(''); }}>
                            <option value="all">{t('allProducts') || 'All Products'}</option>
                            <option value="category">{t('category') || 'Category'}</option>
                            <option value="company">{t('company') || 'Company'}</option>
                        </select>
                    </div>
                    {filterType !== 'all' && (
                        <div className="oztransui-filter-group">
                            <label>{filterType === 'category' ? t('selectCategory') || 'Select Category' : t('selectCompany') || 'Select Company'}:</label>
                            <select value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                                <option value="">{t('all') || 'All'}</option>
                                {(filterType === 'category' ? categories : companies).map(item => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
                <div className="oztransui-stock-pl-product-filter">
                    <ProductFilter
                        products={products}
                        selectedProducts={selectedProducts}
                        onSelect={setSelectedProducts}
                        onClear={() => setSelectedProducts([])}
                        t={t}
                    />
                </div>
            </div>

            <div className="oztransui-stock-pl-summary">
                <div className="oztransui-stock-pl-stats">
                    <div className="oztransui-pl-card oztransui-pl-tone-warning">
                        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faBoxes} /></div>
                        <div className="oztransui-pl-card-content">
                            <span className="oztransui-pl-card-label">{t('totalStockCost') || 'Total Stock Cost'}</span>
                            <span className="oztransui-pl-card-value">Rs. {formatCurrencyFn(stockData.totalCost)}</span>
                        </div>
                    </div>
                    <div className="oztransui-pl-card oztransui-pl-tone-info">
                        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faTag} /></div>
                        <div className="oztransui-pl-card-content">
                            <span className="oztransui-pl-card-label">{t('totalStockValue') || 'Total Stock Value'}</span>
                            <span className="oztransui-pl-card-value">Rs. {formatCurrencyFn(stockData.totalValue)}</span>
                        </div>
                    </div>
                    <div className="oztransui-pl-card oztransui-pl-tone-success">
                        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faChartLine} /></div>
                        <div className="oztransui-pl-card-content">
                            <span className="oztransui-pl-card-label">{t('estimatedProfit') || 'Estimated Profit'}</span>
                            <span className="oztransui-pl-card-value">Rs. {formatCurrencyFn(stockData.totalProfit)}</span>
                            <span className="oztransui-pl-card-sub">{t('profitMargin') || 'Profit Margin'}: {stockData.profitMargin.toFixed(2)}%</span>
                        </div>
                    </div>
                    <div className="oztransui-pl-card oztransui-pl-tone-default">
                        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faShoppingCart} /></div>
                        <div className="oztransui-pl-card-content">
                            <span className="oztransui-pl-card-label">{t('totalItems') || 'Total Items'}</span>
                            <span className="oztransui-pl-card-value">{stockData.totalQuantity}</span>
                            <span className="oztransui-pl-card-sub">{t('products') || 'Products'}: {stockData.productCount}</span>
                        </div>
                    </div>
                </div>
            </div>

            {viewMode === 'detailed' && (
                <div className="oztransui-stock-pl-detailed">
                    {categoryData.length > 0 && (
                        <div className="oztransui-pl-panel">
                            <h5><FontAwesomeIcon icon={faLayerGroup} /> {t('categoryBreakdown') || 'Category Breakdown'}</h5>
                            <div className="oztransui-stock-pl-breakdown">
                                {categoryData.map(cat => (
                                    <div key={cat.name} className="oztransui-breakdown-item">
                                        <div className="oztransui-breakdown-header">
                                            <span className="oztransui-breakdown-name">{cat.name}</span>
                                            <span className="oztransui-breakdown-count">{cat.count} {t('products') || 'products'}</span>
                                        </div>
                                        <div className="oztransui-breakdown-stats">
                                            <span>Cost: Rs. {formatCurrencyFn(cat.cost)}</span>
                                            <span>Value: Rs. {formatCurrencyFn(cat.value)}</span>
                                            <span className="oztransui-breakdown-profit">Profit: Rs. {formatCurrencyFn(cat.profit)}</span>
                                            <span className="oztransui-breakdown-margin">Margin: {cat.value > 0 ? ((cat.profit / cat.value) * 100).toFixed(2) : 0}%</span>
                                        </div>
                                        <div className="oztransui-breakdown-bar">
                                            <div className="oztransui-breakdown-bar-fill" style={{ width: `${cat.value > 0 ? (cat.profit / cat.value) * 100 : 0}%` }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {companyData.length > 0 && (
                        <div className="oztransui-pl-panel">
                            <h5><FontAwesomeIcon icon={faBuilding} /> {t('companyBreakdown') || 'Company Breakdown'}</h5>
                            <div className="oztransui-stock-pl-breakdown">
                                {companyData.map(comp => (
                                    <div key={comp.name} className="oztransui-breakdown-item">
                                        <div className="oztransui-breakdown-header">
                                            <span className="oztransui-breakdown-name">{comp.name}</span>
                                            <span className="oztransui-breakdown-count">{comp.count} {t('products') || 'products'}</span>
                                        </div>
                                        <div className="oztransui-breakdown-stats">
                                            <span>Cost: Rs. {formatCurrencyFn(comp.cost)}</span>
                                            <span>Value: Rs. {formatCurrencyFn(comp.value)}</span>
                                            <span className="oztransui-breakdown-profit">Profit: Rs. {formatCurrencyFn(comp.profit)}</span>
                                            <span className="oztransui-breakdown-margin">Margin: {comp.value > 0 ? ((comp.profit / comp.value) * 100).toFixed(2) : 0}%</span>
                                        </div>
                                        <div className="oztransui-breakdown-bar">
                                            <div className="oztransui-breakdown-bar-fill" style={{ width: `${comp.value > 0 ? (comp.profit / comp.value) * 100 : 0}%` }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Sales-based Profit/Loss Component ──
const SalesProfitLoss = ({ transactions, saleCostsById = {}, formatCurrencyFn, t }) => {
    const [filterType, setFilterType] = useState('all');
    const [filterValue, setFilterValue] = useState('');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [viewMode, setViewMode] = useState('summary');

    const references = useMemo(() => {
        const refs = new Set();
        transactions.forEach(t => {
            if (t.reference_name) refs.add(t.reference_name);
        });
        return Array.from(refs);
    }, [transactions]);

    const filteredTransactions = useMemo(() => {
        let result = transactions.filter(t => t.transaction_type === 'sale');

        if (filterType === 'customer' && filterValue) {
            result = result.filter(t => t.reference_name === filterValue);
        }

        if (dateRange.start) {
            result = result.filter(t => t.transaction_date >= dateRange.start);
        }
        if (dateRange.end) {
            result = result.filter(t => t.transaction_date <= dateRange.end);
        }

        return result;
    }, [transactions, filterType, filterValue, dateRange]);

    const filteredReturns = useMemo(() => {
        let result = transactions.filter(t => t.transaction_type === 'return');

        if (filterType === 'customer' && filterValue) {
            result = result.filter(t => t.reference_name === filterValue);
        }
        if (dateRange.start) {
            result = result.filter(t => t.transaction_date >= dateRange.start);
        }
        if (dateRange.end) {
            result = result.filter(t => t.transaction_date <= dateRange.end);
        }

        return result;
    }, [transactions, filterType, filterValue, dateRange]);

    const salesData = useMemo(() => {
        let totalRevenue = 0;
        let totalProfit = 0;
        let totalCOGS = 0;
        let totalCount = 0;

        filteredTransactions.forEach(t => {
            const amount = parseFloat(t.grand_total || t.amount || 0);
            const cogs = saleCostsById[String(t.sale_id)] ??
                (parseFloat(t.cost_price_total) || parseFloat(t.total_cogs) ||
                    (filteredTransactions.length === 1 ? saleCostsById.__single : 0) || 0);
            const profit = amount - cogs;

            totalRevenue += amount;
            totalProfit += profit;
            totalCOGS += cogs;
            totalCount += 1;
        });

        filteredReturns.forEach(t => {
            const returnValue = parseFloat(t.grand_total || t.amount || 0);
            const returnedCost = parseFloat(t.returned_cost) || 0;
            totalRevenue -= returnValue;
            totalCOGS -= returnedCost;
            totalProfit -= returnValue - returnedCost;
        });

        return {
            totalRevenue,
            totalProfit,
            totalCOGS,
            totalCount,
            profitMargin: totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0
        };
    }, [filteredTransactions, filteredReturns, saleCostsById]);

    const customerData = useMemo(() => {
        const groups = {};
        filteredTransactions.forEach(t => {
            const name = t.reference_name || 'Unknown';
            if (!groups[name]) {
                groups[name] = { revenue: 0, profit: 0, cogs: 0, count: 0 };
            }
            const amount = parseFloat(t.grand_total || t.amount || 0);
            const cogs = saleCostsById[String(t.sale_id)] ??
                (parseFloat(t.cost_price_total) || parseFloat(t.total_cogs) ||
                    (filteredTransactions.length === 1 ? saleCostsById.__single : 0) || 0);
            const profit = amount - cogs;
            groups[name].revenue += amount;
            groups[name].profit += profit;
            groups[name].cogs += cogs;
            groups[name].count += 1;
        });
        filteredReturns.forEach(t => {
            const name = t.reference_name || 'Unknown';
            if (!groups[name]) {
                groups[name] = { revenue: 0, profit: 0, cogs: 0, count: 0 };
            }
            const returnValue = parseFloat(t.grand_total || t.amount || 0);
            const returnedCost = parseFloat(t.returned_cost) || 0;
            groups[name].revenue -= returnValue;
            groups[name].profit -= returnValue - returnedCost;
            groups[name].cogs -= returnedCost;
        });
        return Object.entries(groups).map(([name, data]) => ({ name, ...data }));
    }, [filteredTransactions, filteredReturns, saleCostsById]);

    const monthlyData = useMemo(() => {
        const groups = {};
        filteredTransactions.forEach(t => {
            if (!t.transaction_date) return;
            const date = new Date(t.transaction_date);
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!groups[key]) {
                groups[key] = { revenue: 0, profit: 0, cogs: 0, count: 0 };
            }
            const amount = parseFloat(t.grand_total || t.amount || 0);
            const cogs = saleCostsById[String(t.sale_id)] ??
                (parseFloat(t.cost_price_total) || parseFloat(t.total_cogs) ||
                    (filteredTransactions.length === 1 ? saleCostsById.__single : 0) || 0);
            const profit = amount - cogs;
            groups[key].revenue += amount;
            groups[key].profit += profit;
            groups[key].cogs += cogs;
            groups[key].count += 1;
        });
        filteredReturns.forEach(t => {
            if (!t.transaction_date) return;
            const date = new Date(t.transaction_date);
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!groups[key]) {
                groups[key] = { revenue: 0, profit: 0, cogs: 0, count: 0 };
            }
            const returnValue = parseFloat(t.grand_total || t.amount || 0);
            const returnedCost = parseFloat(t.returned_cost) || 0;
            groups[key].revenue -= returnValue;
            groups[key].profit -= returnValue - returnedCost;
            groups[key].cogs -= returnedCost;
        });
        return Object.entries(groups).map(([key, data]) => ({
            month: key,
            ...data
        })).sort((a, b) => a.month.localeCompare(b.month));
    }, [filteredTransactions, filteredReturns, saleCostsById]);

    if (!transactions || transactions.length === 0) {
        return (
            <div className="oztransui-no-data">
                <FontAwesomeIcon icon={faCashRegister} />
                <p>{t('noSalesData') || 'No sales data available'}</p>
            </div>
        );
    }

    return (
        <div className="oztransui-sales-pl">
            <div className="oztransui-sales-pl-header">
                <h4><FontAwesomeIcon icon={faCashRegister} /> {t('salesProfitAnalysis') || 'Sales Profit Analysis'}</h4>
                <div className="oztransui-sales-pl-controls">
                    <div className="oztransui-view-toggle">
                        <button className={`oztransui-view-btn ${viewMode === 'summary' ? 'active' : ''}`} onClick={() => setViewMode('summary')}>
                            <FontAwesomeIcon icon={faThLarge} /> {t('summary') || 'Summary'}
                        </button>
                        <button className={`oztransui-view-btn ${viewMode === 'detailed' ? 'active' : ''}`} onClick={() => setViewMode('detailed')}>
                            <FontAwesomeIcon icon={faChartPie} /> {t('detailed') || 'Detailed'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="oztransui-sales-pl-filters">
                <div className="oztransui-sales-pl-filter-row">
                    <div className="oztransui-filter-group">
                        <label>{t('filterBy') || 'Filter By'}:</label>
                        <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setFilterValue(''); }}>
                            <option value="all">{t('allSales') || 'All Sales'}</option>
                            <option value="customer">{t('customer') || 'Customer'}</option>
                        </select>
                    </div>
                    {filterType === 'customer' && (
                        <div className="oztransui-filter-group">
                            <label>{t('selectCustomer') || 'Select Customer'}:</label>
                            <select value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                                <option value="">{t('all') || 'All'}</option>
                                {references.map(ref => (
                                    <option key={ref} value={ref}>{ref}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="oztransui-filter-group">
                        <label>{t('startDate') || 'Start Date'}:</label>
                        <input type="date" value={dateRange.start} onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))} />
                    </div>
                    <div className="oztransui-filter-group">
                        <label>{t('endDate') || 'End Date'}:</label>
                        <input type="date" value={dateRange.end} onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))} />
                    </div>
                </div>
            </div>

            <div className="oztransui-sales-pl-summary">
                <div className="oztransui-sales-pl-stats">
                    <div className="oztransui-pl-card oztransui-pl-tone-info">
                        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faCashRegister} /></div>
                        <div className="oztransui-pl-card-content">
                            <span className="oztransui-pl-card-label">{t('totalRevenue') || 'Total Revenue'}</span>
                            <span className="oztransui-pl-card-value">Rs. {formatCurrencyFn(salesData.totalRevenue)}</span>
                        </div>
                    </div>
                    <div className="oztransui-pl-card oztransui-pl-tone-danger">
                        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faShoppingCart} /></div>
                        <div className="oztransui-pl-card-content">
                            <span className="oztransui-pl-card-label">{t('cogs') || 'COGS'}</span>
                            <span className="oztransui-pl-card-value">Rs. {formatCurrencyFn(salesData.totalCOGS)}</span>
                        </div>
                    </div>
                    <div className="oztransui-pl-card oztransui-pl-tone-success">
                        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faHandHoldingUsd} /></div>
                        <div className="oztransui-pl-card-content">
                            <span className="oztransui-pl-card-label">{t('totalProfit') || 'Total Profit'}</span>
                            <span className="oztransui-pl-card-value">Rs. {formatCurrencyFn(salesData.totalProfit)}</span>
                            <span className="oztransui-pl-card-sub">{t('profitMargin') || 'Profit Margin'}: {salesData.profitMargin.toFixed(2)}%</span>
                        </div>
                    </div>
                    <div className="oztransui-pl-card oztransui-pl-tone-default">
                        <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faReceipt} /></div>
                        <div className="oztransui-pl-card-content">
                            <span className="oztransui-pl-card-label">{t('totalSales') || 'Total Sales'}</span>
                            <span className="oztransui-pl-card-value">{salesData.totalCount}</span>
                        </div>
                    </div>
                </div>
            </div>

            {viewMode === 'detailed' && (
                <div className="oztransui-sales-pl-detailed">
                    {customerData.length > 0 && (
                        <div className="oztransui-pl-panel">
                            <h5><FontAwesomeIcon icon={faUsers} /> {t('customerBreakdown') || 'Customer Breakdown'}</h5>
                            <div className="oztransui-sales-pl-breakdown">
                                {customerData.map(customer => (
                                    <div key={customer.name} className="oztransui-breakdown-item">
                                        <div className="oztransui-breakdown-header">
                                            <span className="oztransui-breakdown-name">{customer.name}</span>
                                            <span className="oztransui-breakdown-count">{customer.count} {t('transactions') || 'transactions'}</span>
                                        </div>
                                        <div className="oztransui-breakdown-stats">
                                            <span>Revenue: Rs. {formatCurrencyFn(customer.revenue)}</span>
                                            <span>COGS: Rs. {formatCurrencyFn(customer.cogs)}</span>
                                            <span className="oztransui-breakdown-profit">Profit: Rs. {formatCurrencyFn(customer.profit)}</span>
                                            <span className="oztransui-breakdown-margin">Margin: {customer.revenue > 0 ? ((customer.profit / customer.revenue) * 100).toFixed(2) : 0}%</span>
                                        </div>
                                        <div className="oztransui-breakdown-bar">
                                            <div className="oztransui-breakdown-bar-fill" style={{ width: `${customer.revenue > 0 ? (customer.profit / customer.revenue) * 100 : 0}%` }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {monthlyData.length > 0 && (
                        <div className="oztransui-pl-panel">
                            <h5><FontAwesomeIcon icon={faCalendarAlt} /> {t('monthlyBreakdown') || 'Monthly Breakdown'}</h5>
                            <div className="oztransui-sales-pl-breakdown">
                                {monthlyData.map(month => (
                                    <div key={month.month} className="oztransui-breakdown-item">
                                        <div className="oztransui-breakdown-header">
                                            <span className="oztransui-breakdown-name">{month.month}</span>
                                            <span className="oztransui-breakdown-count">{month.count} {t('transactions') || 'transactions'}</span>
                                        </div>
                                        <div className="oztransui-breakdown-stats">
                                            <span>Revenue: Rs. {formatCurrencyFn(month.revenue)}</span>
                                            <span>COGS: Rs. {formatCurrencyFn(month.cogs)}</span>
                                            <span className="oztransui-breakdown-profit">Profit: Rs. {formatCurrencyFn(month.profit)}</span>
                                            <span className="oztransui-breakdown-margin">Margin: {month.revenue > 0 ? ((month.profit / month.revenue) * 100).toFixed(2) : 0}%</span>
                                        </div>
                                        <div className="oztransui-breakdown-bar">
                                            <div className="oztransui-breakdown-bar-fill" style={{ width: `${month.revenue > 0 ? (month.profit / month.revenue) * 100 : 0}%` }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ==============================================
// PARTNER PROFIT/LOSS
// ==============================================
const PartnerProfitLoss = ({ transactions, products, partners, formatCurrencyFn, t, shopId }) => {
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [partnerFilter, setPartnerFilter] = useState('all');
    const [viewMode, setViewMode] = useState('summary');

    const partnerList = useMemo(() => partners || [], [partners]);

    const calculatePartnerPL = useCallback(() => {
        if (!transactions || transactions.length === 0) return [];

        const shopTransactions = transactions.filter(transaction =>
            parseInt(transaction.shop_id) === parseInt(shopId)
        );
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const toLocalDate = (value) => {
            if (!value) return null;
            const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
            if (!year || !month || !day) return null;
            const date = new Date(year, month - 1, day);
            return Number.isNaN(date.getTime()) ? null : date;
        };
        const transactionAmount = transaction =>
            parseFloat(transaction.grand_total || transaction.amount || 0) || 0;

        let shopTotalStockProfit = 0;
        (products || []).forEach(product => {
            const quantity = parseFloat(product.quantity) || 0;
            const costPrice = parseFloat(product.cost_price) || 0;
            const sellPrice = parseFloat(product.sell_price) || 0;
            if (quantity > 0 && costPrice > 0 && sellPrice > 0) {
                shopTotalStockProfit += quantity * (sellPrice - costPrice);
            }
        });

        return partnerList.reduce((results, partner) => {
            if (partner.status !== 'active') return results;

            const partnerId = partner.partner_id;
            const partnershipType = partner.partnership_type || 'ALL';
            const profitPercentage = parseFloat(partner.profit_percentage) || 0;
            const partnerSharePercentage = profitPercentage / 100;
            const expensesSalariesIncluded = partner.expenses_salaries_included == 1;

            const selectedProductIds = partner.selected_products
                ? partner.selected_products.split(',').map(id => parseInt(id.trim())).filter(Boolean)
                : [];
            const contractStartDate = toLocalDate(partner.contract_start_date);
            const totalContractDays = contractStartDate && contractStartDate <= today
                ? Math.floor((today - contractStartDate) / 86400000) + 1
                : 0;

            const partnerPeriodTransactions = contractStartDate
                ? shopTransactions.filter(transaction => {
                    const date = toLocalDate(transaction.transaction_date);
                    return date && date >= contractStartDate && date <= today && transaction.status !== 'cancelled';
                })
                : [];

            const totalExpenses = partnerPeriodTransactions
                .filter(transaction => transaction.transaction_type === 'expense')
                .reduce((sum, transaction) => sum + transactionAmount(transaction), 0);
            const totalSalaries = partnerPeriodTransactions
                .filter(transaction =>
                    transaction.transaction_type === 'salary_payment' ||
                    transaction.transaction_type === 'employee_salary'
                )
                .reduce((sum, transaction) => sum + transactionAmount(transaction), 0);

            const expenseShare = totalExpenses * partnerSharePercentage;
            const salaryShare = totalSalaries * partnerSharePercentage;
            const totalCostShare = expenseShare + salaryShare;

            const partnerLedger = shopTransactions.filter(transaction =>
                (transaction.partner_id && parseInt(transaction.partner_id) === parseInt(partnerId)) ||
                (transaction.reference_type === 'partner' && parseInt(transaction.reference_id) === parseInt(partnerId))
            );
            let totalInvestment = 0;
            let totalPayments = 0;
            partnerLedger.forEach(transaction => {
                const amount = transactionAmount(transaction);
                if (transaction.transaction_type === 'partner_investment') totalInvestment += amount;
                if (transaction.transaction_type === 'partner_payment') totalPayments += amount;
            });
            const investmentBalance = totalInvestment - totalPayments;

            let partnerStockProfitShare = 0;
            const productBreakdown = [];
            if (partnershipType === 'ALL') {
                partnerStockProfitShare = shopTotalStockProfit * partnerSharePercentage;
            } else if (partnershipType === 'Product Based' && selectedProductIds.length) {
                const selectedProductSet = new Set(selectedProductIds);
                let selectedStockProfit = 0;
                (products || []).forEach(product => {
                    const productId = parseInt(product.id || product.product_id);
                    if (!selectedProductSet.has(productId)) return;
                    const quantity = parseFloat(product.quantity) || 0;
                    const costPrice = parseFloat(product.cost_price) || 0;
                    const sellPrice = parseFloat(product.sell_price) || 0;
                    if (quantity > 0 && costPrice > 0 && sellPrice > 0) {
                        const stockProfit = quantity * (sellPrice - costPrice);
                        selectedStockProfit += stockProfit;
                        productBreakdown.push({
                            product_name: product.product_name || product.name,
                            quantity,
                            stock_profit: stockProfit,
                            share: stockProfit * partnerSharePercentage
                        });
                    }
                });
                partnerStockProfitShare = selectedStockProfit * partnerSharePercentage;
            }

            let finalPayable = partnerStockProfitShare + investmentBalance;
            if (expensesSalariesIncluded) {
                finalPayable = finalPayable - totalCostShare;
            }

            results.push({
                partner_id: partnerId,
                partner_name: partner.partner_name,
                partnership_type: partnershipType,
                profit_percentage: profitPercentage,
                total_investment: totalInvestment,
                total_payments: totalPayments,
                balance: investmentBalance,
                partner_share: partnerStockProfitShare,
                total_contract_days: totalContractDays,
                total_expenses: totalExpenses,
                total_salaries: totalSalaries,
                expense_share: expenseShare,
                salary_share: salaryShare,
                total_cost_share: totalCostShare,
                expenses_salaries_included: expensesSalariesIncluded,
                payable: finalPayable,
                product_details: productBreakdown,
                selected_products: selectedProductIds
            });
            return results;
        }, []);
    }, [transactions, products, partnerList, shopId]);

    const partnerPLResults = useMemo(() => calculatePartnerPL(), [calculatePartnerPL]);
    const filteredResults = useMemo(() => {
        let results = partnerPLResults;
        if (selectedPartner) results = results.filter(result => result.partner_id === selectedPartner);
        if (partnerFilter === 'product_based') results = results.filter(result => result.partnership_type === 'Product Based');
        if (partnerFilter === 'all_partners') results = results.filter(result => result.partnership_type === 'ALL');
        return results;
    }, [partnerPLResults, selectedPartner, partnerFilter]);

    const totals = useMemo(() => {
        let totalStockProfit = 0;
        let totalExpenses = 0;
        let totalSalaries = 0;
        let totalCostShare = 0;
        let totalPayable = 0;
        let totalInvestment = 0;
        let totalBalance = 0;

        filteredResults.forEach(r => {
            totalStockProfit += r.partner_share;
            totalExpenses += r.total_expenses;
            totalSalaries += r.total_salaries;
            totalCostShare += r.total_cost_share;
            totalPayable += r.payable;
            totalInvestment += r.total_investment;
            totalBalance += r.balance;
        });

        return {
            totalStockProfit,
            totalExpenses,
            totalSalaries,
            totalCostShare,
            totalPayable,
            totalInvestment,
            totalBalance,
            count: filteredResults.length
        };
    }, [filteredResults]);

    return (
        <div className="oztransui-partner-pl">
            <div className="oztransui-partner-pl-header">
                <h4><FontAwesomeIcon icon={faHandshake} /> {t('partnerProfitLoss')}</h4>
                <div className="oztransui-view-toggle">
                    <button className={`oztransui-view-btn ${viewMode === 'summary' ? 'active' : ''}`} onClick={() => setViewMode('summary')}>Summary</button>
                    <button className={`oztransui-view-btn ${viewMode === 'detailed' ? 'active' : ''}`} onClick={() => setViewMode('detailed')}>Detailed</button>
                </div>
            </div>

            <div className="oztransui-partner-pl-stats">
                <div className="oztransui-pl-card oztransui-pl-tone-info">
                    <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faUsers} /></div>
                    <div className="oztransui-pl-card-content">
                        <span className="oztransui-pl-card-label">{t('totalPartners') || 'Total Partners'}</span>
                        <span className="oztransui-pl-card-value">{totals.count}</span>
                    </div>
                </div>
                <div className="oztransui-pl-card oztransui-pl-tone-success">
                    <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faHandHoldingUsd} /></div>
                    <div className="oztransui-pl-card-content">
                        <span className="oztransui-pl-card-label">{t('totalStockProfit') || 'Total Stock Profit Share'}</span>
                        <span className="oztransui-pl-card-value">Rs. {formatCurrencyFn(totals.totalStockProfit)}</span>
                    </div>
                </div>
                <div className="oztransui-pl-card oztransui-pl-tone-warning">
                    <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faMoneyCheckDollar} /></div>
                    <div className="oztransui-pl-card-content">
                        <span className="oztransui-pl-card-label">{t('totalCostShare') || 'Total Cost Share'}</span>
                        <span className="oztransui-pl-card-value">Rs. {formatCurrencyFn(totals.totalCostShare)}</span>
                    </div>
                </div>
                <div className="oztransui-pl-card oztransui-pl-tone-primary">
                    <div className="oztransui-pl-card-icon"><FontAwesomeIcon icon={faWallet} /></div>
                    <div className="oztransui-pl-card-content">
                        <span className="oztransui-pl-card-label">{t('totalPayable') || 'Total Payable'}</span>
                        <span className="oztransui-pl-card-value" style={{ color: totals.totalPayable >= 0 ? '#d97706' : '#3b82f6' }}>
                            Rs. {formatCurrencyFn(Math.abs(totals.totalPayable))}
                            <span className="oztransui-pl-card-sub">{totals.totalPayable >= 0 ? '(Receivable)' : '(Payable)'}</span>
                        </span>
                    </div>
                </div>
            </div>

            <div className="oztransui-partner-pl-filters">
                <div className="oztransui-partner-pl-filter-row">
                    <div className="oztransui-filter-group">
                        <label>{t('partnerType') || 'Partner Type'}:</label>
                        <select value={partnerFilter} onChange={(e) => { setPartnerFilter(e.target.value); setSelectedPartner(null); }}>
                            <option value="all">{t('allPartners') || 'All Partners'}</option>
                            <option value="all_partners">{t('overallPartners') || 'Overall Partners'}</option>
                            <option value="product_based">{t('productBased') || 'Product Based'}</option>
                        </select>
                    </div>
                    <div className="oztransui-filter-group">
                        <label>{t('selectPartner') || 'Select Partner'}:</label>
                        <select value={selectedPartner || ''} onChange={(e) => setSelectedPartner(e.target.value || null)}>
                            <option value="">{t('all') || 'All'}</option>
                            {partnerList.filter(p => p.status === 'active').map(p => (
                                <option key={p.partner_id} value={p.partner_id}>
                                    {p.partner_name} ({p.partnership_type})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {viewMode === 'detailed' ? (
                <div className="oztransui-partner-pl-detailed">
                    {filteredResults.map(partner => (
                        <div key={partner.partner_id} className="oztransui-pl-panel">
                            <div className="oztransui-partner-pl-item">
                                <div className="oztransui-partner-pl-item-header">
                                    <div className="oztransui-partner-pl-item-info">
                                        <span className="oztransui-partner-pl-item-name">
                                            <FontAwesomeIcon icon={faHandshake} /> {partner.partner_name}
                                        </span>
                                        <span className="oztransui-partner-pl-item-type">
                                            <span className={`oztransui-type-badge ${partner.partnership_type === 'Product Based' ? 'type-partner-investment' : 'type-sale'}`}>
                                                {partner.partnership_type === 'Product Based' ? 'Product Based' : 'Overall'}
                                            </span>
                                            <span className="oztransui-partner-pl-percentage">{partner.profit_percentage}% Share</span>
                                        </span>
                                    </div>
                                    <div className={`oztransui-partner-pl-item-status ${partner.payable >= 0 ? 'oztransui-profit' : 'oztransui-loss'}`}>
                                        <FontAwesomeIcon icon={partner.payable >= 0 ? faArrowUp : faArrowDown} />
                                        Rs. {formatCurrencyFn(Math.abs(partner.payable))}
                                        <span className="oztransui-payable-label">
                                            {partner.payable >= 0 ? '(Receivable)' : '(Payable)'}
                                        </span>
                                    </div>
                                </div>

                                <div className="oztransui-partner-pl-item-details">
                                    <div className="oztransui-partner-pl-detail-row">
                                        <span>{t('contractDays') || 'Contract days'}:</span>
                                        <span>{partner.total_contract_days || 'Not set'}</span>
                                    </div>
                                    <div className="oztransui-partner-pl-detail-row">
                                        <span>{t('stockProfitShare') || 'Stock Profit Share'}:</span>
                                        <span className="oztransui-profit">Rs. {formatCurrencyFn(partner.partner_share)}</span>
                                    </div>

                                    <div className="oztransui-partner-pl-detail-row oztransui-costs-section">
                                        <span className="oztransui-costs-label">
                                            <FontAwesomeIcon icon={faCheck} /> {t('costsIncluded') || 'Costs Included'}:
                                        </span>
                                        <span className={`oztransui-costs-badge ${partner.expenses_salaries_included ? 'oztransui-costs-yes' : 'oztransui-costs-no'}`}>
                                            {partner.expenses_salaries_included ? (
                                                <>
                                                    <FontAwesomeIcon icon={faCheckCircle} className="oztransui-costs-icon" />
                                                    {t('costsIncludedYes') || 'Yes'}
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faTimes} className="oztransui-costs-icon" />
                                                    {t('costsIncludedNo') || 'No'}
                                                    <span className="oztransui-costs-not-included">({t('notIncluded') || 'Not included'})</span>
                                                </>
                                            )}
                                        </span>
                                    </div>

                                    {partner.expenses_salaries_included ? (
                                        <>
                                            <div className="oztransui-partner-pl-detail-row">
                                                <span>{t('totalExpenses') || 'Total Expenses'} / {t('expenseShare') || 'Your Share'}:</span>
                                                <span>Rs. {formatCurrencyFn(partner.total_expenses)} / Rs. {formatCurrencyFn(partner.expense_share)}</span>
                                            </div>
                                            <div className="oztransui-partner-pl-detail-row">
                                                <span>{t('totalSalaries') || 'Total Salaries'} / {t('salaryShare') || 'Your Share'}:</span>
                                                <span>Rs. {formatCurrencyFn(partner.total_salaries)} / Rs. {formatCurrencyFn(partner.salary_share)}</span>
                                            </div>
                                            <div className="oztransui-partner-pl-detail-row">
                                                <span>{t('totalCostShare') || 'Total Cost Share'}:</span>
                                                <span className="oztransui-cost-share">Rs. {formatCurrencyFn(partner.total_cost_share)}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="oztransui-partner-pl-detail-row oztransui-costs-excluded">
                                            <span>{t('expensesIncluded') || 'Expenses & Salaries'}:</span>
                                            <span className="oztransui-costs-excluded-text">
                                                <FontAwesomeIcon icon={faTimes} className="oztransui-costs-excluded-icon" />
                                                {t('notIncluded') || 'Not included'} - No deduction applied
                                            </span>
                                        </div>
                                    )}

                                    <div className="oztransui-partner-pl-detail-row">
                                        <span>{t('investmentBalance') || 'Investment Balance'}:</span>
                                        <span className={partner.balance >= 0 ? 'oztransui-positive' : 'oztransui-negative'}>
                                            Rs. {formatCurrencyFn(partner.balance)}
                                        </span>
                                    </div>
                                    <div className="oztransui-partner-pl-detail-row oztransui-highlight">
                                        <strong>{t('netPayableAfterCosts') || 'Net Payable After Costs'}:</strong>
                                        <strong className={partner.payable >= 0 ? 'oztransui-positive' : 'oztransui-negative'}>
                                            Rs. {formatCurrencyFn(partner.payable)}
                                        </strong>
                                    </div>
                                </div>

                                {partner.partnership_type === 'Product Based' && partner.product_details && partner.product_details.length > 0 && (
                                    <div className="oztransui-partner-pl-products">
                                        <span className="oztransui-partner-pl-products-label">
                                            <FontAwesomeIcon icon={faBoxes} /> {t('selectedProducts') || 'Selected Products'}:
                                        </span>
                                        <div className="oztransui-partner-pl-products-list">
                                            {partner.product_details.map((p, idx) => (
                                                <span key={idx} className="oztransui-partner-pl-product-tag">
                                                    {p.product_name || p.name || `Product ${p.id}`}
                                                    <span className="oztransui-product-profit">+Rs. {formatCurrencyFn(p.share || 0)}</span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="oztransui-partner-pl-table-wrapper">
                    <table className="oztransui-partner-pl-table">
                        <thead>
                            <tr>
                                <th>{t('partner') || 'Partner'}</th>
                                <th>{t('share') || 'Share %'}</th>
                                <th>{t('contractDays') || 'Contract Days'}</th>
                                <th>{t('stockProfitShare') || 'Stock Profit Share'}</th>
                                <th>{t('costsIncluded') || 'Costs Included'}</th>
                                <th>{t('totalExpenses') || 'Total Expenses'}</th>
                                <th>{t('totalSalaries') || 'Total Salaries'}</th>
                                <th>{t('invBalance') || 'Inv. Balance'}</th>
                                <th>{t('netPayableAfterCosts') || 'Net Payable'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredResults.map(partner => (
                                <tr key={partner.partner_id}>
                                    <td className="oztransui-partner-pl-name">{partner.partner_name}</td>
                                    <td>{partner.profit_percentage}%</td>
                                    <td>{partner.total_contract_days || 'Not set'}</td>
                                    <td className="oztransui-profit">Rs. {formatCurrencyFn(partner.partner_share)}</td>
                                    <td>
                                        <span className={`oztransui-costs-badge ${partner.expenses_salaries_included ? 'oztransui-costs-yes' : 'oztransui-costs-no'}`}>
                                            {partner.expenses_salaries_included ?
                                                <FontAwesomeIcon icon={faCheckCircle} className="oztransui-costs-icon" /> :
                                                <FontAwesomeIcon icon={faTimes} className="oztransui-costs-icon" />
                                            }
                                            {partner.expenses_salaries_included ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td>
                                        <span>Rs. {formatCurrencyFn(partner.total_expenses)}</span>
                                        {partner.expenses_salaries_included && (
                                            <>
                                                <br />
                                                <small>Share: Rs. {formatCurrencyFn(partner.expense_share)}</small>
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        <span>Rs. {formatCurrencyFn(partner.total_salaries)}</span>
                                        {partner.expenses_salaries_included && (
                                            <>
                                                <br />
                                                <small>Share: Rs. {formatCurrencyFn(partner.salary_share)}</small>
                                            </>
                                        )}
                                    </td>
                                    <td className={partner.balance >= 0 ? 'oztransui-positive' : 'oztransui-negative'}>
                                        Rs. {formatCurrencyFn(partner.balance)}
                                    </td>
                                    <td className="oztransui-highlight">
                                        <strong className={partner.payable >= 0 ? 'oztransui-positive' : 'oztransui-negative'}>
                                            Rs. {formatCurrencyFn(partner.payable)}
                                        </strong>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan="3" style={{ textAlign: 'right', fontWeight: '700' }}>TOTAL</td>
                                <td className="oztransui-profit">Rs. {formatCurrencyFn(totals.totalStockProfit)}</td>
                                <td></td>
                                <td>Rs. {formatCurrencyFn(totals.totalExpenses)}</td>
                                <td>Rs. {formatCurrencyFn(totals.totalSalaries)}</td>
                                <td>Rs. {formatCurrencyFn(totals.totalBalance)}</td>
                                <td className="oztransui-highlight">
                                    <strong className={totals.totalPayable >= 0 ? 'oztransui-positive' : 'oztransui-negative'}>
                                        Rs. {formatCurrencyFn(Math.abs(totals.totalPayable))}
                                        <span className="oztransui-payable-label">
                                            {totals.totalPayable >= 0 ? '(Rec)' : '(Pay)'}
                                        </span>
                                    </strong>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
};

// ==============================================
// PARTNER SALES BY PRODUCT COMPONENT
// ==============================================
const PartnerSalesByProduct = ({ transactions, partners, products, formatCurrencyFn, t, shopId }) => {
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [viewMode, setViewMode] = useState('summary');
    const [loading, setLoading] = useState(false);
    const [saleItemsData, setSaleItemsData] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('all');
    const [selectedPartner, setSelectedPartner] = useState('all');
    const [partnerTypeFilter, setPartnerTypeFilter] = useState('all');

    const partnerList = useMemo(() => partners || [], [partners]);

    const fetchSaleItems = useCallback(async () => {
        if (!shopId) return;

        setLoading(true);
        try {
            const listResponse = await fetch(SALES_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_records', shop_id: shopId })
            });

            const listResult = await listResponse.json();

            if (listResult.status !== 'success') {
                throw new Error(listResult.message || 'Could not load sales');
            }

            const saleIds = [...new Set(
                (listResult.data || [])
                    .filter(sale =>
                        sale.status !== 'cancelled' &&
                        sale.payment_status !== 'cancelled' &&
                        (!dateRange.start || sale.date >= dateRange.start) &&
                        (!dateRange.end || sale.date <= dateRange.end)
                    )
                    .map(sale => sale.id)
                    .filter(Boolean)
            )];

            const sales = await Promise.all(
                saleIds.map(async saleId => {
                    const response = await fetch(SALES_API, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'get_record',
                            id: saleId,
                            shop_id: shopId
                        })
                    });

                    const result = await response.json();
                    return result.status === 'success' ? result.data : null;
                })
            );

            setSaleItemsData(
                sales.flatMap(sale =>
                    sale
                        ? (sale.items || []).map(item => ({
                            ...item,
                            sell_id: sale.id,
                            sell_date: sale.date
                        }))
                        : []
                )
            );
        } catch (error) {
            console.error('Error fetching sale items:', error);
            setSaleItemsData([]);
        } finally {
            setLoading(false);
        }
    }, [shopId, dateRange]);

    useEffect(() => {
        fetchSaleItems();
    }, [fetchSaleItems]);

    const calculatePartnerSalesByProduct = useCallback(() => {
        if (!saleItemsData || saleItemsData.length === 0 || !partnerList.length) {
            return {
                productBasedData: [],
                overallData: []
            };
        }

        const productSalesMap = {};

        saleItemsData.forEach(item => {
            const productId = item.product_id;
            if (!productId) return;

            if (!productSalesMap[productId]) {
                const product = (products || []).find(
                    p => (p.id || p.product_id) == productId
                );

                productSalesMap[productId] = {
                    product_id: productId,
                    product_name:
                        product?.product_name ||
                        product?.name ||
                        item.product_name ||
                        `Product ${productId}`,
                    total_revenue: 0,
                    total_profit: 0,
                    quantity_sold: 0,
                    partner_sales: {},
                    sell_id: item.sell_id,
                    sell_date: item.created_at || item.sell_date
                };
            }

            const amount =
                parseFloat(item.total) ||
                ((parseFloat(item.price) || 0) *
                    (parseFloat(item.display_quantity ?? item.quantity) || 0));

            const profit = parseFloat(item.profit) || 0;
            const qty =
                parseFloat(item.display_quantity ?? item.quantity) || 0;

            productSalesMap[productId].total_revenue += amount;
            productSalesMap[productId].total_profit += profit;
            productSalesMap[productId].quantity_sold += qty;
        });

        const isProductBasedPartner = partner => {
            const type = String(partner.partnership_type || '')
                .trim()
                .toLowerCase()
                .replace(/[_-]/g, ' ');

            return type === 'product based' || type === 'productbased';
        };

        const reservedProductIds = new Set(
            partnerList
                .filter(
                    partner =>
                        partner.status === 'active' &&
                        isProductBasedPartner(partner)
                )
                .flatMap(partner =>
                    String(partner.selected_products || '')
                        .split(',')
                        .map(id => parseInt(id.trim(), 10))
                        .filter(Number.isFinite)
                )
        );

        partnerList.forEach(partner => {
            if (partner.status !== 'active') return;

            const isProductBased = isProductBasedPartner(partner);
            const partnershipType = isProductBased
                ? 'Product Based'
                : 'ALL';

            const profitPercentage =
                parseFloat(partner.profit_percentage) || 0;

            const partnerSharePercentage =
                profitPercentage / 100;

            const selectedProductIds = partner.selected_products
                ? partner.selected_products
                    .split(',')
                    .map(id => parseInt(id.trim(), 10))
                    .filter(Boolean)
                : [];

            Object.keys(productSalesMap).forEach(productId => {
                const product = productSalesMap[productId];
                const productIdNum = parseInt(productId, 10);

                let sharePercentage = 0;
                let isApplicable = false;

                if (
                    !isProductBased &&
                    !reservedProductIds.has(productIdNum)
                ) {
                    sharePercentage = partnerSharePercentage;
                    isApplicable = true;
                }
                else if (
                    isProductBased &&
                    selectedProductIds.includes(productIdNum)
                ) {
                    sharePercentage = partnerSharePercentage;
                    isApplicable = true;
                }

                if (isApplicable && sharePercentage > 0) {
                    const partnerRevenue =
                        product.total_revenue * sharePercentage;

                    const partnerProfit =
                        product.total_profit * sharePercentage;

                    product.partner_sales[partner.partner_id] = {
                        partner_id: partner.partner_id,
                        partner_name: partner.partner_name,
                        partnership_type: partnershipType,
                        share_percentage:
                            sharePercentage * 100,
                        revenue: partnerRevenue,
                        profit: partnerProfit,
                        is_overall: !isProductBased
                    };
                }
            });
        });

        const productBasedData = [];
        const overallData = [];

        Object.values(productSalesMap).forEach(product => {
            const overallPartners = Object.values(
                product.partner_sales
            ).filter(p => p.is_overall);

            const productBasedPartners = Object.values(
                product.partner_sales
            ).filter(p => !p.is_overall);

            if (productBasedPartners.length > 0) {
                productBasedData.push({
                    ...product,
                    partners: productBasedPartners,
                    hasPartners: true
                });
            }

            if (overallPartners.length > 0) {
                overallData.push({
                    ...product,
                    partners: overallPartners,
                    hasPartners: true
                });
            }
        });

        return {
            productBasedData,
            overallData
        };
    }, [saleItemsData, partnerList, products]);

    const {
        productBasedData,
        overallData
    } = useMemo(() => {
        return calculatePartnerSalesByProduct();
    }, [calculatePartnerSalesByProduct]);

    const availableProducts = useMemo(() => {
        const map = new Map();

        [...productBasedData, ...overallData].forEach(product => {
            const key = String(product.product_id);

            if (!map.has(key)) {
                map.set(key, {
                    value: key,
                    label: product.product_name
                });
            }
        });

        return Array.from(map.values()).sort((a, b) =>
            a.label.localeCompare(b.label)
        );
    }, [productBasedData, overallData]);

    const availablePartners = useMemo(() => {
        const map = new Map();

        [...productBasedData, ...overallData].forEach(product => {
            (product.partners || []).forEach(partner => {
                const key = String(partner.partner_id);

                if (!map.has(key)) {
                    map.set(key, {
                        value: key,
                        label: partner.partner_name,
                        type:
                            partner.partnership_type === 'Product Based'
                                ? 'product_based'
                                : 'overall'
                    });
                }
            });
        });

        return Array.from(map.values()).sort((a, b) =>
            a.label.localeCompare(b.label)
        );
    }, [productBasedData, overallData]);

    const filterPartnerSalesData = useCallback(
        (data, tableType) => {
            let result = Array.isArray(data)
                ? data.map(product => ({
                    ...product,
                    partners: [...(product.partners || [])]
                }))
                : [];

            if (
                partnerTypeFilter !== 'all' &&
                partnerTypeFilter !== tableType
            ) {
                return [];
            }

            if (selectedProduct !== 'all') {
                result = result.filter(
                    product =>
                        String(product.product_id) ===
                        String(selectedProduct)
                );
            }

            if (selectedPartner !== 'all') {
                result = result
                    .map(product => ({
                        ...product,
                        partners: product.partners.filter(
                            partner =>
                                String(partner.partner_id) ===
                                String(selectedPartner)
                        )
                    }))
                    .filter(product => product.partners.length > 0);
            }

            const term = searchTerm.trim().toLowerCase();

            if (term) {
                result = result
                    .map(product => {
                        const productMatch = String(
                            product.product_name || ''
                        )
                            .toLowerCase()
                            .includes(term);

                        const matchingPartners =
                            product.partners.filter(partner =>
                                String(partner.partner_name || '')
                                    .toLowerCase()
                                    .includes(term)
                            );

                        if (productMatch) {
                            return product;
                        }

                        if (matchingPartners.length > 0) {
                            return {
                                ...product,
                                partners: matchingPartners
                            };
                        }

                        return null;
                    })
                    .filter(Boolean);
            }

            return result;
        },
        [
            searchTerm,
            selectedProduct,
            selectedPartner,
            partnerTypeFilter
        ]
    );

    const filteredProductBasedData = useMemo(
        () =>
            filterPartnerSalesData(
                productBasedData,
                'product_based'
            ),
        [productBasedData, filterPartnerSalesData]
    );

    const filteredOverallData = useMemo(
        () =>
            filterPartnerSalesData(
                overallData,
                'overall'
            ),
        [overallData, filterPartnerSalesData]
    );

    const hasActiveFilters =
        searchTerm.trim() !== '' ||
        selectedProduct !== 'all' ||
        selectedPartner !== 'all' ||
        partnerTypeFilter !== 'all' ||
        dateRange.start !== '' ||
        dateRange.end !== '';

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedProduct('all');
        setSelectedPartner('all');
        setPartnerTypeFilter('all');
        setDateRange({
            start: '',
            end: ''
        });
    };

    const getAllPartners = data => {
        const partnerMap = {};

        data.forEach(product => {
            (product.partners || []).forEach(partner => {
                if (!partnerMap[partner.partner_id]) {
                    partnerMap[partner.partner_id] = {
                        partner_id: partner.partner_id,
                        partner_name: partner.partner_name,
                        partnership_type:
                            partner.partnership_type
                    };
                }
            });
        });

        return Object.values(partnerMap);
    };

    const renderPartnerSalesTable = (
        data,
        title,
        type
    ) => {
        if (loading) {
            return (
                <div className="oztransui-loading-state">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                    />
                    <span>
                        {t('loading') || 'Loading...'}
                    </span>
                </div>
            );
        }

        if (!data || data.length === 0) {
            return (
                <div className="oztransui-partner-sales-product-table-wrapper">
                    <h5 className="oztransui-section-title">
                        <FontAwesomeIcon icon={faColumns} />
                        {' '}
                        {title}
                    </h5>

                    <div className="oztransui-no-data">
                        <FontAwesomeIcon icon={faBoxes} />
                        <p>
                            No matching {title.toLowerCase()} sales found
                        </p>
                    </div>
                </div>
            );
        }

        const allPartners =
            getAllPartners(data);

        const totals = data.reduce(
            (acc, product) => {
                acc.totalRevenue +=
                    parseFloat(product.total_revenue) || 0;

                acc.totalProfit +=
                    parseFloat(product.total_profit) || 0;

                (product.partners || []).forEach(partner => {
                    if (
                        !acc.partnerTotals[
                        partner.partner_id
                        ]
                    ) {
                        acc.partnerTotals[
                            partner.partner_id
                        ] = {
                            revenue: 0,
                            profit: 0
                        };
                    }

                    acc.partnerTotals[
                        partner.partner_id
                    ].revenue +=
                        parseFloat(partner.revenue) || 0;

                    acc.partnerTotals[
                        partner.partner_id
                    ].profit +=
                        parseFloat(partner.profit) || 0;
                });

                return acc;
            },
            {
                totalRevenue: 0,
                totalProfit: 0,
                partnerTotals: {}
            }
        );

        return (
            <div className="oztransui-partner-sales-product-table-wrapper">
                <div
                    className="oztransui-section-title"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px'
                    }}
                >
                    <h5
                        style={{
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faColumns}
                        />
                        {title}
                    </h5>

                    <small>
                        {data.length}{' '}
                        {data.length === 1
                            ? 'product'
                            : 'products'}
                    </small>
                </div>

                <div className="oztransui-table-scroll">
                    <table className="oztransui-partner-sales-product-table">
                        <thead>
                            <tr>
                                <th
                                    rowSpan="2"
                                    className="oztransui-product-col"
                                >
                                    {t('productName') ||
                                        'Product Name'}
                                </th>

                                <th
                                    rowSpan="2"
                                    className="oztransui-amount-col"
                                >
                                    {t('saleRevenue') ||
                                        'Sale Revenue'}
                                </th>

                                <th
                                    rowSpan="2"
                                    className="oztransui-amount-col"
                                >
                                    {t('saleProfit') ||
                                        'Sale Profit'}
                                </th>

                                <th
                                    rowSpan="2"
                                    className="oztransui-qty-col"
                                >
                                    Qty
                                </th>

                                {allPartners.map(partner => (
                                    <th
                                        key={
                                            partner.partner_id
                                        }
                                        colSpan="2"
                                        className="oztransui-partner-col"
                                    >
                                        {
                                            partner.partner_name
                                        }

                                        <small className="oztransui-partner-type">
                                            {partner.partnership_type ===
                                                'Product Based'
                                                ? 'Product Based'
                                                : 'Overall'}
                                        </small>
                                    </th>
                                ))}
                            </tr>

                            <tr>
                                {allPartners.map(partner => (
                                    <React.Fragment
                                        key={
                                            partner.partner_id
                                        }
                                    >
                                        <th>
                                            {t(
                                                'partnerShare'
                                            ) ||
                                                'Share %'}
                                        </th>

                                        <th>
                                            {t(
                                                'partnerAmount'
                                            ) ||
                                                'Amount'}
                                        </th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {data.map(
                                (product, idx) => (
                                    <tr
                                        key={`${type}-${product.product_id}-${idx}`}
                                    >
                                        <td className="oztransui-product-name-cell">
                                            <span className="oztransui-product-name-text">
                                                {
                                                    product.product_name
                                                }
                                            </span>
                                        </td>

                                        <td className="oztransui-amount-cell">
                                            Rs.{' '}
                                            {formatCurrencyFn(
                                                product.total_revenue
                                            )}
                                        </td>

                                        <td className="oztransui-amount-cell oztransui-profit">
                                            Rs.{' '}
                                            {formatCurrencyFn(
                                                product.total_profit
                                            )}
                                        </td>

                                        <td className="oztransui-qty-cell">
                                            {
                                                product.quantity_sold
                                            }
                                        </td>

                                        {allPartners.map(
                                            partner => {
                                                const partnerData =
                                                    product.partners.find(
                                                        p =>
                                                            String(
                                                                p.partner_id
                                                            ) ===
                                                            String(
                                                                partner.partner_id
                                                            )
                                                    );

                                                return partnerData ? (
                                                    <React.Fragment
                                                        key={
                                                            partner.partner_id
                                                        }
                                                    >
                                                        <td className="oztransui-partner-share">
                                                            {Number(
                                                                partnerData.share_percentage ||
                                                                0
                                                            ).toFixed(
                                                                1
                                                            )}
                                                            %
                                                        </td>

                                                        <td className="oztransui-partner-amount">
                                                            Rs.{' '}
                                                            {formatCurrencyFn(
                                                                partnerData.revenue
                                                            )}
                                                        </td>
                                                    </React.Fragment>
                                                ) : (
                                                    <React.Fragment
                                                        key={
                                                            partner.partner_id
                                                        }
                                                    >
                                                        <td className="oztransui-no-data">
                                                            -
                                                        </td>

                                                        <td className="oztransui-no-data">
                                                            -
                                                        </td>
                                                    </React.Fragment>
                                                );
                                            }
                                        )}
                                    </tr>
                                )
                            )}
                        </tbody>

                        <tfoot>
                            <tr>
                                <td className="oztransui-total-label">
                                    <strong>TOTAL</strong>
                                </td>

                                <td className="oztransui-amount-cell">
                                    <strong>
                                        Rs.{' '}
                                        {formatCurrencyFn(
                                            totals.totalRevenue
                                        )}
                                    </strong>
                                </td>

                                <td className="oztransui-amount-cell oztransui-profit">
                                    <strong>
                                        Rs.{' '}
                                        {formatCurrencyFn(
                                            totals.totalProfit
                                        )}
                                    </strong>
                                </td>

                                <td />

                                {allPartners.map(
                                    partner => {
                                        const partnerTotal =
                                            totals.partnerTotals[
                                            partner
                                                .partner_id
                                            ];

                                        return (
                                            <td
                                                key={
                                                    partner.partner_id
                                                }
                                                colSpan="2"
                                                className="oztransui-partner-data-cell oztransui-total-cell"
                                            >
                                                <strong>
                                                    Rs.{' '}
                                                    {formatCurrencyFn(
                                                        partnerTotal?.revenue ||
                                                        0
                                                    )}
                                                </strong>
                                            </td>
                                        );
                                    }
                                )}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
    };

    if (!partnerList || partnerList.length === 0) {
        return (
            <div className="oztransui-no-data">
                <FontAwesomeIcon icon={faHandshake} />
                <p>
                    {t('noPartners') ||
                        'No partners available'}
                </p>
            </div>
        );
    }

    const filteredProductBasedRevenue =
        filteredProductBasedData.reduce(
            (sum, product) =>
                sum +
                (parseFloat(product.total_revenue) || 0),
            0
        );

    const filteredOverallRevenue =
        filteredOverallData.reduce(
            (sum, product) =>
                sum +
                (parseFloat(product.total_revenue) || 0),
            0
        );

    const visiblePartnerIds = new Set();

    [
        ...filteredProductBasedData,
        ...filteredOverallData
    ].forEach(product => {
        (product.partners || []).forEach(partner => {
            visiblePartnerIds.add(
                String(partner.partner_id)
            );
        });
    });

    return (
        <div className="oztransui-partner-sales-product">

            <div className="oztransui-partner-sales-product-header">
                <h4>
                    <FontAwesomeIcon icon={faListAlt} />
                    {' '}
                    {t('partnerSalesByProduct') ||
                        'Partner Sales by Product'}
                </h4>

                <div className="oztransui-partner-sales-product-controls">
                    <button
                        className="oztransui-btn-refresh"
                        onClick={fetchSaleItems}
                        disabled={loading}
                        title="Refresh"
                    >
                        <FontAwesomeIcon
                            icon={faRefresh}
                            spin={loading}
                        />
                    </button>

                    <div className="oztransui-view-toggle">
                        <button
                            className={`oztransui-view-btn ${viewMode === 'summary'
                                ? 'active'
                                : ''
                                }`}
                            onClick={() =>
                                setViewMode('summary')
                            }
                        >
                            <FontAwesomeIcon
                                icon={faThLarge}
                            />
                            {' '}
                            {t('summary') || 'Summary'}
                        </button>

                        <button
                            className={`oztransui-view-btn ${viewMode === 'detailed'
                                ? 'active'
                                : ''
                                }`}
                            onClick={() =>
                                setViewMode('detailed')
                            }
                        >
                            <FontAwesomeIcon
                                icon={faListAlt}
                            />
                            {' '}
                            {t('detailed') ||
                                'Detailed'}
                        </button>
                    </div>
                </div>
            </div>

            <div
                className="oztransui-partner-sales-filters"
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    alignItems: 'flex-end',
                    marginBottom: '16px'
                }}
            >
                <div
                    className="oztransui-filter-group"
                    style={{
                        minWidth: '220px',
                        flex: '1 1 240px'
                    }}
                >
                    <label>
                        Search Product / Partner
                    </label>

                    <div
                        style={{
                            position: 'relative'
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faSearch}
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform:
                                    'translateY(-50%)',
                                opacity: 0.6,
                                pointerEvents: 'none'
                            }}
                        />

                        <input
                            type="text"
                            value={searchTerm}
                            placeholder="Search product or partner..."
                            onChange={e =>
                                setSearchTerm(
                                    e.target.value
                                )
                            }
                            style={{
                                width: '100%',
                                paddingLeft: '36px'
                            }}
                        />

                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() =>
                                    setSearchTerm('')
                                }
                                title="Clear search"
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform:
                                        'translateY(-50%)',
                                    border: 'none',
                                    background:
                                        'transparent',
                                    cursor: 'pointer'
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faTimes}
                                />
                            </button>
                        )}
                    </div>
                </div>

                <div
                    className="oztransui-filter-group"
                    style={{
                        minWidth: '180px'
                    }}
                >
                    <label>Product</label>

                    <select
                        value={selectedProduct}
                        onChange={e =>
                            setSelectedProduct(
                                e.target.value
                            )
                        }
                    >
                        <option value="all">
                            All Products
                        </option>

                        {availableProducts.map(
                            product => (
                                <option
                                    key={product.value}
                                    value={product.value}
                                >
                                    {product.label}
                                </option>
                            )
                        )}
                    </select>
                </div>

                <div
                    className="oztransui-filter-group"
                    style={{
                        minWidth: '180px'
                    }}
                >
                    <label>Partner</label>

                    <select
                        value={selectedPartner}
                        onChange={e => {
                            const value =
                                e.target.value;

                            setSelectedPartner(
                                value
                            );

                            if (
                                value !== 'all'
                            ) {
                                const found =
                                    availablePartners.find(
                                        p =>
                                            p.value ===
                                            value
                                    );

                                if (found) {
                                    setPartnerTypeFilter(
                                        found.type
                                    );
                                }
                            }
                        }}
                    >
                        <option value="all">
                            All Partners
                        </option>

                        {availablePartners
                            .filter(
                                partner =>
                                    partnerTypeFilter ===
                                    'all' ||
                                    partner.type ===
                                    partnerTypeFilter
                            )
                            .map(partner => (
                                <option
                                    key={
                                        partner.value
                                    }
                                    value={
                                        partner.value
                                    }
                                >
                                    {
                                        partner.label
                                    }
                                    {' — '}
                                    {partner.type ===
                                        'product_based'
                                        ? 'Product Based'
                                        : 'Overall'}
                                </option>
                            ))}
                    </select>
                </div>

                <div
                    className="oztransui-filter-group"
                    style={{
                        minWidth: '170px'
                    }}
                >
                    <label>Partner Type</label>

                    <select
                        value={partnerTypeFilter}
                        onChange={e => {
                            const value =
                                e.target.value;

                            setPartnerTypeFilter(
                                value
                            );

                            if (
                                selectedPartner !==
                                'all'
                            ) {
                                const currentPartner =
                                    availablePartners.find(
                                        p =>
                                            p.value ===
                                            selectedPartner
                                    );

                                if (
                                    currentPartner &&
                                    value !==
                                    'all' &&
                                    currentPartner.type !==
                                    value
                                ) {
                                    setSelectedPartner(
                                        'all'
                                    );
                                }
                            }
                        }}
                    >
                        <option value="all">
                            All Types
                        </option>

                        <option value="product_based">
                            Product Based
                        </option>

                        <option value="overall">
                            Overall
                        </option>
                    </select>
                </div>

                <div
                    className="oztransui-filter-group"
                    style={{
                        minWidth: '155px'
                    }}
                >
                    <label>
                        {t('startDate') ||
                            'Start Date'}
                    </label>

                    <input
                        type="date"
                        value={dateRange.start}
                        max={
                            dateRange.end ||
                            undefined
                        }
                        onChange={e =>
                            setDateRange(prev => ({
                                ...prev,
                                start: e.target.value
                            }))
                        }
                    />
                </div>

                <div
                    className="oztransui-filter-group"
                    style={{
                        minWidth: '155px'
                    }}
                >
                    <label>
                        {t('endDate') ||
                            'End Date'}
                    </label>

                    <input
                        type="date"
                        value={dateRange.end}
                        min={
                            dateRange.start ||
                            undefined
                        }
                        onChange={e =>
                            setDateRange(prev => ({
                                ...prev,
                                end: e.target.value
                            }))
                        }
                    />
                </div>

                {hasActiveFilters && (
                    <button
                        type="button"
                        className="oztransui-btn-secondary"
                        onClick={clearFilters}
                        style={{
                            minHeight: '38px'
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                        />
                        {' '}
                        Clear Filters
                    </button>
                )}
            </div>

            {hasActiveFilters && !loading && (
                <div
                    className="oztransui-filter-results-info"
                    style={{
                        marginBottom: '12px',
                        fontSize: '13px',
                        opacity: 0.8
                    }}
                >
                    Showing{' '}
                    <strong>
                        {filteredProductBasedData.length +
                            filteredOverallData.length}
                    </strong>{' '}
                    matching product rows across{' '}
                    <strong>
                        {visiblePartnerIds.size}
                    </strong>{' '}
                    partner
                    {visiblePartnerIds.size === 1
                        ? ''
                        : 's'}
                    .
                </div>
            )}

            <div className="oztransui-partner-sales-product-body">
                {viewMode === 'summary' ? (
                    <div className="oztransui-partner-sales-product-summary">
                        <div className="oztransui-partner-sales-product-stats">

                            <div className="oztransui-pl-card oztransui-pl-tone-info">
                                <div className="oztransui-pl-card-icon">
                                    <FontAwesomeIcon
                                        icon={faBoxes}
                                    />
                                </div>

                                <div className="oztransui-pl-card-content">
                                    <span className="oztransui-pl-card-label">
                                        {t(
                                            'productBasedPartners'
                                        ) ||
                                            'Product Based Partners'}
                                    </span>

                                    <span className="oztransui-pl-card-value">
                                        {
                                            filteredProductBasedData.length
                                        }{' '}
                                        {t('products') ||
                                            'products'}
                                    </span>

                                    <span className="oztransui-pl-card-sub">
                                        Revenue: Rs.{' '}
                                        {formatCurrencyFn(
                                            filteredProductBasedRevenue
                                        )}
                                    </span>
                                </div>
                            </div>

                            <div className="oztransui-pl-card oztransui-pl-tone-success">
                                <div className="oztransui-pl-card-icon">
                                    <FontAwesomeIcon
                                        icon={faUsers}
                                    />
                                </div>

                                <div className="oztransui-pl-card-content">
                                    <span className="oztransui-pl-card-label">
                                        {t(
                                            'overallPartners'
                                        ) ||
                                            'Overall Partners'}
                                    </span>

                                    <span className="oztransui-pl-card-value">
                                        {
                                            filteredOverallData.length
                                        }{' '}
                                        {t('products') ||
                                            'products'}
                                    </span>

                                    <span className="oztransui-pl-card-sub">
                                        Revenue: Rs.{' '}
                                        {formatCurrencyFn(
                                            filteredOverallRevenue
                                        )}
                                    </span>
                                </div>
                            </div>

                            <div className="oztransui-pl-card oztransui-pl-tone-warning">
                                <div className="oztransui-pl-card-icon">
                                    <FontAwesomeIcon
                                        icon={faUsers}
                                    />
                                </div>

                                <div className="oztransui-pl-card-content">
                                    <span className="oztransui-pl-card-label">
                                        {hasActiveFilters
                                            ? 'Matching Partners'
                                            : t(
                                                'totalPartners'
                                            ) ||
                                            'Total Partners'}
                                    </span>

                                    <span className="oztransui-pl-card-value">
                                        {hasActiveFilters
                                            ? visiblePartnerIds.size
                                            : partnerList.filter(
                                                p =>
                                                    p.status ===
                                                    'active'
                                            ).length}
                                    </span>

                                    <span className="oztransui-pl-card-sub">
                                        {hasActiveFilters
                                            ? 'Based on current filters'
                                            : t('active') ||
                                            'Active partners'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div
                            className="oztransui-partner-sales-summary-tables"
                            style={{
                                marginTop: '18px'
                            }}
                        >
                            {partnerTypeFilter !==
                                'overall' &&
                                renderPartnerSalesTable(
                                    filteredProductBasedData,
                                    t(
                                        'productBasedPartners'
                                    ) ||
                                    'Product Based Partners',
                                    'product_based'
                                )}

                            {partnerTypeFilter !==
                                'product_based' &&
                                renderPartnerSalesTable(
                                    filteredOverallData,
                                    t(
                                        'overallPartners'
                                    ) ||
                                    'Overall Partners',
                                    'overall'
                                )}
                        </div>
                    </div>
                ) : (
                    <div className="oztransui-partner-sales-product-detailed">
                        <div className="oztransui-two-column-layout">

                            {partnerTypeFilter !==
                                'overall' && (
                                    <div className="oztransui-left-column">
                                        {renderPartnerSalesTable(
                                            filteredProductBasedData,
                                            t(
                                                'productBasedPartners'
                                            ) ||
                                            'Product Based Partners',
                                            'product_based'
                                        )}
                                    </div>
                                )}

                            {partnerTypeFilter !==
                                'product_based' && (
                                    <div className="oztransui-right-column">
                                        {renderPartnerSalesTable(
                                            filteredOverallData,
                                            t(
                                                'overallPartners'
                                            ) ||
                                            'Overall Partners',
                                            'overall'
                                        )}
                                    </div>
                                )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==============================================
// ENHANCED PROFIT/LOSS WITH CHARTS
// ==============================================
const EnhancedProfitLossWithCharts = ({ data, products, partners, saleCostsById = {}, formatCurrencyFn, shopId, t }) => {
    const [activeChartTab, setActiveChartTab] = useState('overview');
    const [chartAnimation, setChartAnimation] = useState(true);

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return null;

        const shopTransactions = data.filter(t => parseInt(t.shop_id) === parseInt(shopId));
        const shopSaleCount = shopTransactions.filter(t => t.transaction_type === 'sale').length;

        const monthlyMap = {};
        const partnerMap = {};
        const expenseMap = {};
        const salaryMap = {};
        const productRevenueMap = {};

        shopTransactions.forEach(t => {
            const date = new Date(t.transaction_date || t.created_at);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const amount = parseFloat(t.grand_total || t.amount || 0);

            if (!monthlyMap[monthKey]) {
                monthlyMap[monthKey] = { revenue: 0, profit: 0, expenses: 0, salaries: 0, count: 0 };
            }

            if (t.transaction_type === 'sale') {
                const cogs = saleCostsById[String(t.sale_id)] ??
                    (parseFloat(t.cost_price_total) || parseFloat(t.total_cogs) ||
                        (shopSaleCount === 1 ? saleCostsById.__single : 0) || 0);
                monthlyMap[monthKey].revenue += amount;
                monthlyMap[monthKey].profit += amount - cogs;
                monthlyMap[monthKey].count += 1;
            } else if (t.transaction_type === 'return') {
                const returnedCost = parseFloat(t.returned_cost) || 0;
                monthlyMap[monthKey].revenue -= amount;
                monthlyMap[monthKey].profit -= amount - returnedCost;
            } else if (t.transaction_type === 'expense') {
                monthlyMap[monthKey].expenses += amount;
            } else if (t.transaction_type === 'salary_payment' || t.transaction_type === 'employee_salary') {
                monthlyMap[monthKey].salaries += amount;
                monthlyMap[monthKey].expenses += amount;
            }

            if (t.partner_id) {
                const partnerId = t.partner_id;
                const partnerName = t.reference_name || `Partner ${partnerId}`;
                if (!partnerMap[partnerId]) {
                    partnerMap[partnerId] = { name: partnerName, total: 0, profit: 0 };
                }
                partnerMap[partnerId].total += amount;
                partnerMap[partnerId].profit += amount * 0.35;
            }

            if (t.transaction_type === 'expense') {
                const key = t.expense_name || 'General';
                if (!expenseMap[key]) expenseMap[key] = 0;
                expenseMap[key] += amount;
            }
            if (t.transaction_type === 'salary_payment' || t.transaction_type === 'employee_salary') {
                const key = t.reference_name || 'Salary';
                if (!salaryMap[key]) salaryMap[key] = 0;
                salaryMap[key] += amount;
            }

            // Product revenue from products data
            if (t.product_id && t.quantity && t.sell_price) {
                const productId = t.product_id;
                const productName = t.product_name || `Product ${productId}`;
                if (!productRevenueMap[productId]) {
                    productRevenueMap[productId] = { name: productName, revenue: 0, profit: 0 };
                }
                const revenue = t.quantity * t.sell_price;
                productRevenueMap[productId].revenue += revenue;
                productRevenueMap[productId].profit += revenue * 0.35;
            }
        });

        const sortedMonths = Object.keys(monthlyMap).sort();
        const monthlyLabels = sortedMonths.map(m => {
            const [year, month] = m.split('-');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`;
        });
        const monthlyRevenue = sortedMonths.map(m => monthlyMap[m].revenue);
        const monthlyProfit = sortedMonths.map(m => monthlyMap[m].profit);
        const monthlyExpenses = sortedMonths.map(m => monthlyMap[m].expenses);
        const monthlySalaries = sortedMonths.map(m => monthlyMap[m].salaries);

        const partnerData = Object.entries(partnerMap)
            .sort((a, b) => b[1].total - a[1].total)
            .slice(0, 10)
            .map(([id, data]) => ({ label: data.name, value: data.total, profit: data.profit }));

        const expenseData = Object.entries(expenseMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([name, value]) => ({ label: name, value }));

        const salaryData = Object.entries(salaryMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([name, value]) => ({ label: name, value }));

        const productData = (products || [])
            .filter(p => parseInt(p.shop_id) === parseInt(shopId))
            .slice(0, 10)
            .map(p => ({
                label: p.product_name || p.name || 'Unknown',
                value: (parseFloat(p.quantity) || 0) * (parseFloat(p.sell_price) || 0),
                profit: (parseFloat(p.quantity) || 0) * ((parseFloat(p.sell_price) || 0) - (parseFloat(p.cost_price) || 0))
            }))
            .sort((a, b) => b.value - a.value);

        return {
            monthly: {
                labels: monthlyLabels,
                revenue: monthlyRevenue,
                profit: monthlyProfit,
                expenses: monthlyExpenses,
                salaries: monthlySalaries
            },
            partners: partnerData,
            expenses: expenseData,
            salaries: salaryData,
            products: productData,
            summary: {
                totalRevenue: monthlyRevenue.reduce((a, b) => a + b, 0),
                totalProfit: monthlyProfit.reduce((a, b) => a + b, 0),
                totalExpenses: monthlyExpenses.reduce((a, b) => a + b, 0),
                totalSalaries: monthlySalaries.reduce((a, b) => a + b, 0)
            }
        };
    }, [data, products, shopId, saleCostsById]);

    if (!chartData || chartData.monthly.labels.length === 0) {
        return (
            <div className="oztransui-no-data">
                <FontAwesomeIcon icon={faChartLine} />
                <p>{t('noChartData') || 'No data available for charts'}</p>
            </div>
        );
    }

    const lineChartData = chartData.monthly.labels.map((label, index) => ({
        revenue: chartData.monthly.revenue[index] || 0,
        profit: chartData.monthly.profit[index] || 0,
        expenses: chartData.monthly.expenses[index] || 0
    }));

    const barChartData = chartData.monthly.labels.map((label, index) => ({
        revenue: chartData.monthly.revenue[index] || 0,
        profit: chartData.monthly.profit[index] || 0,
        expenses: chartData.monthly.expenses[index] || 0
    }));

    const areaChartData = chartData.monthly.labels.map((label, index) => ({
        expenses: chartData.monthly.expenses[index] || 0,
        salaries: chartData.monthly.salaries[index] || 0
    }));

    return (
        <div className="oztransui-pl-charts-container">
            <div className="oztransui-pl-charts-header">
                <div className="oztransui-pl-charts-title">
                    <FontAwesomeIcon icon={faChartLine} />
                    <span>{t('profitAnalysisCharts') || 'Profit Analysis Charts'}</span>
                </div>
                <div className="oztransui-pl-charts-tabs">
                    <button
                        className={`oztransui-chart-tab ${activeChartTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveChartTab('overview')}
                    >
                        <FontAwesomeIcon icon={faChartLine} /> Overview
                    </button>
                    <button
                        className={`oztransui-chart-tab ${activeChartTab === 'partners' ? 'active' : ''}`}
                        onClick={() => setActiveChartTab('partners')}
                    >
                        <FontAwesomeIcon icon={faChartPie} /> Partners
                    </button>
                    <button
                        className={`oztransui-chart-tab ${activeChartTab === 'expenses' ? 'active' : ''}`}
                        onClick={() => setActiveChartTab('expenses')}
                    >
                        <FontAwesomeIcon icon={faReceipt} /> Expenses
                    </button>
                    <button
                        className={`oztransui-chart-tab ${activeChartTab === 'products' ? 'active' : ''}`}
                        onClick={() => setActiveChartTab('products')}
                    >
                        <FontAwesomeIcon icon={faBoxes} /> Products
                    </button>
                </div>
            </div>

            <div className="oztransui-pl-charts-body">
                {activeChartTab === 'overview' && (
                    <div className="oztransui-charts-grid">
                        <div className="oztransui-chart-card oztransui-chart-card-full">
                            <EnhancedLineChart
                                data={lineChartData}
                                labels={chartData.monthly.labels}
                                title="Monthly Revenue, Profit & Expenses"
                                formatCurrencyFn={formatCurrencyFn}
                                height={300}
                            />
                        </div>
                        <div className="oztransui-chart-card">
                            <EnhancedBarChart
                                data={barChartData}
                                labels={chartData.monthly.labels}
                                title="Monthly Breakdown"
                                formatCurrencyFn={formatCurrencyFn}
                                height={270}
                            />
                        </div>
                        <div className="oztransui-chart-card">
                            <EnhancedAreaChart
                                data={areaChartData}
                                labels={chartData.monthly.labels}
                                title="Expenses & Salaries Trend"
                                formatCurrencyFn={formatCurrencyFn}
                                height={270}
                            />
                        </div>
                    </div>
                )}

                {activeChartTab === 'partners' && (
                    <div className="oztransui-charts-grid">
                        {chartData.partners.length > 0 ? (
                            <>
                                <div className="oztransui-chart-card">
                                    <EnhancedPieChart
                                        data={chartData.partners}
                                        labels={chartData.partners.map(p => p.label)}
                                        title="Partner Revenue Distribution"
                                        formatCurrencyFn={formatCurrencyFn}
                                        height={280}
                                    />
                                </div>
                                <div className="oztransui-chart-card">
                                    <EnhancedPieChart
                                        data={chartData.partners}
                                        labels={chartData.partners.map(p => p.label)}
                                        title="Partner Share"
                                        formatCurrencyFn={formatCurrencyFn}
                                        height={280}
                                        isDoughnut={true}
                                    />
                                </div>
                                <div className="oztransui-chart-card oztransui-chart-card-full">
                                    <EnhancedPolarChart
                                        data={chartData.partners}
                                        labels={chartData.partners.map(p => p.label)}
                                        title="Partner Distribution"
                                        formatCurrencyFn={formatCurrencyFn}
                                        height={280}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="oztransui-no-data">
                                <FontAwesomeIcon icon={faHandshake} />
                                <p>{t('noPartnerData') || 'No partner data available'}</p>
                            </div>
                        )}
                    </div>
                )}

                {activeChartTab === 'expenses' && (
                    <div className="oztransui-charts-grid">
                        {chartData.expenses.length > 0 || chartData.salaries.length > 0 ? (
                            <>
                                <div className="oztransui-chart-card">
                                    <EnhancedPieChart
                                        data={chartData.expenses}
                                        labels={chartData.expenses.map(p => p.label)}
                                        title="Expense Breakdown"
                                        formatCurrencyFn={formatCurrencyFn}
                                        height={280}
                                    />
                                </div>
                                <div className="oztransui-chart-card">
                                    <EnhancedPieChart
                                        data={chartData.salaries}
                                        labels={chartData.salaries.map(p => p.label)}
                                        title="Salary Breakdown"
                                        formatCurrencyFn={formatCurrencyFn}
                                        height={280}
                                        isDoughnut={true}
                                    />
                                </div>
                                <div className="oztransui-chart-card oztransui-chart-card-full">
                                    <EnhancedAreaChart
                                        data={areaChartData}
                                        labels={chartData.monthly.labels}
                                        title="Expenses & Salaries Trend"
                                        formatCurrencyFn={formatCurrencyFn}
                                        height={280}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="oztransui-no-data">
                                <FontAwesomeIcon icon={faReceipt} />
                                <p>{t('noExpenseData') || 'No expense data available'}</p>
                            </div>
                        )}
                    </div>
                )}

                {activeChartTab === 'products' && (
                    <div className="oztransui-charts-grid">
                        {chartData.products.length > 0 ? (
                            <>
                                <div className="oztransui-chart-card">
                                    <EnhancedPieChart
                                        data={chartData.products}
                                        labels={chartData.products.map(p => p.label)}
                                        title="Product Revenue Distribution"
                                        formatCurrencyFn={formatCurrencyFn}
                                        height={280}
                                    />
                                </div>
                                <div className="oztransui-chart-card">
                                    <EnhancedPieChart
                                        data={chartData.products}
                                        labels={chartData.products.map(p => p.label)}
                                        title="Product Sales Share"
                                        formatCurrencyFn={formatCurrencyFn}
                                        height={280}
                                        isDoughnut={true}
                                    />
                                </div>
                                <div className="oztransui-chart-card oztransui-chart-card-full">
                                    <EnhancedRadarChart
                                        data={[
                                            { label: 'Revenue', values: chartData.products.slice(0, 6).map(p => p.value) }
                                        ]}
                                        labels={chartData.products.slice(0, 6).map(p => p.label)}
                                        title="Product Performance"
                                        formatCurrencyFn={formatCurrencyFn}
                                        height={280}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="oztransui-no-data">
                                <FontAwesomeIcon icon={faBoxes} />
                                <p>{t('noProductData') || 'No product data available'}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Summary Cards with Trends */}
            <div className="oztransui-charts-summary">
                <StatsTrendCard
                    icon={faArrowUp}
                    label="Total Revenue"
                    value={chartData.summary.totalRevenue}
                    trend={15.8}
                    trendLabel="Growth"
                    tone="success"
                    formatCurrencyFn={formatCurrencyFn}
                />
                <StatsTrendCard
                    icon={faChartLine}
                    label="Total Profit"
                    value={chartData.summary.totalProfit}
                    trend={22.3}
                    trendLabel="Growth"
                    tone="info"
                    formatCurrencyFn={formatCurrencyFn}
                />
                <StatsTrendCard
                    icon={faArrowDown}
                    label="Total Expenses"
                    value={chartData.summary.totalExpenses}
                    trend={-8.2}
                    trendLabel="Reduction"
                    tone="warning"
                    formatCurrencyFn={formatCurrencyFn}
                />
                <StatsTrendCard
                    icon={faBalanceScale}
                    label="Net Profit"
                    value={chartData.summary.totalProfit - chartData.summary.totalExpenses}
                    trend={35.6}
                    trendLabel="Growth"
                    tone="primary"
                    formatCurrencyFn={formatCurrencyFn}
                />
            </div>

            {/* Mini Progress Bars */}
            <div className="oztransui-mini-progress-section">
                <div className="oztransui-mini-progress-title">Revenue Breakdown</div>
                <div className="oztransui-mini-progress-grid">
                    <MiniProgressBar
                        label="Revenue"
                        value={chartData.summary.totalRevenue}
                        total={chartData.summary.totalRevenue + chartData.summary.totalExpenses}
                        color="#10b981"
                        formatCurrencyFn={formatCurrencyFn}
                    />
                    <MiniProgressBar
                        label="Profit"
                        value={chartData.summary.totalProfit}
                        total={chartData.summary.totalRevenue}
                        color="#3b82f6"
                        formatCurrencyFn={formatCurrencyFn}
                    />
                    <MiniProgressBar
                        label="Expenses"
                        value={chartData.summary.totalExpenses}
                        total={chartData.summary.totalRevenue}
                        color="#ef4444"
                        formatCurrencyFn={formatCurrencyFn}
                    />
                </div>
            </div>
        </div>
    );
};

// ==============================================
// PROFIT/LOSS STATEMENT - UPDATED WITH ENHANCED CHARTS
// ==============================================
const ProfitLossStatement = ({ data, products, partners, translations, formatCurrencyFn, shopId, t }) => {
    const [viewMode, setViewMode] = useState('summary');
    const [plTab, setPlTab] = useState('overview');
    const [saleCostsById, setSaleCostsById] = useState({});
    const [completeSales, setCompleteSales] = useState(null);
    const [completeReturns, setCompleteReturns] = useState(null);

    useEffect(() => {
        let isCancelled = false;

        if (!shopId) {
            setSaleCostsById({});
            setCompleteSales(null);
            return () => { isCancelled = true; };
        }

        setSaleCostsById({});
        setCompleteSales(null);

        const productMap = new Map(
            (products || []).map(product => [
                String(product.id || product.product_id),
                product
            ])
        );

        const loadCompleteSales = async () => {
            let totalCostFallback = null;
            let totalRevenueFallback = null;

            try {
                const summaryResponse = await fetch(SALES_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_profit_summary',
                        shop_id: shopId
                    })
                });
                const summaryResult = await summaryResponse.json();
                const summaryCost = parseFloat(summaryResult.data?.total_cogs);
                const summaryRevenue = parseFloat(summaryResult.data?.total_sales_revenue);

                if (summaryResult.status === 'success' && Number.isFinite(summaryCost)) {
                    totalCostFallback = summaryCost;
                }
                if (summaryResult.status === 'success' && Number.isFinite(summaryRevenue)) {
                    totalRevenueFallback = summaryRevenue;
                }
            } catch (error) {
                console.warn('Could not load complete sales summary:', error);
            }

            let sales = [];
            let salesLoaded = false;
            try {
                const listResponse = await fetch(SALES_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_records',
                        shop_id: shopId
                    })
                });
                const listResult = await listResponse.json();

                if (listResult.status === 'success') {
                    salesLoaded = true;
                    sales = (listResult.data || []).filter(sale =>
                        String(sale.status || '').toLowerCase() !== 'cancelled' &&
                        String(sale.payment_status || '').toLowerCase() !== 'cancelled'
                    );
                }
            } catch (error) {
                console.warn('Could not load the complete sales list:', error);
            }

            const detailedSales = await Promise.all(sales.map(async sale => {
                const saleId = sale.id || sale.sale_id;
                try {
                    const response = await fetch(SALES_API, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'get_record',
                            id: saleId,
                            shop_id: shopId
                        })
                    });
                    const result = await response.json();

                    if (result.status !== 'success') return sale;

                    return { ...sale, ...result.data };
                } catch (error) {
                    console.warn(`Could not load details for sale ${saleId}:`, error);
                    return sale;
                }
            }));

            const entries = detailedSales.map(sale => {
                const saleId = sale.id || sale.sale_id;

                const costOfGoodsSold = (sale.items || []).reduce((sum, item) => {
                    const quantity = parseFloat(item.display_quantity ?? item.quantity) || 0;
                    const product = productMap.get(String(item.product_id));
                    const savedCostPrice = parseFloat(item.cost_price);
                    const currentCostPrice = parseFloat(
                        product?.cost_price ?? product?.cost_per_unit
                    );
                    const costPrice = savedCostPrice > 0
                        ? savedCostPrice
                        : (Number.isFinite(currentCostPrice) ? currentCostPrice : 0);

                    return sum + (quantity * costPrice);
                }, 0);

                return [String(saleId), costOfGoodsSold];
            });

            const normalizedSales = detailedSales.map(sale => {
                const saleTotal = parseFloat(sale.grand_total ?? sale.total ?? sale.amount) || 0;
                const paidCandidate = [sale.paid_amount, sale.amount_paid, sale.paid]
                    .map(value => parseFloat(value))
                    .find(value => Number.isFinite(value));
                const savedBalance = [sale.balance, sale.amount_due]
                    .map(value => parseFloat(value))
                    .find(value => Number.isFinite(value));
                const paymentStatus = String(sale.payment_status || sale.status || '').toLowerCase();
                const isFullyPaid = ['paid', 'full_paid', 'fully_paid'].includes(paymentStatus);
                let cashReceived = Number.isFinite(paidCandidate)
                    ? Math.max(0, paidCandidate)
                    : (Number.isFinite(savedBalance) ? Math.max(0, saleTotal - savedBalance) : 0);

                if (cashReceived === 0 && isFullyPaid && (!Number.isFinite(savedBalance) || savedBalance <= 0)) {
                    cashReceived = saleTotal;
                }

                const balanceDue = Number.isFinite(savedBalance)
                    ? Math.max(0, savedBalance)
                    : Math.max(0, saleTotal - cashReceived);

                return {
                    ...sale,
                    transaction_type: 'sale',
                    sale_id: sale.id || sale.sale_id,
                    transaction_date: sale.date || sale.transaction_date || sale.created_at,
                    amount: saleTotal,
                    grand_total: saleTotal,
                    amount_paid: cashReceived,
                    amount_due: balanceDue,
                    balance: balanceDue,
                    reference_type: 'customer',
                    reference_id: sale.customer_id || null,
                    reference_name: sale.customer_name || 'Walk-in Customer',
                    description: sale.description || `Sale - Bill #${sale.bill_number || sale.invoice_number || sale.id || '-'}`,
                    status: sale.status || sale.payment_status || 'completed'
                };
            });

            if (!isCancelled) {
                const nextSaleCosts = Object.fromEntries(
                    entries.filter(([, cost]) => Number.isFinite(cost))
                );

                if (Number.isFinite(totalCostFallback)) {
                    nextSaleCosts.__total = totalCostFallback;
                    if (normalizedSales.length === 1) {
                        nextSaleCosts.__single = totalCostFallback;
                    }
                }
                if (Number.isFinite(totalRevenueFallback)) {
                    nextSaleCosts.__revenue = totalRevenueFallback;
                }
                nextSaleCosts.__useRevenueFallback = !salesLoaded;

                setSaleCostsById(nextSaleCosts);
                setCompleteSales(salesLoaded ? normalizedSales : null);
            }
        };

        loadCompleteSales();

        return () => { isCancelled = true; };
    }, [products, shopId]);

    useEffect(() => {
        let isCancelled = false;

        if (!shopId) {
            setCompleteReturns(null);
            return () => { isCancelled = true; };
        }

        setCompleteReturns(null);

        const productMap = new Map(
            (products || []).map(product => [
                String(product.id || product.product_id),
                product
            ])
        );

        const loadCompleteReturns = async () => {
            try {
                const listResponse = await fetch(RETURNS_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_records',
                        shop_id: shopId
                    })
                });
                const listResult = await listResponse.json();

                if (listResult.status !== 'success') {
                    throw new Error(listResult.message || 'Could not load returns');
                }

                const detailedReturns = await Promise.all(
                    (listResult.data || []).map(async returnRecord => {
                        const returnId = returnRecord.id || returnRecord.return_id;

                        try {
                            const response = await fetch(RETURNS_API, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    action: 'get_record',
                                    id: returnId,
                                    shop_id: shopId
                                })
                            });
                            const result = await response.json();

                            return result.status === 'success'
                                ? { ...returnRecord, ...result.data }
                                : returnRecord;
                        } catch (error) {
                            console.warn(`Could not load details for return ${returnId}:`, error);
                            return returnRecord;
                        }
                    })
                );

                const normalizedReturns = detailedReturns.map(returnRecord => {
                    const returnValue = parseFloat(
                        returnRecord.grand_total ?? returnRecord.subtotal ?? returnRecord.refund_amount
                    ) || 0;
                    const refundAmount = parseFloat(returnRecord.refund_amount) || 0;
                    const itemReturnedCost = (returnRecord.items || []).reduce((sum, item) => {
                        const quantity = parseFloat(item.quantity) || 0;
                        const product = productMap.get(String(item.product_id));
                        const savedCost = parseFloat(item.cost_price);
                        const currentCost = parseFloat(
                            product?.cost_price ?? product?.cost_per_unit
                        );
                        const costPrice = Number.isFinite(savedCost) && savedCost > 0
                            ? savedCost
                            : (Number.isFinite(currentCost) ? currentCost : 0);

                        return sum + (quantity * costPrice);
                    }, 0);
                    const savedReturnedCost = [
                        returnRecord.returned_cost,
                        returnRecord.cost_price_total,
                        returnRecord.total_cogs
                    ]
                        .map(value => parseFloat(value))
                        .find(value => Number.isFinite(value) && value > 0);
                    const returnedCost = itemReturnedCost > 0
                        ? itemReturnedCost
                        : (savedReturnedCost || 0);

                    return {
                        ...returnRecord,
                        transaction_type: 'return',
                        return_id: returnRecord.id || returnRecord.return_id,
                        sale_id: returnRecord.sell_id || returnRecord.sale_id || null,
                        sell_id: returnRecord.sell_id || returnRecord.sale_id || null,
                        transaction_date: returnRecord.date || returnRecord.transaction_date || returnRecord.created_at,
                        amount: returnValue,
                        grand_total: returnValue,
                        refund_amount: refundAmount,
                        returned_cost: returnedCost,
                        reference_type: 'customer',
                        reference_id: returnRecord.customer_id || null,
                        reference_name: returnRecord.customer_name || 'Walk-in Customer',
                        description: returnRecord.description || `Return #${returnRecord.return_number || returnRecord.id || '-'}`,
                        status: returnRecord.status || (refundAmount > 0 ? 'refunded' : 'completed')
                    };
                });

                if (!isCancelled) {
                    setCompleteReturns(normalizedReturns);
                }
            } catch (error) {
                console.warn('Could not load complete return records; using transaction returns:', error);
                if (!isCancelled) setCompleteReturns(null);
            }
        };

        loadCompleteReturns();

        return () => { isCancelled = true; };
    }, [products, shopId]);

    const completeProfitLossData = useMemo(() => {
        const shopData = (data || []).filter(transaction =>
            parseInt(transaction.shop_id) === parseInt(shopId)
        );

        let combinedData = shopData;

        if (completeSales !== null) {
            combinedData = [
                ...combinedData.filter(transaction => transaction.transaction_type !== 'sale'),
                ...completeSales
            ];
        }

        if (completeReturns !== null) {
            combinedData = [
                ...combinedData.filter(transaction => transaction.transaction_type !== 'return'),
                ...completeReturns
            ];
        }

        const salesById = new Map(
            combinedData
                .filter(transaction => transaction.transaction_type === 'sale')
                .map(transaction => [
                    String(transaction.sale_id || transaction.id),
                    transaction
                ])
        );

        return combinedData.map(transaction => {
            if (transaction.transaction_type !== 'return') return transaction;

            const savedReturnedCost = [
                transaction.returned_cost,
                transaction.cost_price_total,
                transaction.total_cogs
            ]
                .map(value => parseFloat(value))
                .find(value => Number.isFinite(value) && value > 0);

            if (Number.isFinite(savedReturnedCost) && savedReturnedCost > 0) {
                return { ...transaction, returned_cost: savedReturnedCost };
            }

            const linkedSaleId = transaction.sell_id || transaction.sale_id;
            const linkedSale = linkedSaleId
                ? salesById.get(String(linkedSaleId))
                : null;
            const fallbackSaleCost = [
                linkedSaleId ? saleCostsById[String(linkedSaleId)] : null,
                linkedSale?.cost_price_total,
                linkedSale?.total_cogs,
                salesById.size === 1 ? saleCostsById.__single : null
            ]
                .map(value => parseFloat(value))
                .find(value => Number.isFinite(value) && value > 0);

            if (!Number.isFinite(fallbackSaleCost) || fallbackSaleCost <= 0) {
                return { ...transaction, returned_cost: 0 };
            }

            const returnValue = parseFloat(
                transaction.grand_total ?? transaction.amount ?? transaction.subtotal
            ) || 0;
            const linkedSaleRevenue = parseFloat(
                linkedSale?.grand_total ?? linkedSale?.amount ?? linkedSale?.total
            ) || 0;
            const returnedShare = linkedSaleRevenue > 0
                ? Math.min(1, Math.max(0, returnValue / linkedSaleRevenue))
                : 1;

            return {
                ...transaction,
                returned_cost: fallbackSaleCost * returnedShare
            };
        });
    }, [data, completeSales, completeReturns, shopId, saleCostsById]);

    const profitData = useMemo(() => {
        if (completeProfitLossData.length === 0) return null;

        let totalSalesRevenue = 0;
        let totalActualProfit = 0;
        let totalReturns = 0;
        let totalReturnedCost = 0;
        let totalRefundAmount = 0;
        let totalReturnedCashExcluded = 0;
        let totalReturnedBalanceExcluded = 0;
        let totalCOGS = 0;
        let totalCashReceived = 0;
        let totalBalanceDue = 0;
        let totalSalaryPayments = 0;
        let totalExpenses = 0;

        const shopTransactions = completeProfitLossData;

        const remainingFinancialsBySaleId = new Map();

        shopTransactions
            .filter(transaction => transaction.transaction_type === 'sale')
            .forEach(sale => {
                const saleId = sale.sale_id || sale.id;
                if (!saleId) return;

                const saleRevenue = parseFloat(sale.grand_total ?? sale.amount ?? sale.total) || 0;
                const paidCandidate = [sale.amount_paid, sale.paid_amount, sale.paid]
                    .map(value => parseFloat(value))
                    .find(value => Number.isFinite(value));
                const savedBalance = [sale.amount_due, sale.balance]
                    .map(value => parseFloat(value))
                    .find(value => Number.isFinite(value));
                const paymentStatus = String(sale.payment_status || sale.status || '').toLowerCase();
                const isFullyPaid = ['paid', 'full_paid', 'fully_paid'].includes(paymentStatus);
                let cashReceived = Number.isFinite(paidCandidate)
                    ? Math.max(0, paidCandidate)
                    : (Number.isFinite(savedBalance) ? Math.max(0, saleRevenue - savedBalance) : 0);

                if (cashReceived === 0 && isFullyPaid && (!Number.isFinite(savedBalance) || savedBalance <= 0)) {
                    cashReceived = saleRevenue;
                }

                const balanceDue = Number.isFinite(savedBalance)
                    ? Math.max(0, savedBalance)
                    : Math.max(0, saleRevenue - cashReceived);

                remainingFinancialsBySaleId.set(String(saleId), {
                    revenue: saleRevenue,
                    cashReceived,
                    balanceDue
                });
            });

        shopTransactions.forEach(t => {
            const amount = parseFloat(t.amount) || 0;
            const grand = parseFloat(t.grand_total) || 0;

            switch (t.transaction_type) {
                case 'sale':
                    {
                        const saleRevenue = grand || amount;
                        const paidCandidate = [t.amount_paid, t.paid_amount, t.paid]
                            .map(value => parseFloat(value))
                            .find(value => Number.isFinite(value));
                        const savedBalance = [t.amount_due, t.balance]
                            .map(value => parseFloat(value))
                            .find(value => Number.isFinite(value));
                        const paymentStatus = String(t.payment_status || t.status || '').toLowerCase();
                        const isFullyPaid = ['paid', 'full_paid', 'fully_paid'].includes(paymentStatus);
                        let cashReceived = Number.isFinite(paidCandidate)
                            ? Math.max(0, paidCandidate)
                            : (Number.isFinite(savedBalance) ? Math.max(0, saleRevenue - savedBalance) : 0);

                        if (cashReceived === 0 && isFullyPaid && (!Number.isFinite(savedBalance) || savedBalance <= 0)) {
                            cashReceived = saleRevenue;
                        }

                        const balanceDue = Number.isFinite(savedBalance)
                            ? Math.max(0, savedBalance)
                            : Math.max(0, saleRevenue - cashReceived);

                        totalSalesRevenue += saleRevenue;
                        totalCashReceived += cashReceived;
                        totalBalanceDue += balanceDue;
                    }
                    {
                        const savedTransactionCost =
                            parseFloat(t.cost_price_total) || parseFloat(t.total_cogs) || 0;
                        const matchedSaleCost = saleCostsById[String(t.sale_id)];
                        const saleCost = Number.isFinite(matchedSaleCost)
                            ? matchedSaleCost
                            : (savedTransactionCost > 0
                                ? savedTransactionCost
                                : saleCostsById.__single);

                        if (Number.isFinite(saleCost)) {
                            totalCOGS += saleCost;
                        }
                    }
                    break;
                case 'return':
                    {
                        const returnValue = grand || amount;
                        const refundAmount = parseFloat(
                            t.refund_amount ?? t.amount_paid ?? t.amount
                        ) || 0;
                        const returnedCost = [t.returned_cost, t.cost_price_total, t.total_cogs]
                            .map(value => parseFloat(value))
                            .find(value => Number.isFinite(value) && value > 0) || 0;

                        totalReturns += returnValue;
                        totalReturnedCost += returnedCost;
                        totalRefundAmount += refundAmount;

                        const linkedSaleId = t.sell_id || t.sale_id;

                        if (linkedSaleId) {
                            const saleKey = String(linkedSaleId);
                            const linkedFinancials = remainingFinancialsBySaleId.get(saleKey);

                            if (linkedFinancials && linkedFinancials.revenue > 0) {
                                const applicableReturnValue = Math.min(
                                    returnValue,
                                    linkedFinancials.revenue
                                );
                                const returnedShare = applicableReturnValue / linkedFinancials.revenue;
                                const returnedCash = linkedFinancials.cashReceived * returnedShare;
                                const returnedBalance = linkedFinancials.balanceDue * returnedShare;

                                totalReturnedCashExcluded += returnedCash;
                                totalReturnedBalanceExcluded += returnedBalance;

                                remainingFinancialsBySaleId.set(saleKey, {
                                    revenue: Math.max(0, linkedFinancials.revenue - applicableReturnValue),
                                    cashReceived: Math.max(0, linkedFinancials.cashReceived - returnedCash),
                                    balanceDue: Math.max(0, linkedFinancials.balanceDue - returnedBalance)
                                });
                            }
                        }
                    }
                    break;
                case 'salary_payment':
                case 'employee_salary':
                    totalSalaryPayments += amount;
                    break;
                case 'expense':
                    totalExpenses += amount;
                    break;
                default:
                    break;
            }
        });

        let totalStockCost = 0;
        let totalStockValue = 0;
        let totalEstimatedProfit = 0;
        let totalStockQuantity = 0;

        if (products && products.length > 0) {
            const shopProducts = products.filter(p =>
                parseInt(p.shop_id) === parseInt(shopId)
            );

            shopProducts.forEach(product => {
                const quantity = parseFloat(product.quantity) || 0;
                const costPrice = parseFloat(product.cost_price) || parseFloat(product.cost_per_unit) || 0;
                const sellPrice = parseFloat(product.sell_price) || parseFloat(product.sell_price_per_unit) || 0;

                if (quantity > 0 && costPrice > 0 && sellPrice > 0) {
                    totalStockCost += quantity * costPrice;
                    totalStockValue += quantity * sellPrice;
                    totalEstimatedProfit += quantity * (sellPrice - costPrice);
                    totalStockQuantity += quantity;
                }
            });
        }

        if (
            saleCostsById.__useRevenueFallback &&
            Number.isFinite(saleCostsById.__revenue)
        ) {
            totalSalesRevenue = saleCostsById.__revenue;
        }
        if (Number.isFinite(saleCostsById.__total)) {
            totalCOGS = saleCostsById.__total;
        }

        const grossSalesRevenue = totalSalesRevenue;
        const grossCOGS = totalCOGS;
        const totalReturnedProfit = totalReturns - totalReturnedCost;
        totalSalesRevenue = Math.max(0, grossSalesRevenue - totalReturns);
        totalCOGS = Math.max(0, grossCOGS - totalReturnedCost);
        totalActualProfit = totalSalesRevenue - totalCOGS;

        totalCashReceived = Math.max(0, totalCashReceived - totalReturnedCashExcluded);
        totalBalanceDue = Math.max(0, totalBalanceDue - totalReturnedBalanceExcluded);

        const totalDeductions = totalSalaryPayments + totalExpenses;
        const netProfit = totalActualProfit - totalSalaryPayments - totalExpenses;

        return {
            totalSalesRevenue,
            totalStockCost,
            totalStockValue,
            totalEstimatedProfit,
            totalActualProfit,
            totalCashReceived,
            totalBalanceDue,
            totalReturns,
            totalReturnedCost,
            totalReturnedProfit,
            totalRefundAmount,
            totalSalaryPayments,
            totalExpenses,
            totalDeductions,
            netProfit,
            totalCOGS,
            totalStockQuantity,
            isProfit: netProfit >= 0
        };
    }, [completeProfitLossData, products, shopId, saleCostsById]);

    if (!profitData) return (
        <div className="oztransui-no-data">
            <FontAwesomeIcon icon={faChartBar} />
            <p>{translations.noTransactions || 'No data available'}</p>
        </div>
    );

    return (
        <div className="oztransui-profit-loss-container">
            <div className="oztransui-profit-loss-header">
                <h3><FontAwesomeIcon icon={faBalanceScale} /> {t('profitLossStatement') || 'Profit & Loss Statement'}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div className="oztransui-pl-sub-tabs">
                        <button className={`oztransui-pl-sub-tab ${plTab === 'overview' ? 'active' : ''}`} onClick={() => setPlTab('overview')}>
                            <FontAwesomeIcon icon={faChartBar} /> {t('overview') || 'Overview'}
                        </button>
                        <button className={`oztransui-pl-sub-tab ${plTab === 'stock' ? 'active' : ''}`} onClick={() => setPlTab('stock')}>
                            <FontAwesomeIcon icon={faBoxes} /> {t('stockBased') || 'Stock Based'}
                        </button>
                        <button className={`oztransui-pl-sub-tab ${plTab === 'sales' ? 'active' : ''}`} onClick={() => setPlTab('sales')}>
                            <FontAwesomeIcon icon={faCashRegister} /> {t('salesBased') || 'Sales Based'}
                        </button>
                        <button className={`oztransui-pl-sub-tab ${plTab === 'partners' ? 'active' : ''}`} onClick={() => setPlTab('partners')}>
                            <FontAwesomeIcon icon={faHandshake} /> {t('partners') || 'Partners'}
                        </button>
                        <button className={`oztransui-pl-sub-tab ${plTab === 'partner_sales' ? 'active' : ''}`} onClick={() => setPlTab('partner_sales')}>
                            <FontAwesomeIcon icon={faListAlt} /> {t('partnerSalesByProduct') || 'Partner Sales'}
                        </button>
                        <button className={`oztransui-pl-sub-tab ${plTab === 'charts' ? 'active' : ''}`} onClick={() => setPlTab('charts')}>
                            <FontAwesomeIcon icon={faChartLine} /> {t('charts') || 'Charts'}
                        </button>
                    </div>
                    <div className="oztransui-view-toggle">
                        <button className={`oztransui-view-btn ${viewMode === 'summary' ? 'active' : ''}`} onClick={() => setViewMode('summary')}>
                            <FontAwesomeIcon icon={faThLarge} /> {t('summary') || 'Summary'}
                        </button>
                        <button className={`oztransui-view-btn ${viewMode === 'detailed' ? 'active' : ''}`} onClick={() => setViewMode('detailed')}>
                            <FontAwesomeIcon icon={faChartPie} /> {t('detailed') || 'Detailed'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="oztransui-profit-loss-body">
                {plTab === 'overview' ? (
                    viewMode === 'summary' ? (
                        <div className="oztransui-profit-loss-grid">
                            <StockProfitCard
                                icon={faCashRegister}
                                label={t('netSalesRevenue') || 'Net Sales Revenue'}
                                value={profitData.totalSalesRevenue}
                                tone="info"
                                formatCurrencyFn={formatCurrencyFn}
                            />
                            <StockProfitCard
                                icon={faBoxes}
                                label={t('stockCost') || 'Stock Cost Value'}
                                value={profitData.totalStockCost}
                                tone="warning"
                                subValue={profitData.totalStockQuantity}
                                subLabel={t('stockItems') || 'Total Items'}
                                formatCurrencyFn={formatCurrencyFn}
                            />
                            <StockProfitCard
                                icon={faTag}
                                label={t('stockValue') || 'Stock Sell Value'}
                                value={profitData.totalStockValue}
                                tone="info"
                                formatCurrencyFn={formatCurrencyFn}
                            />
                            <StockProfitCard
                                icon={faChartLine}
                                label={t('estimatedProfit') || 'Estimated Profit (Stock)'}
                                value={profitData.totalEstimatedProfit}
                                tone="success"
                                formatCurrencyFn={formatCurrencyFn}
                            />
                            <StockProfitCard
                                icon={faHandHoldingUsd}
                                label={t('actualProfit') || 'Actual Profit (Sales)'}
                                value={profitData.totalActualProfit}
                                tone="success"
                                formatCurrencyFn={formatCurrencyFn}
                            />
                            <StockProfitCard
                                icon={faWallet}
                                label={t('cashReceived') || 'Cash Received'}
                                value={profitData.totalCashReceived}
                                subItems={[
                                    {
                                        label: t('balanceDue') || 'Balance Due',
                                        value: profitData.totalBalanceDue
                                    }
                                ]}
                                tone="success"
                                formatCurrencyFn={formatCurrencyFn}
                            />
                            <StockProfitCard
                                icon={faUndo}
                                label={t('totalReturns') || 'Total Returns'}
                                value={profitData.totalReturns}
                                tone="warning"
                                formatCurrencyFn={formatCurrencyFn}
                            />
                            <StockProfitCard
                                icon={faUserTie}
                                label={t('salaryPayments') || 'Salary Payments'}
                                value={profitData.totalSalaryPayments}
                                tone="warning"
                                formatCurrencyFn={formatCurrencyFn}
                            />
                            <StockProfitCard
                                icon={faReceipt}
                                label={t('totalExpenses') || 'Total Expenses'}
                                value={profitData.totalExpenses}
                                tone="danger"
                                formatCurrencyFn={formatCurrencyFn}
                            />
                            <StockProfitCard
                                icon={profitData.isProfit ? faArrowUp : faArrowDown}
                                label={t('netProfitLoss') || 'Net Profit/Loss'}
                                value={Math.abs(profitData.netProfit)}
                                tone={profitData.isProfit ? 'success' : 'danger'}
                                subLabel={profitData.isProfit ? (t('profit') || 'Profit') : (t('loss') || 'Loss')}
                                formatCurrencyFn={formatCurrencyFn}
                            />
                        </div>
                    ) : (
                        <div className="oztransui-pl-detailed">
                            <div className={`oztransui-pl-hero oztransui-pl-tone-${profitData.isProfit ? 'success' : 'danger'}`}>
                                <div className="oztransui-pl-hero-icon"><FontAwesomeIcon icon={profitData.isProfit ? faArrowUp : faArrowDown} /></div>
                                <div className="oztransui-pl-hero-text">
                                    <span className="oztransui-pl-hero-label">{t('netProfitLoss') || 'Net Profit/Loss'}</span>
                                    <span className="oztransui-pl-hero-value">{profitData.isProfit ? '+' : '-'} Rs. {formatCurrencyFn(Math.abs(profitData.netProfit))}</span>
                                    <span className="oztransui-pl-hero-tag">{profitData.isProfit ? (t('profit') || 'Profit') : (t('loss') || 'Loss')}</span>
                                </div>
                            </div>
                            <div className="oztransui-pl-detailed-grid">
                                <div className="oztransui-pl-panel">
                                    <h4><FontAwesomeIcon icon={faBoxes} /> {t('stockProfitAnalysis') || 'Stock Profit Analysis'}</h4>
                                    <div className="oztransui-pl-barrows">
                                        <PLBarRow
                                            icon={faBoxes}
                                            label={t('stockCost') || 'Stock Cost'}
                                            amount={profitData.totalStockCost}
                                            total={profitData.totalStockCost + profitData.totalEstimatedProfit}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="warning"
                                        />
                                        <PLBarRow
                                            icon={faTag}
                                            label={t('stockValue') || 'Stock Value'}
                                            amount={profitData.totalStockValue}
                                            total={profitData.totalStockCost + profitData.totalEstimatedProfit}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="info"
                                        />
                                        <PLBarRow
                                            icon={faChartLine}
                                            label={t('estimatedProfit') || 'Estimated Profit'}
                                            amount={profitData.totalEstimatedProfit}
                                            total={profitData.totalStockCost + profitData.totalEstimatedProfit}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="success"
                                        />
                                    </div>
                                </div>
                                <div className="oztransui-pl-panel">
                                    <h4><FontAwesomeIcon icon={faCashRegister} /> {t('salesProfitAnalysis') || 'Sales Profit Analysis'}</h4>
                                    <div className="oztransui-pl-barrows">
                                        <PLBarRow
                                            icon={faCashRegister}
                                            label={t('netSalesRevenue') || 'Net Sales Revenue'}
                                            amount={profitData.totalSalesRevenue}
                                            total={profitData.totalSalesRevenue}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="info"
                                        />
                                        <PLBarRow
                                            icon={faShoppingCart}
                                            label={t('netCostOfGoodsSold') || 'Net COGS'}
                                            amount={profitData.totalCOGS}
                                            total={profitData.totalSalesRevenue}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="danger"
                                        />
                                        <PLBarRow
                                            icon={faUndo}
                                            label={t('totalReturns') || 'Returns'}
                                            amount={profitData.totalReturns}
                                            total={profitData.totalSalesRevenue}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="warning"
                                        />
                                        <PLBarRow
                                            icon={faBoxes}
                                            label={t('returnedCost') || 'Returned Cost'}
                                            amount={profitData.totalReturnedCost}
                                            total={profitData.totalSalesRevenue}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="info"
                                        />
                                        <PLBarRow
                                            icon={faHandHoldingUsd}
                                            label={t('actualProfit') || 'Actual Profit'}
                                            amount={profitData.totalActualProfit}
                                            total={profitData.totalSalesRevenue}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="success"
                                        />
                                    </div>
                                </div>
                                <div className="oztransui-pl-panel">
                                    <h4><FontAwesomeIcon icon={faReceipt} /> {t('deductions') || 'Deductions'}</h4>
                                    <div className="oztransui-pl-barrows">
                                        <PLBarRow
                                            icon={faUserTie}
                                            label={t('salaryPayments') || 'Salaries'}
                                            amount={profitData.totalSalaryPayments}
                                            total={profitData.totalDeductions || 1}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="warning"
                                        />
                                        <PLBarRow
                                            icon={faReceipt}
                                            label={t('totalExpenses') || 'Expenses'}
                                            amount={profitData.totalExpenses}
                                            total={profitData.totalDeductions || 1}
                                            formatCurrencyFn={formatCurrencyFn}
                                            colorClass="danger"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="oztransui-pl-panel oztransui-pl-panel-wide">
                                <h4><FontAwesomeIcon icon={faChartBar} /> {t('profitFlow') || 'Profit Flow'}</h4>
                                <PLFlowChart formatCurrencyFn={formatCurrencyFn} steps={[
                                    { label: t('stockCost') || 'Stock Cost', value: -profitData.totalStockCost, tone: 'warning' },
                                    { label: t('stockValue') || 'Stock Value', value: profitData.totalStockValue, tone: 'info' },
                                    { label: t('estimatedProfit') || 'Estimated Stock Profit', value: profitData.totalEstimatedProfit, tone: 'success' },
                                    { label: t('netSalesRevenue') || 'Net Sales Revenue', value: profitData.totalSalesRevenue, tone: 'info' },
                                    { label: t('netCostOfGoodsSold') || 'Net COGS', value: -profitData.totalCOGS, tone: 'danger' },
                                    { label: t('actualProfit') || 'Actual Sales Profit', value: profitData.totalActualProfit, tone: 'success' },
                                    { label: t('salaryPayments') || 'Salaries', value: -profitData.totalSalaryPayments, tone: 'warning' },
                                    { label: t('totalExpenses') || 'Expenses', value: -profitData.totalExpenses, tone: 'danger' },
                                    { label: t('netProfitLoss') || 'Net Profit/Loss', value: profitData.netProfit, tone: profitData.isProfit ? 'success' : 'danger' }
                                ]} />
                            </div>
                        </div>
                    )
                ) : plTab === 'stock' ? (
                    <StockProfitLoss
                        products={products}
                        formatCurrencyFn={formatCurrencyFn}
                        t={t}
                    />
                ) : plTab === 'partners' ? (
                    <PartnerProfitLoss
                        transactions={completeProfitLossData}
                        products={products}
                        partners={partners || []}
                        formatCurrencyFn={formatCurrencyFn}
                        t={t}
                        shopId={shopId}
                    />
                ) : plTab === 'partner_sales' ? (
                    <PartnerSalesByProduct
                        transactions={completeProfitLossData}
                        partners={partners || []}
                        products={products || []}
                        formatCurrencyFn={formatCurrencyFn}
                        t={t}
                        shopId={shopId}
                    />
                ) : plTab === 'charts' ? (
                    <EnhancedProfitLossWithCharts
                        data={completeProfitLossData}
                        products={products}
                        partners={partners || []}
                        saleCostsById={saleCostsById}
                        formatCurrencyFn={formatCurrencyFn}
                        shopId={shopId}
                        t={t}
                    />
                ) : (
                    <SalesProfitLoss
                        transactions={completeProfitLossData}
                        saleCostsById={saleCostsById}
                        formatCurrencyFn={formatCurrencyFn}
                        t={t}
                    />
                )}
            </div>
        </div>
    );
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const CosmeticsTransactionsManagerSpecial = () => {
    const { user, currentShop } = useAuth();
    const { t, isRTL } = useTranslator();

    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#10b981');

    const [productsData, setProductsData] = useState([]);
    const [partners, setPartners] = useState([]);

    const [activeTab, setActiveTab] = useState('all');
    const [transactions, setTransactions] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });

    const [filters, setFilters] = useState({
        type: '',
        status: '',
        startDate: '',
        endDate: '',
        search: '',
        entityType: '',
        entityId: ''
    });

    const [pagination, setPagination] = useState({ page: 1, limit: 500, total: 0 });

    const [formData, setFormData] = useState({
        type: 'customer_due',
        reference_id: '',
        amount: '',
        amount_paid: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        payment_method: 'cash',
        payment_account_id: '',
        invoice_number: '',
        expense_category: '',
        expense_name: ''
    });

    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [paymentAccounts, setPaymentAccounts] = useState([]);

    const entityTypeOptions = useMemo(() => [
        { value: '', label: t('allEntities'), icon: faLayerGroup },
        { value: 'customer', label: t('customers'), icon: faUsers },
        { value: 'supplier', label: t('suppliers'), icon: faTruck },
        { value: 'partner', label: t('partners'), icon: faHandshake },
        { value: 'employee', label: t('employees'), icon: faUserTie }
    ], [t]);

    const entityOptions = useMemo(() => {
        switch (filters.entityType) {
            case 'customer':
                return buildEntityOptions(customers, 'customer', faUsers, t('allCustomers'));
            case 'supplier':
                return buildEntityOptions(suppliers, 'supplier', faTruck, t('allSuppliers'));
            case 'partner':
                return buildEntityOptions(partners, 'partner', faHandshake, t('allPartners'));
            case 'employee':
                return buildEntityOptions(employees, 'employee', faUserTie, t('allEmployees'));
            default:
                return [];
        }
    }, [filters.entityType, customers, suppliers, partners, employees, t]);

    const selectedEntityOption = entityOptions.find(option =>
        option.value === filters.entityId
    );

    const abortControllerRef = useRef(null);
    const rawTransactionsRef = useRef([]);
    const entityDirectoryRef = useRef({
        customer: [],
        supplier: [],
        employee: [],
        partner: []
    });

    const applyThemeToDocument = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        document.documentElement.style.setProperty('--oztransui-primary', color);
        document.documentElement.style.setProperty('--oztransui-primary-rgb', `${r}, ${g}, ${b}`);
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        applyThemeToDocument(localStorage.getItem('ourzest_theme') || '#10b981');

        const onStorage = (e) => {
            if (e.key === 'ourzest_theme') applyThemeToDocument(e.newValue || '#10b981');
        };
        const onTheme = (e) => {
            if (e.detail?.theme) applyThemeToDocument(e.detail.theme);
        };

        window.addEventListener('storage', onStorage);
        window.addEventListener('themeChanged', onTheme);
        window.addEventListener('themeUpdated', onTheme);

        const interval = setInterval(() => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            if (theme !== currentThemeColor) applyThemeToDocument(theme);
        }, 1000);

        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('themeChanged', onTheme);
            window.removeEventListener('themeUpdated', onTheme);
            clearInterval(interval);
        };
    }, [applyThemeToDocument, currentThemeColor]);

    useEffect(() => {
        const syncDarkMode = () => {
            const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);
            document.body.classList.toggle('oztransui-dark', isDark);
            const container = document.querySelector('.oztransui-container');
            if (container) container.classList.toggle('oztransui-dark', isDark);
        };

        syncDarkMode();

        const onStorage = (e) => {
            if (e.key === 'ourzest_darkMode') syncDarkMode();
        };
        const onToggle = (e) => {
            const isDark = e.detail?.darkMode ?? localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);
            const container = document.querySelector('.oztransui-container');
            if (container) container.classList.toggle('oztransui-dark', isDark);
        };

        window.addEventListener('storage', onStorage);
        window.addEventListener('darkModeToggled', onToggle);
        window.addEventListener('darkModeUpdated', onToggle);

        const interval = setInterval(() => {
            const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
            if (isDark !== darkMode) syncDarkMode();
        }, 1000);

        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('darkModeToggled', onToggle);
            window.removeEventListener('darkModeUpdated', onToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    const showToast = useCallback((type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false, type: '', message: '' }), 3000);
    }, []);

    const cancelRequests = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);

    const loadReferences = useCallback(async () => {
        if (!currentShop) return;
        const endpoints = [
            {
                api: CUSTOMERS_API,
                setter: setCustomers,
                entityType: 'customer',
                action: 'get_records',
                keys: ['customers']
            },
            {
                api: SUPPLIERS_API,
                setter: setSuppliers,
                entityType: 'supplier',
                action: 'list',
                keys: ['suppliers']
            },
            {
                api: EMPLOYEES_API,
                setter: setEmployees,
                entityType: 'employee',
                action: 'get_records',
                keys: ['employees']
            }
        ];

        await Promise.all(endpoints.map(async ({ api, setter, entityType, action, keys }) => {
            try {
                const records = await fetchAllApiRecords({
                    api,
                    action,
                    shopId: currentShop.shop_id,
                    username: user?.username,
                    keys
                });
                const shopId = normalizeEntityId(currentShop.shop_id);
                const shopRecords = records.filter(record => {
                    const recordShopId = record.shop_id ?? record.shopid ?? record.shopId;
                    return recordShopId === undefined || recordShopId === null || recordShopId === '' ||
                        normalizeEntityId(recordShopId) === shopId;
                });
                entityDirectoryRef.current[entityType] = shopRecords;
                setter(shopRecords);
            } catch (err) {
                console.warn(`Failed to load ${keys[0]}:`, err);
                entityDirectoryRef.current[entityType] = [];
                setter([]);
            }
        }));
    }, [currentShop, user]);

    const loadPartners = useCallback(async () => {
        if (!currentShop) return;
        try {
            const records = await fetchAllApiRecords({
                api: PARTNERS_API,
                action: 'get_partners',
                shopId: currentShop.shop_id,
                username: user?.username,
                keys: ['partners']
            });
            const shopId = normalizeEntityId(currentShop.shop_id);
            const shopPartners = records.filter(record => {
                const recordShopId = record.shop_id ?? record.shopid ?? record.shopId;
                return recordShopId === undefined || recordShopId === null || recordShopId === '' ||
                    normalizeEntityId(recordShopId) === shopId;
            });
            entityDirectoryRef.current.partner = shopPartners;
            setPartners(shopPartners);
        } catch (err) {
            console.warn('Failed to load partners:', err);
            entityDirectoryRef.current.partner = [];
            setPartners([]);
        }
    }, [currentShop, user]);

    const loadPaymentAccounts = useCallback(async () => {
        if (!currentShop?.shop_id) {
            setPaymentAccounts([]);
            return;
        }
        try {
            const response = await fetch(ACCOUNTS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'list_accounts',
                    shop_id: currentShop.shop_id,
                    username: user?.username || 'system',
                    // Keep inactive accounts available for resolving historical
                    // transaction methods. The add/edit dropdown still filters
                    // this list to active accounts only.
                    active_only: false
                })
            });
            const text = await response.text();
            let result;
            try { result = JSON.parse(text); }
            catch { throw new Error(`Accounts API returned invalid JSON (HTTP ${response.status})`); }
            if (!response.ok || result.status !== 'success') {
                throw new Error(result.message || 'Could not load payment accounts');
            }
            setPaymentAccounts(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            console.warn('Failed to load payment accounts:', error);
            setPaymentAccounts([]);
            showToast('error', error.message || 'Could not load payment accounts');
        }
    }, [currentShop, user, showToast]);

    const loadProducts = useCallback(async () => {
        if (!currentShop) return;
        try {
            const res = await fetch(PRODUCTS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    shop_id: currentShop.shop_id,
                    username: user?.username
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                const shopProducts = (data.data || []).filter(p =>
                    parseInt(p.shop_id) === parseInt(currentShop.shop_id)
                );
                setProductsData(shopProducts);
            } else {
                setProductsData([]);
            }
        } catch (err) {
            console.warn('Failed to load products:', err);
            setProductsData([]);
        }
    }, [currentShop, user]);

    const filtersRef = useRef(filters);
    const activeTabRef = useRef(activeTab);

    useEffect(() => { filtersRef.current = filters; }, [filters]);
    useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

    const loadTransactions = useCallback(async () => {
        if (!currentShop) return;
        cancelRequests();
        rawTransactionsRef.current = [];
        abortControllerRef.current = new AbortController();
        setLoading(true);

        const currentFilters = filtersRef.current;
        const currentTab = activeTabRef.current;

        try {
            const body = {
                action: 'get_transactions',
                shop_id: currentShop.shop_id,
                limit: currentTab === 'all' && currentFilters.entityType && currentFilters.entityId
                    ? 100000
                    : 1000,
                offset: 0
            };

            const tabTypeMap = {
                sales: 'sale',
                salary: 'salary_payment',
                expense: 'expense',
                purchases: 'purchase',
                returns: ['return', 'purchase_return', 'supplier_return'],
                partners: ['partner_investment', 'partner_payment']
            };

            if (tabTypeMap[currentTab]) {
                if (Array.isArray(tabTypeMap[currentTab])) {
                    body.transaction_types = tabTypeMap[currentTab];
                } else {
                    body.transaction_type = tabTypeMap[currentTab];
                }
            } else if (currentTab === 'all' && currentFilters.type) {
                body.transaction_type = currentFilters.type;
            }

            // Sale/purchase status is reconciled against its source document
            // below. A stale transaction status must not hide the bill first.
            if (currentFilters.status && !['sales', 'purchases'].includes(currentTab)) {
                body.status = currentFilters.status;
            }
            if (currentFilters.startDate) body.start_date = currentFilters.startDate;
            if (currentFilters.endDate) body.end_date = currentFilters.endDate;

            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                signal: abortControllerRef.current.signal
            });
            const data = await res.json();

            if (data.status === 'success') {
                const all = data.data || [];
                let filteredAll = all;
                if (currentTab === 'partners') {
                    filteredAll = all.filter(tx =>
                        tx.transaction_type === 'partner_investment' ||
                        tx.transaction_type === 'partner_payment'
                    );
                }
                if (['sales', 'all'].includes(currentTab) && filteredAll.some(tx =>
                    String(tx.transaction_type || '').toLowerCase() === 'sale'
                )) {
                    try {
                        const salesResponse = await fetch(SALES_API, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                action: 'get_records',
                                shop_id: currentShop.shop_id,
                                username: user?.username
                            }),
                            signal: abortControllerRef.current.signal
                        });
                        const salesResult = await salesResponse.json();
                        if (salesResult.status === 'success' && Array.isArray(salesResult.data)) {
                            const salesById = new Map();
                            const salesByBill = new Map();
                            salesResult.data.forEach(sale => {
                                const id = sale.id ?? sale.sell_id ?? sale.sale_id;
                                if (id !== undefined && id !== null && id !== '') {
                                    salesById.set(String(id), sale);
                                }
                                [sale.bill_number, sale.bn].forEach(bill => {
                                    const key = String(bill || '').trim().toLowerCase();
                                    if (key) salesByBill.set(key, sale);
                                });
                            });

                            filteredAll = filteredAll.map(tx => {
                                const sourceIsSale = String(tx.source_type || '').toLowerCase() === 'sale';
                                const saleId = tx.sale_id ?? tx.sell_id ?? (sourceIsSale ? tx.source_id : null);
                                const billKey = String(
                                    tx.sale_bill_number ?? tx.invoice_number ?? tx.bill_number ?? tx.bn ?? ''
                                ).trim().toLowerCase();
                                const sale = (
                                    saleId !== undefined && saleId !== null && saleId !== ''
                                        ? salesById.get(String(saleId))
                                        : null
                                ) || salesByBill.get(billKey);
                                if (!sale) return tx;

                                const invoiceTotal = Math.max(0, Number.parseFloat(sale.grand_total) || 0);
                                const invoicePaid = Math.min(
                                    invoiceTotal,
                                    Math.max(0, Number.parseFloat(sale.paid_amount) || 0)
                                );
                                const invoiceBalance = Math.max(0, invoiceTotal - invoicePaid);
                                const invoiceStatus = invoiceBalance <= 0.01
                                    ? 'full_paid'
                                    : (invoicePaid > 0.01 ? 'partial_paid' : 'unpaid');

                                return {
                                    ...tx,
                                    sale_id: sale.id ?? saleId,
                                    amount: invoiceTotal,
                                    grand_total: invoiceTotal,
                                    amount_paid: invoicePaid,
                                    paid_amount: invoicePaid,
                                    amount_due: invoiceBalance,
                                    balance: invoiceBalance,
                                    status: invoiceStatus,
                                    payment_status: invoiceStatus,
                                    _invoice_total: invoiceTotal,
                                    _invoice_paid: invoicePaid,
                                    _invoice_balance: invoiceBalance,
                                    _invoice_authoritative: true,
                                    payment_method: sale.payment_method || tx.payment_method,
                                    payment_breakdown: sale.payment_breakdown ?? tx.payment_breakdown
                                };
                            });
                        }
                    } catch (salesError) {
                        if (salesError.name === 'AbortError') throw salesError;
                        console.warn('Could not reconcile current sale balances:', salesError);
                    }
                }

                if (['purchases', 'all'].includes(currentTab) && filteredAll.some(tx =>
                    String(tx.transaction_type || '').toLowerCase() === 'purchase'
                )) {
                    try {
                        const purchasesResponse = await fetch(PURCHASES_API, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                action: 'get_records',
                                shop_id: currentShop.shop_id,
                                username: user?.username,
                                include_items: false
                            }),
                            signal: abortControllerRef.current.signal
                        });
                        const purchasesResult = await purchasesResponse.json();
                        if (purchasesResult.status === 'success' && Array.isArray(purchasesResult.data)) {
                            const purchasesById = new Map();
                            const purchasesByBill = new Map();
                            purchasesResult.data.forEach(purchase => {
                                const id = purchase.id ?? purchase.purchase_id;
                                if (id !== undefined && id !== null && id !== '') {
                                    purchasesById.set(String(id), purchase);
                                }
                                const billKey = String(purchase.bill_no || '').trim().toLowerCase();
                                if (billKey) purchasesByBill.set(billKey, purchase);
                            });

                            filteredAll = filteredAll.map(tx => {
                                if (String(tx.transaction_type || '').toLowerCase() !== 'purchase') return tx;
                                const sourceIsPurchase = String(tx.source_type || '').toLowerCase() === 'purchase';
                                const purchaseId = tx.purchase_id ?? (sourceIsPurchase ? tx.source_id : null);
                                const billKey = String(
                                    tx.purchase_bill_number ?? tx.invoice_number ?? tx.bill_no ?? ''
                                ).trim().toLowerCase();
                                const purchase = (
                                    purchaseId !== undefined && purchaseId !== null && purchaseId !== ''
                                        ? purchasesById.get(String(purchaseId))
                                        : null
                                ) || purchasesByBill.get(billKey);
                                if (!purchase) return tx;

                                const invoiceTotal = Math.max(0, Number.parseFloat(purchase.grand_total) || 0);
                                const invoicePaid = Math.min(
                                    invoiceTotal,
                                    Math.max(0, Number.parseFloat(purchase.paid) || 0)
                                );
                                const invoiceBalance = Math.max(0, invoiceTotal - invoicePaid);
                                const invoiceStatus = invoiceBalance <= 0.01
                                    ? 'full_paid'
                                    : (invoicePaid > 0.01 ? 'partial_paid' : 'unpaid');

                                return {
                                    ...tx,
                                    purchase_id: purchase.id ?? purchaseId,
                                    amount: invoiceTotal,
                                    grand_total: invoiceTotal,
                                    amount_paid: invoicePaid,
                                    paid_amount: invoicePaid,
                                    amount_due: invoiceBalance,
                                    balance: invoiceBalance,
                                    previous_balance: Number.parseFloat(purchase.previous_balance) || 0,
                                    payable_total: Number.parseFloat(purchase.payable_total) || invoiceTotal,
                                    paid_at_purchase: Number.parseFloat(purchase.paid_at_purchase) || invoicePaid,
                                    status: invoiceStatus,
                                    payment_status: invoiceStatus,
                                    _invoice_total: invoiceTotal,
                                    _invoice_paid: invoicePaid,
                                    _invoice_balance: invoiceBalance,
                                    _invoice_authoritative: true,
                                    payment_method: purchase.payment_method || tx.payment_method,
                                    payment_breakdown: purchase.payment_breakdown ?? tx.payment_breakdown
                                };
                            });
                        }
                    } catch (purchasesError) {
                        if (purchasesError.name === 'AbortError') throw purchasesError;
                        console.warn('Could not reconcile current purchase balances:', purchasesError);
                    }
                }

                rawTransactionsRef.current = filteredAll;
                const processed = processCascadingBalances(filteredAll, {
                    invoiceView: ['sales', 'purchases'].includes(currentTab)
                });
                setTransactions(processed);
                setPagination(prev => ({
                    ...prev,
                    total: data.pagination?.total || processed.length
                }));
            } else {
                showToast('error', data.message || 'Failed to load transactions');
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                showToast('error', 'Failed to load transactions');
            }
        } finally {
            setLoading(false);
            abortControllerRef.current = null;
        }
    }, [currentShop, user, cancelRequests, showToast]);

   const processCascadingBalances = useCallback((data, options = {}) => {
    const invoiceView = Boolean(options.invoiceView);
    const toNumber = (value) => {
        const number = Number.parseFloat(value);
        return Number.isFinite(number) ? number : 0;
    };

    // Payment-only entries do not create a new bill. Their complete amount belongs
    // in the Paid column and is deducted from the account's previous balance.
    const PAYMENT_TRANSACTION_TYPES = new Set([
        'payment_received',
        'payment_to_supplier',
        'partner_payment',
        'advance_received',
        'advance_received_customer',
        'advance_paid_supplier',
        'advance_return_customer',
        'advance_return_supplier',
        'salary_payment',
        'employee_payment',
        'salary_advance',
        'payment',
        'due_payment',
        'due_paid',
        'payment_against_due',
        'customer_due_payment',
        'supplier_due_payment'
    ]);

    // These entries return an earlier advance, so they increase the account
    // balance even though their amount is still shown in the Paid column.
    const REVERSE_ADVANCE_TYPES = new Set([
        'advance_return_customer',
        'advance_return_supplier'
    ]);
    const RETURN_BILL_TYPES = new Set([
    'return',
    'sale_return',
    'sales_return',
    'customer_return',
    'purchase_return',
    'supplier_return'
]);

    const getTransactionAmount = (tx, type) => {
        const directCandidates = [
            tx.amount,
            tx.transaction_amount,
            tx.payment_amount,
            tx.received_amount,
            tx.grand_total
        ];
        const fallbackCandidates = PAYMENT_TRANSACTION_TYPES.has(type)
            ? [tx.amount_paid, tx.paid_amount, tx.grand_total]
            : [tx.grand_total, tx.amount_due, tx.balance];
        const candidates = [
            ...directCandidates,
            ...fallbackCandidates
        ];
        const value = candidates.find(candidate =>
            candidate !== undefined && candidate !== null && candidate !== ''
        );
        return toNumber(value);
    };

    const normalizeType = (type) => ({
        customer_due: 'due',
        customer_payment: 'payment_received',
        customer_due_payment: 'payment_received',
        supplier_payment: 'payment_to_supplier',
        supplier_due_payment: 'payment_to_supplier',
        employee_salary_payment: 'salary_payment',
        advance_payment: 'advance_received',
        customer_advance: 'advance_received',
        supplier_advance: 'advance_received',
        customer_advance_received: 'advance_received_customer',
        supplier_advance_paid: 'advance_paid_supplier',
        customer_advance_return: 'advance_return_customer',
        supplier_advance_return: 'advance_return_supplier'
    }[String(type || '').toLowerCase()] || String(type || '').toLowerCase());

    const normalizeReferenceType = (type) =>
        String(type || '').toLowerCase().replace(/s$/, '');

    const entityFields = {
        customer: {
            transactionIds: ['customer_id', 'customerid', 'customerId', 'client_id'],
            recordIds: ['customer_id', 'customerid', 'customerId', 'id', 'value'],
            transactionNames: ['customer_name', 'customerName', 'customername', 'client_name'],
            recordNames: ['customer_name', 'customerName', 'customername', 'full_name', 'display_name', 'name', 'label']
        },
        supplier: {
            transactionIds: ['supplier_id', 'supplierid', 'supplierId', 'vendor_id'],
            recordIds: ['supplier_id', 'supplierid', 'supplierId', 'id', 'value'],
            transactionNames: ['supplier_name', 'supplierName', 'suppliername', 'vendor_name'],
            recordNames: ['supplier_name', 'supplierName', 'suppliername', 'company_name', 'display_name', 'name', 'label']
        },
        partner: {
            transactionIds: ['partner_id', 'partnerid', 'partnerId'],
            recordIds: ['partner_id', 'partnerid', 'partnerId', 'id', 'value'],
            transactionNames: ['partner_name', 'partnerName', 'partnername'],
            recordNames: ['partner_name', 'partnerName', 'partnername', 'full_name', 'display_name', 'name', 'label']
        },
        employee: {
            transactionIds: ['employee_id', 'employeeid', 'employeeId', 'staff_id'],
            recordIds: ['employee_id', 'employeeid', 'employeeId', 'id', 'value'],
            transactionNames: ['employee_name', 'employeeName', 'employeename', 'staff_name'],
            recordNames: ['employee_name', 'employeeName', 'employeename', 'full_name', 'display_name', 'name', 'label']
        }
    };

    const firstPopulatedField = (record, fields = []) => fields
        .map(field => record?.[field])
        .find(value => value !== undefined && value !== null && String(value).trim() !== '');

    const extractEmbeddedEntityName = (transaction, accountType) => {
        const description = String(transaction.description || '').trim();
        if (!description) return '';

        const patterns = accountType === 'customer'
            ? [
                /\bcustomer\s*:\s*(.+)$/i,
                /^advance\s+received\s+from\s+customer\s*[-:]\s*(.+)$/i,
                /^advance\s+returned\s+(?:back\s+)?to\s+customer\s*[-:]\s*(.+)$/i,
                /^advance\s+received\s+from\s+(.+)$/i,
                /^payment\s+received\s+from\s+(.+)$/i,
                /^customer\s+(?:due|payment)\s*[-:]\s*(.+)$/i
            ]
            : accountType === 'supplier'
                ? [
                    /\bsupplier\s*:\s*(.+)$/i,
                    /^advance\s+paid\s+to\s+supplier\s*[-:]\s*(.+)$/i,
                    /^supplier\s+returned\s+(?:back\s+)?advance\s*[-:]\s*(.+)$/i,
                    /^payment\s+to\s+supplier\s*[-:]?\s*(.+)$/i,
                    /^supplier\s+(?:due|payment)\s*[-:]\s*(.+)$/i
                ]
                : accountType === 'partner'
                    ? [/\bpartner\s*:\s*(.+)$/i]
                    : accountType === 'employee'
                        ? [/\bemployee\s*:\s*(.+)$/i]
                        : [];

        for (const pattern of patterns) {
            const match = description.match(pattern);
            if (match?.[1]?.trim()) return match[1].trim();
        }
        return '';
    };

    const resolveEntityIdentity = (transaction, accountType, fallbackId) => {
        const fields = entityFields[accountType];
        if (!fields) {
            const fallbackName = String(transaction.reference_name || '').trim();
            const normalizedId = normalizeEntityId(fallbackId);
            return {
                id: normalizedId || '',
                name: fallbackName,
                key: normalizedId ? `id:${normalizedId}` : (fallbackName ? `name:${normalizeEntityName(fallbackName)}` : '')
            };
        }

        const directory = entityDirectoryRef.current[accountType] || [];
        const getRecordId = record => normalizeEntityId(firstPopulatedField(record, fields.recordIds));
        const getRecordName = record => String(firstPopulatedField(record, fields.recordNames) || '').trim();
        const findById = id => {
            const normalizedId = normalizeEntityId(id);
            return normalizedId
                ? directory.find(record => getRecordId(record) === normalizedId) || null
                : null;
        };
        const findUniqueByName = name => {
            const normalizedName = normalizeEntityName(name);
            if (!normalizedName) return null;
            const matches = directory.filter(record => normalizeEntityName(getRecordName(record)) === normalizedName);
            return matches.length === 1 ? matches[0] : null;
        };

        const embeddedName = extractEmbeddedEntityName(transaction, accountType);
        const dedicatedId = firstPopulatedField(transaction, fields.transactionIds);
        const dedicatedName = String(firstPopulatedField(transaction, fields.transactionNames) || '').trim();
        const referenceId = fallbackId ?? transaction.reference_id;
        const referenceName = String(transaction.reference_name || '').trim();
        const embeddedConflict = Boolean(
            embeddedName && referenceName &&
            normalizeEntityName(embeddedName) !== normalizeEntityName(referenceName)
        );

        // Generated invoice descriptions contain the actual party name. An exact,
        // unique directory match is authoritative when a joined reference is stale.
        const embeddedRecord = findUniqueByName(embeddedName);
        const dedicatedIdRecord = findById(dedicatedId);
        const dedicatedNameRecord = findUniqueByName(dedicatedName);
        // A valid foreign key is never discarded merely because free-text parsing
        // found a different phrase. If the description resolves to a real entity,
        // embeddedRecord still wins; otherwise the API reference remains authoritative.
        const referenceIdRecord = findById(referenceId);
        const referenceNameRecord = embeddedConflict ? null : findUniqueByName(referenceName);
        const resolvedRecord = embeddedRecord || dedicatedIdRecord || dedicatedNameRecord ||
            referenceIdRecord || referenceNameRecord;

        const resolvedId = resolvedRecord
            ? getRecordId(resolvedRecord)
            : normalizeEntityId(dedicatedId || referenceId);
        const resolvedName = resolvedRecord
            ? getRecordName(resolvedRecord)
            : (referenceName || dedicatedName || embeddedName);
        const key = resolvedId
            ? `id:${resolvedId}`
            : (resolvedName ? `name:${normalizeEntityName(resolvedName)}` : '');

        return {
            id: resolvedId,
            name: resolvedName,
            key,
            corrected: Boolean(embeddedRecord && embeddedConflict) || Boolean(
                resolvedRecord && referenceName &&
                normalizeEntityName(getRecordName(resolvedRecord)) !== normalizeEntityName(referenceName)
            )
        };
    };

    const sorted = [...data].sort((a, b) => {
        const d1 = new Date(a.created_at || a.transaction_date || 0).getTime() || 0;
        const d2 = new Date(b.created_at || b.transaction_date || 0).getTime() || 0;
        if (d1 !== d2) return d1 - d2;
        return toNumber(a.transaction_id || a.id) - toNumber(b.transaction_id || b.id);
    });

    // `mechanics_sell.paid_amount` is a current invoice total. After a later
    // customer receipt is allocated to a sale, the API updates that total, but
    // the receipt also exists as its own payment_received ledger row. For this
    // cash-flow screen we must display only the cash paid when the sale was
    // created, otherwise the later receipt is counted twice.
    const salePaidAtCreation = new Map();
    sorted.forEach((saleTx, saleIndex) => {
        if (normalizeType(saleTx.transaction_type) !== 'sale') return;
        const customerId = saleTx.customer_id ?? saleTx.customerid ?? saleTx.customerId ?? saleTx.reference_id;
        if (customerId === undefined || customerId === null || customerId === '') return;
        const currentSalePaid = toNumber(
            saleTx.amount_paid ?? saleTx.paid_amount ?? saleTx.sale_paid_amount ?? saleTx.paid
        );
        const laterReceipts = sorted.slice(saleIndex + 1).reduce((sum, candidate) => {
            const candidateType = normalizeType(candidate.transaction_type);
            const candidateCustomerId = candidate.customer_id ?? candidate.customerid ?? candidate.customerId ?? candidate.reference_id;
            if (candidateCustomerId == customerId &&
                (candidateType === 'payment_received' || candidateType === 'advance_received_customer')) {
                return sum + getTransactionAmount(candidate, candidateType);
            }
            return sum;
        }, 0);
        salePaidAtCreation.set(saleTx, Math.max(0, currentSalePaid - laterReceipts));
    });

    // mechanics_purchases.paid is also a current invoice total. Standalone
    // supplier payments are separate outflow rows, so subtract later FIFO
    // allocations when rendering the combined All Transactions cash flow.
    const purchasePaidAtCreation = new Map();
    sorted.forEach((purchaseTx, purchaseIndex) => {
        if (normalizeType(purchaseTx.transaction_type) !== 'purchase') return;
        const supplierId = purchaseTx.supplier_id ?? purchaseTx.supplierid ??
            purchaseTx.supplierId ?? purchaseTx.reference_id;
        if (supplierId === undefined || supplierId === null || supplierId === '') return;
        const currentPurchasePaid = toNumber(
            purchaseTx.amount_paid ?? purchaseTx.paid_amount ?? purchaseTx.paid
        );
        const laterPayments = sorted.slice(purchaseIndex + 1).reduce((sum, candidate) => {
            const candidateType = normalizeType(candidate.transaction_type);
            const candidateSupplierId = candidate.supplier_id ?? candidate.supplierid ??
                candidate.supplierId ?? candidate.reference_id;
            if (candidateSupplierId == supplierId && candidateType === 'payment_to_supplier') {
                return sum + getTransactionAmount(candidate, candidateType);
            }
            return sum;
        }, 0);
        purchasePaidAtCreation.set(
            purchaseTx,
            Math.max(0, currentPurchasePaid - laterPayments)
        );
    });

    const runningBalances = {};

    return sorted.map(tx => {
        const type = normalizeType(tx.transaction_type);
        const refType = normalizeReferenceType(tx.reference_type);
        const amount = getTransactionAmount(tx, type);
        const isPaymentTransaction = PAYMENT_TRANSACTION_TYPES.has(type);

        let accountType = null;
        let accountId = null;

        if (type === 'due' || type === 'payment_received' ||
            type === 'advance_received_customer' || type === 'advance_return_customer') {
            accountType = 'customer';
            accountId = tx.customer_id ?? tx.customerid ?? tx.customerId ?? tx.reference_id;
        } else if (type === 'supplier_due' || type === 'payment_to_supplier' ||
            type === 'advance_paid_supplier' || type === 'advance_return_supplier') {
            accountType = 'supplier';
            accountId = tx.supplier_id ?? tx.supplierid ?? tx.supplierId ?? tx.reference_id;
        } else if (type === 'partner_investment' || type === 'partner_payment') {
            accountType = 'partner';
            accountId = tx.partner_id ?? tx.reference_id;
        } else if (type === 'employee_salary' || type === 'salary_payment' ||
            type === 'employee_payment' || type === 'salary_advance') {
            accountType = 'employee';
            accountId = tx.employee_id ?? tx.employeeid ?? tx.employeeId ?? tx.reference_id;
        } else if (type === 'advance_received') {
            accountType = refType;
            accountId = refType === 'partner'
                ? (tx.partner_id ?? tx.reference_id)
                : refType === 'customer'
                    ? (tx.customer_id ?? tx.customerid ?? tx.customerId ?? tx.reference_id)
                    : refType === 'supplier'
                        ? (tx.supplier_id ?? tx.supplierid ?? tx.supplierId ?? tx.reference_id)
                        : tx.reference_id;
        } else {
            if (tx.reference_type) {
                accountType = normalizeReferenceType(tx.reference_type);
                accountId = accountType === 'customer'
                    ? (tx.customer_id ?? tx.customerid ?? tx.customerId ?? tx.reference_id)
                    : accountType === 'supplier'
                        ? (tx.supplier_id ?? tx.supplierid ?? tx.supplierId ?? tx.reference_id)
                        : tx.reference_id;
            } else if (tx.customer_id || type === 'sale' || type === 'return') {
                accountType = 'customer';
                accountId = tx.customer_id ?? tx.customerid ?? tx.customerId ?? tx.reference_id;
            } else if (tx.supplier_id) {
                accountType = 'supplier';
                accountId = tx.supplier_id;
            }
        }

        const resolvedIdentity = resolveEntityIdentity(tx, accountType, accountId);
        if (resolvedIdentity.id) accountId = resolvedIdentity.id;
        const accountKey = resolvedIdentity.key || (
            accountId !== undefined && accountId !== null && accountId !== ''
                ? `id:${normalizeEntityId(accountId)}`
                : ''
        );
        const hasAccount = Boolean(accountType && accountType !== '' && accountKey);

        // Sales and Purchases tabs are invoice registers, not combined party
        // cash-flow ledgers. Their Paid/Balance/Status values come from the
        // current source header, which later FIFO payments can update without
        // changing the historical transaction row.
       /*
 * Apply this rule inside processCascadingBalances(), immediately after
 * resolvedIdentity/accountKey/hasAccount are calculated and before the running
 * account-balance branch. It replaces the old `if (invoiceView && ...)` block.
 */
if (type === 'sale' || type === 'purchase') {
    const invoiceTotal = Math.max(0, toNumber(
        tx._invoice_total ?? tx.grand_total ?? tx.amount ?? tx.total
    ));
    const invoicePaid = Math.min(invoiceTotal, Math.max(0, toNumber(
        tx._invoice_paid ?? tx.paid_amount ?? tx.amount_paid ??
        tx.sale_paid_amount ?? tx.paid
    )));
    const invoiceBalance = Math.max(0, invoiceTotal - invoicePaid);
    const invoiceStatus = invoiceBalance <= 0.01
        ? 'full_paid'
        : (invoicePaid > 0.01 ? 'partial_paid' : 'unpaid');
    const previousBalance = Math.max(0, toNumber(tx.previous_balance));

    return {
        ...tx,
        reference_type: accountType || tx.reference_type,
        reference_id: resolvedIdentity.id ||
            (resolvedIdentity.corrected ? '' : tx.reference_id),
        reference_name: resolvedIdentity.name || tx.reference_name,
        grand_total: invoiceTotal,
        amount_paid: invoicePaid,
        paid_amount: invoicePaid,
        amount_due: invoiceBalance,
        balance: invoiceBalance,
        _display_previous_balance: previousBalance,
        _display_grand_total: invoiceTotal,
        _display_paid: invoicePaid,
        _display_balance: invoiceBalance,
        _transaction_amount: invoiceTotal,
        _is_payment_transaction: false,
        _resolved_reference_id: resolvedIdentity.id || '',
        _resolved_reference_name: resolvedIdentity.name || '',
        _reference_was_corrected: Boolean(resolvedIdentity.corrected),
        _original_reference_id: tx.reference_id,
        _original_reference_name: tx.reference_name,
        _is_cascaded: false,
        _previous_balance: previousBalance,
        status: invoiceStatus,
        payment_status: invoiceStatus
    };
}


        let previousBalance = 0;
        let grandTotal = 0;
        let paidTotal = 0;
        let finalBalance = 0;
        let paidAmount = 0;

        if (hasAccount) {
            const key = `${accountType}_${accountKey}`;

            if (runningBalances[key] !== undefined) {
                previousBalance = runningBalances[key];
            }

            // The transaction amount is the actual amount being recorded
            const transactionAmount = amount;

            // Bill-creating entries increase the account balance and can also
            // contain an immediate/partial payment. Sales must be included
            // here; otherwise their valid database amount_paid is overwritten
            // with zero by this display-normalization step.
            const supportsImmediatePayment = type === 'sale' ||
                type === 'due' ||
                type === 'supplier_due' ||
                type === 'purchase' ||
                RETURN_BILL_TYPES.has(type) ||
                type === 'partner_investment' ||
                type === 'employee_salary' ||
                type === 'expense';

            // A payment row has no bill amount: Previous + 0 Bill - Paid = Balance.
            grandTotal = isPaymentTransaction ? 0 : transactionAmount;
            paidAmount = isPaymentTransaction ? transactionAmount : 0;

            // Purchases and due-creating rows may contain an immediate partial payment.
            if (!isPaymentTransaction && supportsImmediatePayment) {
                // Read both transaction-table and source-sale naming variants.
                const specifiedPaid = type === 'sale' && salePaidAtCreation.has(tx)
                    ? salePaidAtCreation.get(tx)
                    : (type === 'purchase' && purchasePaidAtCreation.has(tx)
                        ? purchasePaidAtCreation.get(tx)
                        : toNumber(
                        tx.amount_paid ??
                        tx.paid_amount ??
                        tx.sale_paid_amount ??
                        tx.paid
                    ));
                if (specifiedPaid > 0 && specifiedPaid <= transactionAmount) {
                    paidAmount = specifiedPaid;
                } else {
                    // If no specific paid amount, assume it's a due (0 paid)
                    paidAmount = 0;
                }
            }
            paidTotal = paidAmount;

            // One consistent ledger equation for bills and payments.
            finalBalance = REVERSE_ADVANCE_TYPES.has(type)
                ? previousBalance + paidAmount
                : (RETURN_BILL_TYPES.has(type)
                    ? previousBalance - grandTotal + paidAmount
                    : previousBalance + grandTotal - paidAmount);

            runningBalances[key] = finalBalance;
        } else {
            grandTotal = isPaymentTransaction ? 0 : amount;
            paidAmount = isPaymentTransaction
                ? amount
                : (toNumber(tx.amount_paid ?? tx.paid_amount) || 0);
            paidTotal = paidAmount;
            finalBalance = REVERSE_ADVANCE_TYPES.has(type)
                ? paidTotal
                : (RETURN_BILL_TYPES.has(type) ? -grandTotal + paidTotal : grandTotal - paidTotal);
            previousBalance = 0;
        }

        const isAdvanceTransaction = type === 'advance_received' ||
            type === 'advance_received_customer' || type === 'advance_paid_supplier' ||
            type === 'advance_return_customer' || type === 'advance_return_supplier';
        const status = isAdvanceTransaction || RETURN_BILL_TYPES.has(type)
            ? 'completed'
            : (Math.abs(finalBalance) <= 0.01
                ? 'full_paid'
                : (finalBalance > 0 ? 'partial_paid' : 'unpaid'));

        // Old payment rows can contain a stale "credit kept" note calculated
        // before all customer dues were included. The running ledger is the
        // authoritative value, so render the note from its live negative
        // balance instead of the obsolete text stored in the description.
        const isCustomerReceipt = type === 'payment_received' && accountType === 'customer';
        const displayDescription = isCustomerReceipt && finalBalance < -0.005
            ? `${String(tx.description || '')
                .replace(/\s*\|\s*Customer credit kept for next sale:\s*Rs\.\s*[\d,.]+/i, '')
                .trim()} | Customer Credit kept for next sale: Rs. ${Math.abs(finalBalance).toFixed(2)}`
            : tx.description;

        return {
            ...tx,
            description: displayDescription,
            reference_type: accountType || tx.reference_type,
            reference_id: resolvedIdentity.id || (resolvedIdentity.corrected ? '' : tx.reference_id),
            reference_name: resolvedIdentity.name || tx.reference_name,
            grand_total: grandTotal,
            amount_paid: paidTotal,
            amount_due: finalBalance,
            balance: finalBalance,
            _display_previous_balance: previousBalance,
            _display_grand_total: grandTotal,
            _display_paid: paidTotal,
            _display_balance: finalBalance,
            _transaction_amount: amount,
            _is_payment_transaction: isPaymentTransaction,
            _resolved_reference_id: resolvedIdentity.id || '',
            _resolved_reference_name: resolvedIdentity.name || '',
            _reference_was_corrected: Boolean(resolvedIdentity.corrected),
            _original_reference_id: tx.reference_id,
            _original_reference_name: tx.reference_name,
            _is_cascaded: hasAccount,
            _previous_balance: previousBalance,
            status,
            payment_status: status
        };
    });
}, []);

    // References and transactions load in parallel. Re-run identity resolution as
    // soon as an entity directory arrives so stale joined names cannot persist.
    useEffect(() => {
        if (rawTransactionsRef.current.length === 0) return;
        const correctedTransactions = processCascadingBalances(rawTransactionsRef.current, {
            invoiceView: ['sales', 'purchases'].includes(activeTabRef.current)
        });
        setTransactions(correctedTransactions);
        setPagination(prev => ({ ...prev, total: correctedTransactions.length }));
    }, [customers, suppliers, employees, partners, processCascadingBalances]);

    const applySearchFilter = useCallback((data, search) => {
        if (!search?.trim()) return data;
        const term = search.toLowerCase().trim();
        return data.filter(tx => {
            const ref = (tx.reference_name || '').toLowerCase();
            const desc = (tx.description || '').toLowerCase();
            const inv = (tx.invoice_number || '').toLowerCase();
            return ref.includes(term) || desc.includes(term) || inv.includes(term);
        });
    }, []);

    const applyEntityFilter = useCallback((data, filterValues) => {
        if (!filterValues.entityType) return data;
        const selectedOption = entityOptions.find(option =>
            option.value === filterValues.entityId
        );
        return data.filter(transaction => transactionMatchesEntity(
            transaction,
            filterValues.entityType,
            filterValues.entityId,
            selectedOption?.label || ''
        ));
    }, [entityOptions]);

    useEffect(() => {
        const entityFiltered = activeTab === 'all'
            ? applyEntityFilter(transactions, filters)
            : transactions;
        const statusFiltered = ['sales', 'purchases'].includes(activeTab) && filters.status
            ? entityFiltered.filter(transaction =>
                String(transaction.status || transaction.payment_status || '').toLowerCase() ===
                String(filters.status).toLowerCase()
            )
            : entityFiltered;
        const nextFilteredData = applySearchFilter(statusFiltered, filters.search);
        setFilteredData(nextFilteredData);
        setPagination(prev => ({
            ...prev,
            total: nextFilteredData.length,
            page: Math.min(
                prev.page,
                Math.max(1, Math.ceil(nextFilteredData.length / prev.limit))
            )
        }));
    }, [transactions, filters, activeTab, applyEntityFilter, applySearchFilter]);

    const filteredSummary = useMemo(() => {
        const toNumber = (value) => {
            const parsed = Number.parseFloat(value);
            return Number.isFinite(parsed) ? parsed : 0;
        };

        const totals = filteredData.reduce((summary, transaction) => {
            const billAmount = transaction._display_grand_total !== undefined
                ? transaction._display_grand_total
                : (transaction.grand_total ?? transaction.amount);
            const paidAmount = transaction._display_paid !== undefined
                ? transaction._display_paid
                : (transaction.amount_paid ?? transaction.paid_amount ?? transaction.paid);

            summary.totalBill += toNumber(billAmount);
            summary.totalPaid += toNumber(paidAmount);
            return summary;
        }, { totalBill: 0, totalPaid: 0 });

        const totalBalance = totals.totalBill - totals.totalPaid;
        return {
            ...totals,
            totalBalance: Math.abs(totalBalance) < 0.005 ? 0 : totalBalance
        };
    }, [filteredData]);

    useEffect(() => {
        if (currentShop) {
            loadReferences();
            loadTransactions();
            loadProducts();
            loadPartners();
            loadPaymentAccounts();
        }
        return () => cancelRequests();
    }, [currentShop, loadReferences, loadTransactions, loadProducts, loadPartners, loadPaymentAccounts, cancelRequests]);

    useEffect(() => {
        if (currentShop) {
            const timer = setTimeout(() => loadTransactions(), 300);
            return () => clearTimeout(timer);
        }
    }, [filters.type, filters.status, filters.startDate, filters.endDate, filters.entityType, filters.entityId, activeTab, currentShop, loadTransactions]);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        setPagination(prev => ({ ...prev, page: 1 }));
        if (tab !== 'all') {
            setFilters(prev => ({ ...prev, type: '' }));
        }
    }, []);

    const resetFilters = useCallback(() => {
        setFilters({
            type: '',
            status: '',
            startDate: '',
            endDate: '',
            search: '',
            entityType: '',
            entityId: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const getReferenceOptions = useCallback(() => {
        const type = formData.type;
        let list = [];
        let label = '';

        if (type === 'customer_due' || type === 'customer_payment') {
            list = customers.map(c => ({ value: c.id || c.customerid, label: c.customer_name || c.name }));
            label = t('selectCustomer');
        } else if (type === 'supplier_due' || type === 'supplier_payment') {
            list = suppliers.map(s => ({ value: s.id || s.supplierid, label: s.supplier_name || s.name }));
            label = t('selectSupplier');
        } else if (type === 'partner_investment' || type === 'partner_payment') {
            list = partners.map(p => ({ value: p.id || p.partner_id, label: p.partner_name || p.name }));
            label = t('selectPartner');
        } else if (type === 'employee_salary') {
            list = employees.map(e => ({ value: e.id || e.employee_id, label: e.employee_name || e.name }));
            label = t('selectEmployee');
        }
        return { options: list, label };
    }, [formData.type, customers, suppliers, partners, employees, t]);

    const handleAddTransaction = useCallback(async () => {
        if (!currentShop) { showToast('error', 'Select a shop first'); return; }

        const actionMap = {
            customer_due: 'add_due',
            customer_payment: 'add_payment',
            supplier_due: 'add_supplier_due',
            supplier_payment: 'add_supplier_payment',
            partner_investment: 'add_partner_credit',
            partner_payment: 'add_partner_debit',
            employee_salary: 'add_employee_salary',
            expense: 'add_expense'
        };

        const refKeyMap = {
            customer_due: 'customer_id',
            customer_payment: 'customer_id',
            supplier_due: 'supplier_id',
            supplier_payment: 'supplier_id',
            partner_investment: 'partner_id',
            partner_payment: 'partner_id',
            employee_salary: 'employee_id'
        };

        const action = actionMap[formData.type];
        if (!action) { showToast('error', 'Invalid transaction type'); return; }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            showToast('error', 'Please enter a valid amount');
            return;
        }

        if (formData.type !== 'expense' && !formData.reference_id) {
            showToast('error', 'Please select a reference');
            return;
        }

        if (formData.type === 'expense' && !formData.expense_name) {
            showToast('error', 'Please enter expense name');
            return;
        }

        const paymentType = paymentMethodToType(formData.payment_method);
        if (paymentType !== 'cash' && !formData.payment_account_id) {
            showToast('error', `Select the ${paymentType === 'bank_account' ? 'bank account' : 'mobile-payment account'}`);
            return;
        }

        setLoading(true);
        try {
            const body = {
                action,
                shop_id: currentShop.shop_id,
                username: user?.username || 'system',
                transaction_date: formData.date,
                payment_method: paymentTypeToMethod(paymentType),
                payment_type: paymentType,
                payment_account_id: paymentType === 'cash' ? null : parseInt(formData.payment_account_id),
                account_id: paymentType === 'cash' ? null : parseInt(formData.payment_account_id),
                description: formData.description,
                amount: parseFloat(formData.amount)
            };

            if (refKeyMap[formData.type]) {
                body[refKeyMap[formData.type]] = parseInt(formData.reference_id);
            }

            if (formData.type === 'expense') {
                body.expense_name = formData.expense_name;
                body.expense_category = formData.expense_category || 'General';
            }

            if (formData.invoice_number) {
                body.invoice_number = formData.invoice_number;
            }

            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();

            if (data.status === 'success') {
                showToast('success', data.message || 'Transaction recorded successfully');
                setShowAddModal(false);
                resetForm();
                await loadTransactions();
                await loadProducts();
                await loadReferences();
                await loadPaymentAccounts();
            } else {
                showToast('error', data.message || 'Failed to add transaction');
            }
        } catch (err) {
            showToast('error', err.message || 'Failed to add transaction');
        } finally {
            setLoading(false);
        }
    }, [currentShop, formData, user, showToast, loadTransactions, loadProducts, loadReferences, loadPaymentAccounts]);

    const handleUpdateTransaction = useCallback(async () => {
        if (!currentShop || !editingTransaction) return;

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            showToast('error', 'Please enter a valid amount');
            return;
        }

        const linkedBillTypes = ['sale', 'purchase', 'return', 'refund', 'purchase_return', 'supplier_return'];
        const isLinkedBill = linkedBillTypes.includes(editingTransaction.transaction_type);
        const paidAmount = parseFloat(formData.amount_paid || 0);
        if (isLinkedBill && (paidAmount < 0 || paidAmount > parseFloat(formData.amount))) {
            showToast('error', 'Paid amount must be between zero and the bill amount');
            return;
        }

        const paymentType = paymentMethodToType(formData.payment_method);
        if (paymentType !== 'cash' && !formData.payment_account_id) {
            showToast('error', `Select the ${paymentType === 'bank_account' ? 'bank account' : 'mobile-payment account'}`);
            return;
        }

        setLoading(true);
        try {
            const body = {
                action: 'update_transaction',
                shop_id: currentShop.shop_id,
                transaction_id: editingTransaction.transaction_id,
                amount: parseFloat(formData.amount),
                transaction_date: formData.date,
                payment_method: paymentTypeToMethod(paymentType),
                payment_type: paymentType,
                payment_account_id: paymentType === 'cash' ? null : parseInt(formData.payment_account_id),
                account_id: paymentType === 'cash' ? null : parseInt(formData.payment_account_id),
                description: formData.description,
                invoice_number: formData.invoice_number,
                username: user?.username || 'system'
            };
            // Payments/dues derive their accounting columns automatically.
            // A source-backed bill exposes Paid so both the invoice and ledger
            // can be changed together without accepting a fake status value.
            if (isLinkedBill) body.amount_paid = paidAmount;

            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();

            if (data.status === 'success') {
                showToast('success', t('updateSuccess') || 'Updated successfully');
                setShowEditModal(false);
                setEditingTransaction(null);
                resetForm();
                await loadTransactions();
                await loadProducts();
                await loadReferences();
                await loadPaymentAccounts();
            } else {
                showToast('error', data.message || 'Failed to update');
            }
        } catch (err) {
            showToast('error', err.message || 'Failed to update');
        } finally {
            setLoading(false);
        }
    }, [currentShop, editingTransaction, formData, user, showToast, loadTransactions, loadProducts, loadReferences, loadPaymentAccounts, t]);

    const handleDeleteTransaction = useCallback(async () => {
        if (!currentShop || !selectedTransaction) return;

        setLoading(true);
        try {
            const res = await fetch(TRANSACTIONS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_transaction',
                    shop_id: currentShop.shop_id,
                    transaction_id: selectedTransaction.transaction_id,
                    username: user?.username || 'system'
                })
            });
            const data = await res.json();

            if (data.status === 'success') {
                showToast('success', t('deleteSuccess') || 'Deleted successfully');
                setShowDeleteModal(false);
                setSelectedTransaction(null);
                await loadTransactions();
                await loadProducts();
                await loadReferences();
                await loadPaymentAccounts();
            } else {
                showToast('error', data.message || 'Failed to delete');
            }
        } catch (err) {
            showToast('error', err.message || 'Failed to delete');
        } finally {
            setLoading(false);
        }
    }, [currentShop, selectedTransaction, user, showToast, loadTransactions, loadProducts, loadReferences, loadPaymentAccounts, t]);

    const resetForm = useCallback(() => {
        setFormData({
            type: 'customer_due',
            reference_id: '',
            amount: '',
            amount_paid: '',
            date: new Date().toISOString().split('T')[0],
        description: '',
        payment_method: 'cash',
        payment_account_id: '',
        invoice_number: '',
            expense_category: '',
            expense_name: '',
            status: 'completed'
        });
    }, []);

    const handleEdit = useCallback((tx) => {
        const paymentType = tx.payment_type || paymentMethodToType(tx.payment_method);
        setEditingTransaction(tx);
        setFormData({
            type: tx.transaction_type || 'customer_due',
            reference_id: tx.reference_id || '',
            amount: tx.grand_total || tx.amount || '',
            amount_paid: tx.amount_paid ?? 0,
            date: tx.transaction_date || new Date().toISOString().split('T')[0],
            description: tx.description || '',
            payment_method: paymentTypeToMethod(paymentType),
            payment_account_id: paymentType === 'cash' ? '' : String(tx.payment_account_id || tx.account_id || ''),
            invoice_number: tx.invoice_number || '',
            expense_category: '',
            expense_name: '',
            status: tx.status || 'completed'
        });
        setShowEditModal(true);
    }, []);

    const handleDelete = useCallback((tx) => {
        setSelectedTransaction(tx);
        setShowDeleteModal(true);
    }, []);

    const fetchPrintBusinessInfo = useCallback(async () => {
        const fallback = {
            businessname: user?.business_name || user?.fullname || currentShop?.shop_name || '',
            bussinesslogo: user?.bussinesslogo || '',
            bussinesslogo_url: user?.bussinesslogo_url || '',
            businessaddress: user?.business_address || '',
            phonenumber: user?.phonenumber || '',
            email: user?.email || '',
            gst_number: user?.gst_number || ''
        };
        if (!user?.username) return fallback;

        try {
            const response = await fetch(
                `${BUSINESS_INFO_PRINT_API}?action=get_business_info&username=${encodeURIComponent(user.username)}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    credentials: 'include'
                }
            );
            if (!response.ok) return fallback;
            const result = await response.json();
            return result.status === 'success' ? { ...fallback, ...result } : fallback;
        } catch (error) {
            console.warn('Could not refresh business information for print:', error);
            return fallback;
        }
    }, [user, currentShop]);

    const fetchSourceRecordForPrint = useCallback(async (kind, transaction, { allowFuzzy = false } = {}) => {
        if (!currentShop || !kind) return null;

        const config = {
            sale: {
                api: SALES_PRINT_API,
                listKeys: ['sales', 'records'],
                idFields: ['sale_id', 'sell_id', 'source_sale_id', 'linked_sale_id'],
                recordIdFields: ['id', 'sale_id', 'sell_id'],
                referenceFields: ['bill_number', 'bill_no', 'invoice_number']
            },
            purchase: {
                api: PURCHASES_PRINT_API,
                listKeys: ['purchases', 'records'],
                idFields: ['purchase_id', 'source_purchase_id', 'linked_purchase_id'],
                recordIdFields: ['id', 'purchase_id'],
                referenceFields: ['bill_no', 'bill_number', 'invoice_number']
            },
            return: {
                api: RETURNS_PRINT_API,
                listKeys: ['returns', 'records'],
                idFields: ['return_id', 'source_return_id', 'linked_return_id'],
                recordIdFields: ['id', 'return_id'],
                referenceFields: ['return_number', 'receipt_number']
            },
            purchase_return: {
                api: PURCHASE_RETURNS_PRINT_API,
                listKeys: ['returns', 'records'],
                idFields: ['return_id', 'source_id', 'source_return_id', 'linked_return_id'],
                recordIdFields: ['id', 'return_id'],
                referenceFields: ['return_number', 'receipt_number']
            }
        }[kind];
        if (!config) return null;

        const post = async (body) => {
            const response = await fetch(config.api, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...body,
                    username: user?.username || '',
                    shop_id: currentShop.shop_id
                })
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
        };

        const distinctRowId = transaction?.id !== undefined && transaction?.transaction_id !== undefined &&
            normalizeReceiptKey(transaction.id) !== normalizeReceiptKey(transaction.transaction_id)
            ? transaction.id
            : null;
        const sourceIds = [...new Set([
            ...config.idFields.map(field => transaction?.[field]),
            distinctRowId
        ]
            .filter(value => value !== undefined && value !== null && String(value).trim() !== '')
            .map(value => String(value).trim()))];

        for (const sourceId of sourceIds) {
            try {
                const result = await post({ action: 'get_record', id: sourceId });
                if (result.status === 'success' && result.data && !Array.isArray(result.data)) {
                    return result.data;
                }
            } catch (error) {
                console.warn(`Could not load ${kind} record ${sourceId}:`, error);
            }
        }

        const referenceCandidates = [
            extractTransactionInvoiceReference(transaction),
            transaction?.invoice_number,
            transaction?.bill_number,
            transaction?.bill_no,
            transaction?.return_number
        ].map(normalizeReceiptKey).filter(Boolean);
        const referenceKeys = [...new Set(referenceCandidates)];

        if (!allowFuzzy && sourceIds.length === 0 && referenceKeys.length === 0) return null;

        let records = [];
        try {
            const result = await post({ action: 'get_records', filters: {} });
            if (result.status !== 'success') return null;
            records = extractApiRecords(result, config.listKeys);
        } catch (error) {
            console.warn(`Could not search ${kind} records for printing:`, error);
            return null;
        }

        const exactMatch = records.find(record => {
            const recordIds = config.recordIdFields.map(field => normalizeReceiptKey(record?.[field])).filter(Boolean);
            const recordReferences = config.referenceFields.map(field => normalizeReceiptKey(record?.[field])).filter(Boolean);
            return sourceIds.some(id => recordIds.includes(normalizeReceiptKey(id))) ||
                referenceKeys.some(reference => recordReferences.includes(reference));
        });
        if (exactMatch) return exactMatch;
        if (!allowFuzzy) return null;

        const safeDateKey = (value) => {
            if (!value) return '';
            const date = new Date(value);
            return Number.isNaN(date.getTime())
                ? String(value).slice(0, 10)
                : date.toISOString().slice(0, 10);
        };
        const transactionDate = safeDateKey(
            transaction.transaction_date || transaction.date || transaction.created_at
        );
        const transactionAmount = Number.parseFloat(
            transaction._display_grand_total ?? transaction.grand_total ?? transaction.amount
        );
        const transactionParty = normalizeEntityName(transaction.reference_name);

        return records
            .map(record => {
                let score = 0;
                const recordDate = safeDateKey(record.date || record.transaction_date || record.created_at);
                const recordAmount = Number.parseFloat(record.grand_total ?? record.total ?? record.amount);
                const recordParty = normalizeEntityName(
                    record.customer_name || record.supplier_name || record.reference_name
                );
                if (transactionDate && recordDate === transactionDate) score += 1;
                if (Number.isFinite(transactionAmount) && Number.isFinite(recordAmount) &&
                    Math.abs(transactionAmount - recordAmount) < 0.01) score += 1;
                if (transactionParty && recordParty === transactionParty) score += 1;
                return { record, score };
            })
            .filter(candidate => candidate.score >= 2)
            .sort((a, b) => b.score - a.score)[0]?.record || null;
    }, [currentShop, user]);

    const handleTransactionPrint = useCallback(async (transaction) => {
        if (!transaction) return;
        if (!currentShop) {
            showToast('error', 'Please select a shop first');
            return;
        }

        // Open immediately from the click event so browsers do not block the receipt page.
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            showToast('error', 'Please allow popups to open the receipt page');
            return;
        }

        printWindow.document.write(`<!DOCTYPE html><html><head><title>Preparing receipt...</title></head>
            <body style="margin:0;padding:32px;font:14px Arial;color:#334155;text-align:center">
                <div style="font-size:28px;margin-bottom:12px">&#128424;</div>
                <strong>Preparing printable receipt page...</strong>
            </body></html>`);
        printWindow.document.close();

        const transactionType = String(transaction.transaction_type || '').toLowerCase();
        const directKind = ({
            sale: 'sale',
            sales: 'sale',
            purchase: 'purchase',
            supplier_purchase: 'purchase',
            return: 'return',
            sale_return: 'return',
            sales_return: 'return',
            customer_return: 'return',
            purchase_return: 'purchase_return',
            supplier_return: 'purchase_return'
        }[transactionType] || null);
        const linkedKind = directKind || ({
            due: 'sale',
            customer_due: 'sale',
            payment_received: 'sale',
            customer_payment: 'sale',
            advance_received: 'sale',
            supplier_due: 'purchase',
            payment_to_supplier: 'purchase',
            supplier_payment: 'purchase'
        }[transactionType] || null);

        try {
            const [businessInfo, sourceRecord] = await Promise.all([
                fetchPrintBusinessInfo(),
                linkedKind
                    ? fetchSourceRecordForPrint(linkedKind, transaction, { allowFuzzy: Boolean(directKind) })
                    : Promise.resolve(null)
            ]);

            if (printWindow.closed) return;
            if (directKind && !sourceRecord) {
                showToast('warning', `Linked ${directKind} record was not found; printing its transaction receipt instead`);
            }

            const receiptHtml = buildThermalReceiptHtml({
                kind: directKind && sourceRecord ? directKind : 'transaction',
                record: sourceRecord,
                transaction,
                paymentMethodDetails: resolveTransactionPaymentDetails(transaction, paymentAccounts),
                paymentAccounts,
                businessInfo,
                user,
                currentShop
            });

            printWindow.document.open();
            printWindow.document.write(receiptHtml);
            printWindow.document.close();
            printWindow.focus();
            showToast('success', 'Receipt page opened — choose a page size, then print when ready');
        } catch (error) {
            console.error('Transaction receipt page error:', error);
            if (!printWindow.closed) {
                printWindow.document.open();
                printWindow.document.write(`<!DOCTYPE html><html><body style="padding:28px;font:14px Arial;color:#991b1b">
                    <strong>Could not prepare this receipt.</strong><p>Please close this window and try again.</p>
                </body></html>`);
                printWindow.document.close();
            }
            showToast('error', 'Could not prepare the transaction receipt page');
        }
    }, [currentShop, user, paymentAccounts, fetchPrintBusinessInfo, fetchSourceRecordForPrint, showToast]);

    const typeOptions = useMemo(() => {
        const base = [
            { value: '', label: t('allTypes') || 'All Types' },
            { value: 'sale', label: t('sales') || 'Sales', icon: faShoppingCart, arrow: faArrowDown, arrowType: 'incoming' },
            { value: 'purchase', label: t('purchases') || 'Purchases', icon: faCartPlus, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'due', label: t('customerDue') || 'Customer Due', icon: faUsers, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'payment_received', label: t('customerPayment') || 'Customer Payment', icon: faUsers, arrow: faArrowDown, arrowType: 'incoming' },
            { value: 'advance_received_customer', label: 'Advance Received from Customer', icon: faHandHoldingUsd, arrow: faArrowDown, arrowType: 'incoming' },
            { value: 'advance_received', label: 'Advance Received (Legacy)', icon: faHandHoldingUsd, arrow: faArrowDown, arrowType: 'incoming' },
            { value: 'advance_return_customer', label: 'Advance Return to Customer', icon: faUndo, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'payment_to_supplier', label: t('supplierPayment') || 'Supplier Payment', icon: faTruck, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'supplier_due', label: t('supplierDue') || 'Supplier Due', icon: faTruck, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'advance_paid_supplier', label: 'Advance Paid to Supplier', icon: faTruck, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'advance_return_supplier', label: 'Advance Returned Back by Supplier', icon: faUndo, arrow: faArrowDown, arrowType: 'incoming' },
            { value: 'salary_payment', label: t('salaryPayment') || 'Salary Payment', icon: faUserTie, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'expense', label: t('shopExpense') || 'Shop Expense', icon: faReceipt, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'return', label: t('returns') || 'Return', icon: faUndo, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'partner_investment', label: t('partnerInvestment') || 'Partner Investment', icon: faHandshake, arrow: faArrowDown, arrowType: 'incoming' },
            { value: 'partner_payment', label: t('partnerPayment') || 'Partner Payment', icon: faHandshake, arrow: faArrowUp, arrowType: 'outgoing' },
            { value: 'employee_salary', label: t('employeeSalary') || 'Employee Salary', icon: faUserTie, arrow: faArrowUp, arrowType: 'outgoing' }
        ];
        return base;
    }, [t]);

    const statusOptions = useMemo(() => [
        { value: '', label: 'All Status' },
        { value: 'full_paid', label: 'Full Paid' },
        { value: 'partial_paid', label: 'Partial Paid' },
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'cancelled', label: 'Cancelled' }
    ], []);

    const paymentMethodOptions = useMemo(() => [
        { value: 'cash', label: t('cash') || 'Cash' },
        { value: 'bank_transfer', label: t('bankTransfer') || 'Bank Account' },
        { value: 'mobile_payment', label: t('mobilePayment') || 'Mobile Payment' }
    ], [t]);

    const selectedPaymentType = paymentMethodToType(formData.payment_method);
    const eligiblePaymentAccounts = useMemo(() => paymentAccounts.filter((account) =>
        Number(account.is_active) === 1 && account.account_type === selectedPaymentType
    ), [paymentAccounts, selectedPaymentType]);

    const getPaymentPresentation = useCallback(
        (transaction = {}) => resolveTransactionPaymentPresentation(transaction, paymentAccounts),
        [paymentAccounts]
    );

    const addTypeOptions = useMemo(() => [
        { value: 'customer_due', label: t('customerDue') || 'Customer Due' },
        { value: 'customer_payment', label: t('customerPayment') || 'Customer Payment' },
        { value: 'supplier_due', label: t('supplierDue') || 'Supplier Due' },
        { value: 'supplier_payment', label: t('supplierPayment') || 'Supplier Payment' },
        { value: 'partner_investment', label: t('partnerInvestment') || 'Partner Investment' },
        { value: 'partner_payment', label: t('partnerPayment') || 'Partner Payment' },
        { value: 'employee_salary', label: t('employeeSalary') || 'Employee Salary' },
        { value: 'expense', label: t('shopExpense') || 'Shop Expense' }
    ], [t]);

    const getStatusBadge = (status) => {
        const map = {
            'full_paid': { label: 'Full Paid', class: 'oztransui-status-full-paid' },
            'partial_paid': { label: 'Partial Paid', class: 'oztransui-status-partial-paid' },
            'unpaid': { label: 'Unpaid', class: 'oztransui-status-unpaid' },
            'completed': { label: 'Completed', class: 'oztransui-status-full-paid' },
            'paid': { label: 'Paid', class: 'oztransui-status-full-paid' },
            'pending': { label: 'Pending', class: 'oztransui-status-unpaid' },
            'cancelled': { label: 'Cancelled', class: 'oztransui-status-cancelled' }
        };
        return map[status] || { label: status, class: '' };
    };

    const openLedger = useCallback(() => {
        if (!currentShop) {
            showToast('error', 'Please select a shop first');
            return;
        }
        const params = new URLSearchParams({
            shop_id: currentShop.shop_id,
            action: 'view_ledger',
            username: user?.username || ''
        });
        if (filters.startDate) params.append('start_date', filters.startDate);
        if (filters.endDate) params.append('end_date', filters.endDate);
        if (filters.type) params.append('transaction_type', filters.type);
        if (filters.status) params.append('status', filters.status);
        if (filters.search) params.append('search', filters.search);
        if (activeTab === 'all' && filters.entityType) {
            params.append('entity_type', filters.entityType);
            if (filters.entityId) {
                params.append('entity_id', filters.entityId);
                if (selectedEntityOption?.label) {
                    params.append('entity_name', selectedEntityOption.label);
                }
            }
        }
        if (activeTab !== 'all') params.append('tab', activeTab);

        window.open(`/ledger?${params.toString()}`, '_blank');
    }, [currentShop, user, filters, activeTab, selectedEntityOption, showToast]);

    const hydratePdfPaymentBreakdowns = useCallback(async (reportRows = []) => {
        if (!currentShop?.shop_id || !reportRows.length) return reportRows;

        const normalizedType = row => String(row?.transaction_type || '').toLowerCase();
        const hasSales = reportRows.some(row => normalizedType(row) === 'sale');
        const hasPurchases = reportRows.some(row => normalizedType(row) === 'purchase');

        const fetchSourceRows = async (api, label) => {
            try {
                const response = await fetch(api, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        action: 'get_records',
                        shop_id: currentShop.shop_id,
                        username: user?.username || '',
                        include_items: false
                    })
                });
                const result = await response.json();
                if (!response.ok || result.status !== 'success') {
                    throw new Error(result.message || `${label} records could not be loaded`);
                }
                return Array.isArray(result.data)
                    ? result.data
                    : (Array.isArray(result.records) ? result.records : []);
            } catch (error) {
                console.warn(`Could not load ${label} payment breakdowns for PDF:`, error);
                return [];
            }
        };

        const [sales, purchases] = await Promise.all([
            hasSales ? fetchSourceRows(SALES_API, 'sale') : Promise.resolve([]),
            hasPurchases ? fetchSourceRows(PURCHASES_API, 'purchase') : Promise.resolve([])
        ]);

        const indexSources = (sourceRows, idFields, referenceFields) => {
            const byId = new Map();
            const byReference = new Map();
            sourceRows.forEach(source => {
                idFields.forEach(field => {
                    const value = source?.[field];
                    if (value !== undefined && value !== null && String(value).trim()) {
                        byId.set(String(value).trim(), source);
                    }
                });
                referenceFields.forEach(field => {
                    const key = String(source?.[field] || '').trim().toLowerCase();
                    if (key) byReference.set(key, source);
                });
            });
            return { byId, byReference };
        };

        const saleIndex = indexSources(sales, ['id', 'sale_id', 'sell_id'], ['bill_number', 'bn', 'invoice_number']);
        const purchaseIndex = indexSources(purchases, ['id', 'purchase_id'], ['bill_no', 'bill_number', 'invoice_number']);

        return reportRows.map(transaction => {
            const type = normalizedType(transaction);
            const isSale = type === 'sale';
            const isPurchase = type === 'purchase';
            if (!isSale && !isPurchase) return transaction;

            const sourceIsSameType = String(transaction.source_type || '').toLowerCase() === type;
            const sourceId = isSale
                ? (transaction.sale_id ?? transaction.sell_id ?? (sourceIsSameType ? transaction.source_id : null))
                : (transaction.purchase_id ?? (sourceIsSameType ? transaction.source_id : null));
            const reference = String(isSale
                ? (transaction.sale_bill_number ?? transaction.invoice_number ?? transaction.bill_number ?? transaction.bn ?? '')
                : (transaction.purchase_bill_number ?? transaction.invoice_number ?? transaction.bill_no ?? transaction.bill_number ?? '')
            ).trim().toLowerCase();
            const index = isSale ? saleIndex : purchaseIndex;
            const source = (sourceId !== undefined && sourceId !== null && sourceId !== ''
                ? index.byId.get(String(sourceId).trim())
                : null) || index.byReference.get(reference);
            if (!source) return transaction;

            return {
                ...transaction,
                payment_method: source.payment_method || transaction.payment_method,
                payment_breakdown: source.payment_breakdown ?? transaction.payment_breakdown
            };
        });
    }, [currentShop, user]);

    const exportTransactionsReport = useCallback(async () => {
        if (!filteredData.length) {
            showToast('warning', 'No transactions to export');
            return;
        }

        setPdfLoading(true);
        try {
            let reportData = filteredData;

            if (!['sales', 'purchases'].includes(activeTab)) {
                try {
                    const requestBody = {
                        action: 'get_transactions',
                        shop_id: currentShop?.shop_id,
                        limit: 100000,
                        offset: 0
                    };
                    if (filters.type) requestBody.transaction_type = filters.type;
                    if (filters.status) requestBody.status = filters.status;
                    if (filters.startDate) requestBody.start_date = filters.startDate;
                    if (filters.endDate) requestBody.end_date = filters.endDate;

                    const response = await fetch(TRANSACTIONS_API, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(requestBody)
                    });
                    const result = await response.json();

                    if (result.status === 'success') {
                        const processedReportData = processCascadingBalances(result.data || []);
                        reportData = applySearchFilter(
                            applyEntityFilter(processedReportData, filters),
                            filters.search
                        );
                    }
                } catch (error) {
                    console.warn('Could not reload all transactions for the report; using the current results:', error);
                }
            }

            // The full-report endpoint returns mechanics_transactions rows only.
            // Reattach the saved sale/purchase payment lines before building PDF
            // cells so Split (Cash + Wallet/Bank) is identical to the screen.
            reportData = await hydratePdfPaymentBreakdowns(reportData);

            if (!reportData.length) {
                showToast('warning', 'No transactions to export');
                return;
            }

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true
            });
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 12;
            const generatedAt = new Date();
            const reportId = `${generatedAt.toISOString().slice(0, 10).replace(/-/g, '')}-${String(generatedAt.getTime()).slice(-6)}`;
            const number = value => Number.parseFloat(value) || 0;
            const money = value => `Rs. ${number(value).toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;
            const themeRgb = (() => {
                const hex = String(currentThemeColor || '#10b981').replace('#', '');
                const normalized = hex.length === 3
                    ? hex.split('').map(character => character + character).join('')
                    : hex.padEnd(6, '0').slice(0, 6);
                return [0, 2, 4].map(index =>
                    parseInt(normalized.slice(index, index + 2), 16) || 0
                );
            })();

            const rows = reportData.map((transaction, index) => {
                const typeConfig = TRANSACTION_TYPES[transaction.transaction_type] || {};
                const methodDetails = getPaymentPresentation(transaction);
                const originalAmount = number(transaction.grand_total ?? transaction.amount);
                const originalPaid = number(transaction.amount_paid ?? transaction.paid_amount);
                const previousBalance = transaction._display_previous_balance !== undefined
                    ? transaction._display_previous_balance
                    : transaction._previous_balance || 0;
                const grandTotal = transaction._display_grand_total !== undefined
                    ? transaction._display_grand_total
                    : originalAmount;
                const paid = transaction._display_paid !== undefined
                    ? transaction._display_paid
                    : originalPaid;
                const balance = transaction._display_balance !== undefined
                    ? transaction._display_balance
                    : transaction.balance;
                return [
                    index + 1,
                    formatDate(transaction.transaction_date || transaction.date),
                    typeConfig.label || transaction.transaction_type || '-',
                    transaction.reference_name || '-',
                    transaction.description || '-',
                    methodDetails.pdfText,
                    money(previousBalance),
                    money(grandTotal),
                    money(paid),
                    money(balance)
                ];
            });

            const reportTotals = reportData.reduce((totals, transaction) => {
                const billAmount = number(
                    transaction._display_grand_total ?? transaction.grand_total ?? transaction.amount
                );
                const paidAmount = number(
                    transaction._display_paid ?? transaction.amount_paid ?? transaction.paid_amount
                );
                totals.grand += billAmount;
                totals.paid += paidAmount;
                totals.balance += billAmount - paidAmount;
                return totals;
            }, { grand: 0, paid: 0, balance: 0 });

            const activeFilters = [
                filters.entityType && `Entity: ${entityTypeOptions.find(option => option.value === filters.entityType)?.label || filters.entityType
                }${filters.entityId ? ` / ${selectedEntityOption?.label || filters.entityId}` : ''}`,
                filters.type && `Type: ${typeOptions.find(option => option.value === filters.type)?.label || filters.type}`,
                filters.status && `Status: ${filters.status}`,
                filters.startDate && `From: ${filters.startDate}`,
                filters.endDate && `To: ${filters.endDate}`,
                filters.search && `Search: "${filters.search}"`
            ].filter(Boolean).join('  |  ');

            const headerBottom = activeFilters ? 47 : 39;
            const drawReportHeader = () => {
                doc.setTextColor(107, 114, 128);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(7);
                doc.text('TRANSACTION REPORT', pageWidth / 2, 10, { align: 'center' });

                doc.setTextColor(31, 41, 55);
                doc.setFontSize(17);
                doc.text(currentShop?.shop_name || 'All Transactions', pageWidth / 2, 18, { align: 'center' });

                doc.setDrawColor(...themeRgb);
                doc.setLineWidth(0.8);
                doc.line(pageWidth / 2 - 10, 22, pageWidth / 2 + 10, 22);

                doc.setTextColor(107, 114, 128);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7);
                doc.text(
                    `Records: ${reportData.length}  |  Generated: ${generatedAt.toLocaleString('en-IN')}`,
                    pageWidth / 2,
                    27,
                    { align: 'center' }
                );

                doc.setDrawColor(229, 231, 235);
                doc.setLineWidth(0.2);
                doc.line(margin, 30, pageWidth - margin, 30);
                doc.line(margin, 35, pageWidth - margin, 35);
                doc.setFontSize(6.5);
                doc.text(`Report ID: #${reportId}`, margin, 33.2);
                doc.text(
                    `Generated by: ${user?.fullname || user?.username || 'System'}`,
                    pageWidth / 2,
                    33.2,
                    { align: 'center' }
                );
                doc.text(
                    `Shop Code: ${currentShop?.shop_code || 'N/A'}`,
                    pageWidth - margin,
                    33.2,
                    { align: 'right' }
                );

                if (activeFilters) {
                    doc.setFillColor(249, 250, 251);
                    doc.setDrawColor(...themeRgb);
                    doc.rect(margin, 38, pageWidth - margin * 2, 7, 'F');
                    doc.setLineWidth(0.8);
                    doc.line(margin, 38, margin, 45);
                    doc.setTextColor(75, 85, 99);
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(6.5);
                    doc.text(`FILTERS APPLIED: ${activeFilters}`, margin + 3, 42.5, {
                        maxWidth: pageWidth - margin * 2 - 6
                    });
                }
            };

            autoTable(doc, {
                head: [[
                    'S.No', 'Date', 'Type', 'Reference', 'Description', 'Method',
                    'Previous Dues', 'Grand Total', 'Paid', 'Balance'
                ]],
                body: rows,
                startY: headerBottom,
                margin: { top: headerBottom, right: margin, bottom: 14, left: margin },
                theme: 'grid',
                showHead: 'everyPage',
                styles: {
                    font: 'helvetica',
                    fontSize: 7,
                    cellPadding: 1.6,
                    lineColor: [229, 231, 235],
                    lineWidth: 0.15,
                    textColor: [31, 41, 55],
                    overflow: 'linebreak',
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: themeRgb,
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    halign: 'center',
                    minCellHeight: 8
                },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                columnStyles: {
                    0: { cellWidth: 9, halign: 'center' },
                    1: { cellWidth: 23, halign: 'center' },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 32 },
                    4: { cellWidth: 'auto' },
                    5: { cellWidth: 40 },
                    6: { cellWidth: 27, halign: 'center' },
                    7: { cellWidth: 24, halign: 'center' },
                    8: { cellWidth: 24, halign: 'center' },
                    9: { cellWidth: 24, halign: 'center' }
                },
                didDrawPage: ({ pageNumber }) => {
                    drawReportHeader();
                    const pageHeight = doc.internal.pageSize.getHeight();
                    doc.setDrawColor(229, 231, 235);
                    doc.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
                    doc.setTextColor(107, 114, 128);
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(7);
                    doc.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
                }
            });

            // Draw the totals independently from AutoTable so the summary has
            // its own equal-width layout instead of inheriting table columns.
            const summaryGap = 3;
            const summaryHeight = 14;
            const pageHeight = doc.internal.pageSize.getHeight();
            const summaryBottomLimit = pageHeight - 14;
            let summaryY = (doc.lastAutoTable?.finalY || headerBottom) + summaryGap;

            if (summaryY + summaryHeight > summaryBottomLimit) {
                doc.addPage();
                drawReportHeader();
                summaryY = headerBottom;
            }

            // Match the four financial columns on the table's right side:
            // Previous Dues (totals label), Grand Total, Paid and Balance.
            const summarySectionWidths = [27, 24, 24, 24];
            const summaryWidth = summarySectionWidths.reduce((sum, width) => sum + width, 0);
            const summaryX = pageWidth - margin - summaryWidth;
            const summaryItems = [
                { label: 'REPORT TOTALS', value: '', titleOnly: true },
                { label: 'TOTAL PAYMENT', value: money(reportTotals.grand) },
                { label: 'PAID', value: money(reportTotals.paid) },
                { label: 'BALANCE', value: money(reportTotals.balance) }
            ];

            doc.setFillColor(249, 250, 251);
            doc.setDrawColor(229, 231, 235);
            doc.setLineWidth(0.2);
            doc.rect(summaryX, summaryY, summaryWidth, summaryHeight, 'FD');

            // A restrained theme rule visually closes the table without turning
            // the totals into a separate oversized card.
            doc.setDrawColor(...themeRgb);
            doc.setLineWidth(0.7);
            doc.line(summaryX, summaryY, summaryX + summaryWidth, summaryY);

            let sectionX = summaryX;
            summaryItems.forEach((item, index) => {
                const sectionWidth = summarySectionWidths[index];
                const centerX = sectionX + sectionWidth / 2;

                if (index > 0) {
                    doc.setDrawColor(229, 231, 235);
                    doc.setLineWidth(0.2);
                    doc.line(sectionX, summaryY + 2.5, sectionX, summaryY + summaryHeight - 2.5);
                }

                if (item.titleOnly) {
                    doc.setTextColor(71, 85, 105);
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(7.5);
                    doc.text(item.label, centerX, summaryY + 8.3, { align: 'center' });
                } else {
                    doc.setTextColor(100, 116, 139);
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(6.5);
                    doc.text(item.label, centerX, summaryY + 4.7, { align: 'center' });

                    doc.setTextColor(15, 23, 42);
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(10);
                    const availableTextWidth = sectionWidth - 3;
                    const measuredTextWidth = doc.getTextWidth(item.value) || availableTextWidth;
                    const fittedFontSize = Math.max(7.5, Math.min(10, 10 * availableTextWidth / measuredTextWidth));
                    doc.setFontSize(fittedFontSize);
                    doc.text(item.value, centerX, summaryY + 10.5, { align: 'center' });
                }

                sectionX += sectionWidth;
            });

            const finalPageCount = doc.internal.getNumberOfPages();
            for (let page = 1; page <= finalPageCount; page += 1) {
                doc.setPage(page);
                const pageHeight = doc.internal.pageSize.getHeight();
                doc.setFillColor(255, 255, 255);
                doc.rect(0, pageHeight - 11, pageWidth, 11, 'F');
                doc.setDrawColor(229, 231, 235);
                doc.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
                doc.setTextColor(107, 114, 128);
                doc.setFontSize(7);
                doc.text(`Page ${page} of ${finalPageCount}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
            }

            const safeShopName = (currentShop?.shop_name || 'All_Transactions')
                .replace(/[^a-z0-9_-]/gi, '_');
            doc.save(`Transaction_Report_${safeShopName}_${generatedAt.toISOString().slice(0, 10)}.pdf`);
            showToast('success', `Transaction report generated with ${reportData.length} records`);
        } catch (error) {
            console.error('Transaction PDF error:', error);
            showToast('error', 'Error generating transaction report');
        } finally {
            setPdfLoading(false);
        }
    }, [filteredData, filters, activeTab, currentShop, user, currentThemeColor, showToast, typeOptions, entityTypeOptions, selectedEntityOption, processCascadingBalances, applyEntityFilter, applySearchFilter, getPaymentPresentation, hydratePdfPaymentBreakdowns]);

    const renderTabs = () => {
        const tabs = [
            { id: 'all', label: t('allTransactions'), icon: faExchangeAlt, count: filteredData.length },
          
            { id: 'sales', label: t('sales'), icon: faShoppingCart, count: filteredData.filter(t => t.transaction_type === 'sale').length },
            { id: 'purchases', label: t('purchases'), icon: faCartPlus, count: filteredData.filter(t => t.transaction_type === 'purchase').length },
            { id: 'returns', label: t('returns'), icon: faUndo, count: filteredData.filter(t => ['return', 'purchase_return', 'supplier_return'].includes(t.transaction_type)).length },
            { id: 'salary', label: t('salaries'), icon: faUserTie, count: filteredData.filter(t => t.transaction_type === 'salary_payment' || t.transaction_type === 'employee_salary').length },
            { id: 'expense', label: t('expenses'), icon: faReceipt, count: filteredData.filter(t => t.transaction_type === 'expense').length },
            { id: 'partners', label: t('partners'), icon: faHandshake, count: filteredData.filter(t => t.transaction_type === 'partner_investment' || t.transaction_type === 'partner_payment').length },
            { id: 'profit_loss', label: t('profitLoss'), icon: faBalanceScale, count: null }
        ];

        return (
            <div className="oztransui-tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`oztransui-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                    >
                        <FontAwesomeIcon icon={tab.icon} />
                        <span className="oztransui-tab-label">{tab.label}</span>
                        {tab.count !== null && tab.count !== undefined && (
                            <span className="oztransui-tab-count">{tab.count}</span>
                        )}
                    </button>
                ))}
            </div>
        );
    };

    const renderTransactionSummary = () => (
        <>
            <style>{`
                .oztransui-filter-summary {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                    gap: 12px;
                    margin: 16px 0;
                }

                .oztransui-filter-summary-card {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    min-width: 0;
                    min-height: 86px;
                    padding: 15px 17px;
                    overflow: hidden;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                   
                    box-shadow: 0 3px 12px rgba(15, 23, 42, 0.06);
                }

                .oztransui-filter-summary-card::before {
                    content: '';
                    position: absolute;
                    inset: 0 auto 0 0;
                    width: 4px;
                    background: currentColor;
                }

                .oztransui-filter-summary-card.bill { color: #2563eb; }
                .oztransui-filter-summary-card.paid { color: #16a34a; }
                .oztransui-filter-summary-card.dues { color: #dc2626; }

                .oztransui-filter-summary-icon {
                    display: inline-flex;
                    flex: 0 0 42px;
                    align-items: center;
                    justify-content: center;
                    width: 42px;
                    height: 42px;
                    font-size: 17px;
                    background: currentColor;
                    border-radius: 11px;
                }

                .oztransui-filter-summary-icon svg { color: #ffffff; }

                .oztransui-filter-summary-content {
                    min-width: 0;
                }

                .oztransui-filter-summary-label {
                    display: block;
                    margin-bottom: 4px;
                    color: #64748b;
                    font-size: 12px;
                    font-weight: 700;
                    line-height: 1.25;
                    letter-spacing: 0.035em;
                    text-transform: uppercase;
                }

                .oztransui-filter-summary-value {
                    display: block;
                    color: #0f172a;
                    font-size: clamp(18px, 2vw, 23px);
                    font-weight: 800;
                    line-height: 1.2;
                    overflow-wrap: anywhere;
                }

                .oztransui-filter-summary-card.dues .oztransui-filter-summary-value {
                    color: #dc2626;
                }

                .oztransui-dark .oztransui-filter-summary-card {
                    background: #111827;
                    border-color: #374151;
                    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.2);
                }

                .oztransui-dark .oztransui-filter-summary-label { color: #94a3b8; }
                .oztransui-dark .oztransui-filter-summary-value { color: #f8fafc; }
                .oztransui-dark .oztransui-filter-summary-card.dues .oztransui-filter-summary-value { color: #f87171; }

                .oztransui-printable-row {
                    cursor: pointer;
                }

                .oztransui-printable-row > td {
                    transition: background-color 160ms ease, box-shadow 160ms ease;
                }

                .oztransui-printable-row:hover > td {
                    background-color: rgba(37, 99, 235, 0.055);
                }

                .oztransui-printable-row:focus-visible {
                    outline: 2px solid #2563eb;
                    outline-offset: -2px;
                }

                .oztransui-dark .oztransui-printable-row:hover > td {
                    background-color: rgba(96, 165, 250, 0.09);
                }

                .oztransui-method-cell {
                    min-width: 170px;
                    vertical-align: middle;
                }

                .oztransui-method-details {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 3px;
                    min-width: 0;
                    line-height: 1.25;
                }

              

                .oztransui-method-line {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 2px;
                    min-width: 0;
                }

                .oztransui-method-split .oztransui-method-line {
                    width: 100%;
                    padding: 5px 0 2px;
                }

                .oztransui-method-split .oztransui-method-line + .oztransui-method-line {
                    border-top: 1px dashed #d1d5db;
                }

                .oztransui-method-heading {
                    display: block;
                    max-width: 100%;
                    color: #1f2937;
                    font-size: 13px;
                    overflow-wrap: anywhere;
                }

                .oztransui-method-heading strong {
                    color: var(--oztransui-primary, #10b981);
                    font-weight: 750;
                }

                .oztransui-method-number {
                    color: #64748b;
                    font-size: 12px;
                    font-variant-numeric: tabular-nums;
                    letter-spacing: 0.015em;
                    overflow-wrap: anywhere;
                }

                .oztransui-method-cash .oztransui-method-heading strong {
                    color: #0f766e;
                }

                .oztransui-dark .oztransui-method-heading {
                    color: #e5e7eb;
                }

                .oztransui-dark .oztransui-method-number {
                    color: #94a3b8;
                }

             

                .oztransui-dark .oztransui-method-split .oztransui-method-line + .oztransui-method-line {
                    border-color: #475569;
                }

                .oztransui-dark .oztransui-method-cash .oztransui-method-heading strong {
                    color: #5eead4;
                }

                .oztransui-transaction-print-btn {
                    color: #0f766e !important;
                }

                .oztransui-transaction-print-btn:hover {
                    color: #ffffff !important;
                    background: #0f766e !important;
                }

                @media (max-width: 520px) {
                    .oztransui-filter-summary {
                        grid-template-columns: minmax(0, 1fr);
                        gap: 10px;
                        margin: 12px 0;
                    }

                    .oztransui-filter-summary-card {
                        min-height: 78px;
                        padding: 13px 15px;
                    }

                    .oztransui-filter-summary-icon {
                        flex-basis: 38px;
                        width: 38px;
                        height: 38px;
                    }

                    .oztransui-method-cell {
                        min-width: 150px;
                    }
                }
            `}</style>
            <section className="oztransui-filter-summary" aria-label="Filtered transaction summary">
                <article className="oztransui-filter-summary-card bill">
                    <span className="oztransui-filter-summary-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faArrowUp} />
                    </span>
                    <div className="oztransui-filter-summary-content">
                        <span className="oztransui-filter-summary-label">Total Bill Amount</span>
                        <strong className="oztransui-filter-summary-value">
                            Rs. {formatCurrency(filteredSummary.totalBill)}
                        </strong>
                    </div>
                </article>

                <article className="oztransui-filter-summary-card paid">
                    <span className="oztransui-filter-summary-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faArrowDown} />
                    </span>
                    <div className="oztransui-filter-summary-content">
                        <span className="oztransui-filter-summary-label">Total Paid</span>
                        <strong className="oztransui-filter-summary-value">
                            Rs. {formatCurrency(filteredSummary.totalPaid)}
                        </strong>
                    </div>
                </article>

                <article className="oztransui-filter-summary-card dues">
                    <span className="oztransui-filter-summary-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faWallet} />
                    </span>
                    <div className="oztransui-filter-summary-content">
                        <span className="oztransui-filter-summary-label">
                            {filteredSummary.totalBalance < -0.005
                                ? 'Customer Credit (kept for next sale)'
                                : 'Total Balance (Dues)'}
                        </span>
                        <strong className="oztransui-filter-summary-value">
                            {filteredSummary.totalBalance < -0.005 ? '' : 'Rs. '}
                            {filteredSummary.totalBalance < -0.005
                                ? `Rs. ${formatCurrency(Math.abs(filteredSummary.totalBalance))}`
                                : formatCurrency(filteredSummary.totalBalance)}
                        </strong>
                    </div>
                </article>
            </section>
        </>
    );

    const renderTable = () => {
        const currentData = filteredData.slice(
            (pagination.page - 1) * pagination.limit,
            pagination.page * pagination.limit
        );

        if (loading && currentData.length === 0) {
            return (
                <div className="oztransui-loading-state">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>{t('loading')}</span>
                </div>
            );
        }

        if (currentData.length === 0) {
            return (
                <div className="oztransui-empty-state">
                    <FontAwesomeIcon icon={faReceipt} />
                    <p>{t('noTransactions')}</p>
                </div>
            );
        }

        return (
            <div className="oztransui-table-wrapper">
                <table className="oztransui-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Reference</th>
                            <th>Description</th>
                            <th>Method</th>
                            <th className="oztransui-amount-cell">Previous Dues</th>
                            <th className="oztransui-amount-cell">Bill Amount</th>
                            <th className="oztransui-amount-cell">Paid</th>
                            <th className="oztransui-amount-cell">Balance</th>
                            <th className="oztransui-actions-cell">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.map((tx, index) => {
                            const typeConfig = TRANSACTION_TYPES[tx.transaction_type] || {
                                label: tx.transaction_type,
                                class: 'type-default'
                            };
                            const statusInfo = getStatusBadge(tx.status || tx.payment_status);
                            const balance = tx._display_balance !== undefined ? tx._display_balance : tx.balance;
                            const previousBalance = tx._display_previous_balance !== undefined ? tx._display_previous_balance : tx._previous_balance || 0;
                            const originalAmount = parseFloat(tx.grand_total ?? tx.amount ?? 0);
                            const paid = parseFloat(tx.amount_paid ?? tx.paid_amount ?? 0);
                            const displayGrandTotal = tx._display_grand_total !== undefined
                                ? tx._display_grand_total
                                : originalAmount;
                            const displayPaid = tx._display_paid !== undefined
                                ? tx._display_paid
                                : paid;
                            const displayBalance = tx._display_balance !== undefined
                                ? tx._display_balance
                                : balance;
                            const displayPreviousBalance = previousBalance;
                            const paymentPresentation = getPaymentPresentation(tx);
                            const isPurchaseReturnLedger = ['purchase_return', 'supplier_return']
                                .includes(String(tx.transaction_type || '').toLowerCase());

                            return (
                                <tr
                                    key={tx.transaction_id || tx.id}
                                    className="oztransui-printable-row"
                                    role="button"
                                    tabIndex={0}
                                    title="Click to open this transaction receipt"
                                    onClick={() => handleTransactionPrint(tx)}
                                    onKeyDown={(event) => {
                                        if (event.target !== event.currentTarget) return;
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            handleTransactionPrint(tx);
                                        }
                                    }}
                                >
                                    <td className="oztransui-id-cell">
                                        {index + 1 + (pagination.page - 1) * pagination.limit}
                                    </td>
                                    <td>{formatDate(tx.transaction_date || tx.date)}</td>
                                    <td>
                                        <div className="oztransui-type-badge-wrapper">
                                            {typeConfig.arrow && (
                                                <FontAwesomeIcon
                                                    icon={typeConfig.arrow}
                                                    className={`oztransui-type-arrow ${typeConfig.arrowType === 'incoming' ? 'oztransui-incoming' : 'oztransui-outgoing'}`}
                                                />
                                            )}
                                            <span className={`oztransui-type-badge ${typeConfig.class}`}>
                                                {typeConfig.label}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        {tx.reference_name || tx._resolved_reference_name ? (
                                            <span className="oztransui-reference-name">
                                                {tx.reference_name || tx._resolved_reference_name}
                                            </span>
                                        ) : (
                                            <span className="oztransui-reference-name">-</span>
                                        )}
                                    </td>
                                    <td className="oztransui-desc-cell">
                                        <span className="oztransui-desc-text">{tx.description || '-'}</span>
                                    </td>
                                    <td className="oztransui-method-cell">
                                        <div className={`oztransui-method-details ${paymentPresentation.isSplit ? 'oztransui-method-split' : ''}`}>
                                            {/* {paymentPresentation.isSplit && (
                                                <strong className="oztransui-split-label">{paymentPresentation.label}</strong>
                                            )} */}
                                            {paymentPresentation.lines.map((line, lineIndex) => (
                                                <div className={`oztransui-method-line oztransui-method-${line.paymentType}`} key={`${line.paymentType}-${line.accountNumber || lineIndex}`}>
                                                    <span className="oztransui-method-heading">
                                                        <strong>{line.provider}</strong>
                                                        {line.accountTitle && <span> - {line.accountTitle}</span>}
                                                    </span>
                                                    {line.accountNumber && (
                                                        <span className="oztransui-method-number">{line.accountNumber}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="oztransui-amount-cell">
                                        Rs. {formatCurrency(displayPreviousBalance)}
                                    </td>
                                    <td className="oztransui-amount-cell">
                                        Rs. {formatCurrency(displayGrandTotal)}
                                    </td>
                                    <td className="oztransui-amount-cell">
                                        Rs. {formatCurrency(displayPaid)}
                                    </td>
                                    <td className="oztransui-amount-cell oztransui-balance-cell">
                                        <span className={displayBalance > 0.01 ? 'oztransui-has-balance' : displayBalance < -0.01 ? 'oztransui-negative' : 'oztransui-zero-balance'}>
                                            Rs. {formatCurrency(displayBalance)}
                                        </span>
                                    </td>
                                    <td className="oztransui-actions-cell">
                                        <div className="oztransui-actions-group" onClick={(event) => event.stopPropagation()}>
                                            <button
                                                className="oztransui-action-btn oztransui-transaction-print-btn"
                                                onClick={() => handleTransactionPrint(tx)}
                                                title="Open printable receipt"
                                            >
                                                <FontAwesomeIcon icon={faPrint} />
                                            </button>
                                            {!isPurchaseReturnLedger && (
                                                <button
                                                    className="oztransui-action-btn oztransui-edit-btn"
                                                    onClick={() => handleEdit(tx)}
                                                    title="Edit"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                            )}
                                            {!isPurchaseReturnLedger && (
                                                <button
                                                    className="oztransui-action-btn oztransui-delete-btn"
                                                    onClick={() => handleDelete(tx)}
                                                    title="Delete"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderProfitLoss = () => {
        return (
            <div className="oztransui-profit-loss-wrapper">
                <ProfitLossStatement
                    data={transactions}
                    products={productsData}
                    partners={partners}
                    translations={t}
                    formatCurrencyFn={formatCurrency}
                    shopId={currentShop?.shop_id}
                    t={t}
                />
            </div>
        );
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(filteredData.length / pagination.limit);
        if (totalPages <= 1) return null;

        return (
            <div className="oztransui-pagination">
                <div className="oztransui-pagination-info">
                    {t('showing')} {(pagination.page - 1) * pagination.limit + 1} - {Math.min(pagination.page * pagination.limit, filteredData.length)} {t('of')} {filteredData.length}
                </div>
                <div className="oztransui-pagination-controls">
                    <button
                        disabled={pagination.page === 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} /> {t('previous')}
                    </button>
                    <span className="oztransui-page-info">{t('page')} {pagination.page} {t('of')} {totalPages}</span>
                    <button
                        disabled={pagination.page === totalPages}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                        {t('next')} <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>
        );
    };

    const renderModal = (isEdit = false) => {
        const refInfo = getReferenceOptions();
        const isExpense = formData.type === 'expense';
        const showReference = !isExpense;
        const isBillEdit = isEdit && ['sale', 'purchase', 'return', 'refund', 'purchase_return', 'supplier_return'].includes(editingTransaction?.transaction_type);
        const calculatedBalance = Math.max(
            0,
            (parseFloat(formData.amount) || 0) - (parseFloat(formData.amount_paid) || 0)
        );

        return (
            <div className="oztransui-modal-overlay" onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
            }}>
                <div className="oztransui-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="oztransui-modal-header">
                        <h2>
                            <FontAwesomeIcon icon={isEdit ? faEdit : faPlus} />
                            {isEdit ? t('edit') || 'Edit Transaction' : t('addTransaction')}
                        </h2>
                        <button className="oztransui-modal-close" onClick={() => {
                            setShowAddModal(false);
                            setShowEditModal(false);
                            resetForm();
                        }}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div className="oztransui-modal-body">


                        {isExpense && (
                            <div className="oztransui-form-row">
                                <div className="oztransui-form-group">
                                    <label>{t('expenseName') || 'Expense Name'}</label>
                                    <input
                                        type="text"
                                        value={formData.expense_name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, expense_name: e.target.value }))}
                                        placeholder="Enter expense name"
                                    />
                                </div>
                                <div className="oztransui-form-group">
                                    <label>{t('expenseCategory') || 'Category'}</label>
                                    <input
                                        type="text"
                                        value={formData.expense_category}
                                        onChange={(e) => setFormData(prev => ({ ...prev, expense_category: e.target.value }))}
                                        placeholder="e.g., Rent, Utilities"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="oztransui-form-row">
                            <div className="oztransui-form-group">
                                <label>{t('amount')}</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.amount}
                                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="oztransui-form-group">
                                <label>{t('date')}</label>
                                <input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                />
                            </div>
                        </div>

                        {isBillEdit && (
                            <div className="oztransui-form-row">
                                <div className="oztransui-form-group">
                                    <label>Paid Amount</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max={formData.amount || undefined}
                                        step="0.01"
                                        value={formData.amount_paid}
                                        onChange={(e) => setFormData(prev => ({ ...prev, amount_paid: e.target.value }))}
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="oztransui-form-group">
                                    <label>Balance (auto-calculated)</label>
                                    <input
                                        type="text"
                                        value={`Rs. ${calculatedBalance.toFixed(2)}`}
                                        readOnly
                                        aria-readonly="true"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="oztransui-form-row">
                            <div className="oztransui-form-group">
                                <label>{t('paymentMethod')}</label>
                                <select
                                    value={formData.payment_method}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        payment_method: e.target.value,
                                        payment_account_id: ''
                                    }))}
                                >
                                    {paymentMethodOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            {selectedPaymentType !== 'cash' && (
                                <div className="oztransui-form-group">
                                    <label>{selectedPaymentType === 'bank_account' ? 'Bank Account' : 'Mobile Payment Account'}</label>
                                    <select
                                        value={formData.payment_account_id}
                                        onChange={(e) => setFormData(prev => ({ ...prev, payment_account_id: e.target.value }))}
                                        required
                                    >
                                        <option value="">Select account</option>
                                        {eligiblePaymentAccounts.map(account => (
                                            <option key={account.account_id} value={account.account_id}>
                                                {account.provider_name} — {account.account_title} ({account.account_number}) · Rs. {Number(account.current_balance || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </option>
                                        ))}
                                    </select>
                                    {eligiblePaymentAccounts.length === 0 && <small className="oztransui-field-warning">No active account is configured for this payment method.</small>}
                                </div>
                            )}
                            <div className="oztransui-form-group">
                                <label>{t('invoiceNumber')} <span className="oztransui-optional">({t('optional')})</span></label>
                                <input
                                    type="text"
                                    value={formData.invoice_number}
                                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                                    placeholder="INV-001"
                                />
                            </div>
                        </div>

                        <div className="oztransui-form-row">
                            <div className="oztransui-form-group oztransui-full-width">
                                <label>{t('description')}</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    rows="2"
                                    placeholder="Enter description..."
                                />
                            </div>
                        </div>
                    </div>

                    <div className="oztransui-modal-footer">
                        <button className="oztransui-btn-secondary" onClick={() => {
                            setShowAddModal(false);
                            setShowEditModal(false);
                            resetForm();
                        }}>
                            {t('cancel')}
                        </button>
                        <button
                            className="oztransui-btn-primary"
                            onClick={isEdit ? handleUpdateTransaction : handleAddTransaction}
                            disabled={loading}
                        >
                            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
                            {t('save')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderDeleteModal = () => (
        <div className="oztransui-modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="oztransui-modal oztransui-modal-sm" onClick={(e) => e.stopPropagation()}>
                <div className="oztransui-modal-header">
                    <h2><FontAwesomeIcon icon={faExclamationTriangle} /> {t('confirmDelete')}</h2>
                    <button className="oztransui-modal-close" onClick={() => setShowDeleteModal(false)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="oztransui-modal-body">
                    <p>Are you sure you want to delete this transaction?</p>
                    <p className="oztransui-warning-text">{t('thisActionCannotBeUndone')}</p>
                    {selectedTransaction && (
                        <div className="oztransui-delete-preview">
                            <div><strong>Type:</strong> {TRANSACTION_TYPES[selectedTransaction.transaction_type]?.label || selectedTransaction.transaction_type}</div>
                            <div><strong>Amount:</strong> Rs. {formatCurrency(selectedTransaction.grand_total || selectedTransaction.amount || 0)}</div>
                            <div><strong>Reference:</strong> {selectedTransaction.reference_name || '-'}</div>
                        </div>
                    )}
                </div>
                <div className="oztransui-modal-footer">
                    <button className="oztransui-btn-secondary" onClick={() => setShowDeleteModal(false)}>
                        {t('cancel')}
                    </button>
                    <button className="oztransui-btn-danger" onClick={handleDeleteTransaction} disabled={loading}>
                        {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrash} />}
                        {t('delete')}
                    </button>
                </div>
            </div>
        </div>
    );

    const isProfitLossTab = activeTab === 'profit_loss';
    const isDailyOCTab = activeTab === 'daily_oc';
    const isTransactionTab = !isProfitLossTab && !isDailyOCTab;

    return (
        <div className={`oztransui-container ${isRTL ? 'oztransui-rtl' : 'oztransui-ltr'} ${darkMode ? 'oztransui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} t={t} />

            {renderTabs()}

            {/* {isDailyOCTab && (
                <DailySummary
                    currentShop={currentShop}
                    t={t}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                    showToast={showToast}
                    user={user}
                    showHistory
                />
            )} */}

            {isTransactionTab && (
                <div className="oztransui-filters">
                    <div className="oztransui-filters-row">
                        {activeTab === 'all' && (
                            <>
                                <div className="oztransui-filter-item">
                                    <FilterDropdown
                                        label={t('entity')}
                                        icon={faLayerGroup}
                                        value={filters.entityType}
                                        onChange={(value) => {
                                            setFilters(prev => ({
                                                ...prev,
                                                entityType: value,
                                                entityId: '',
                                                type: ''
                                            }));
                                            setPagination(prev => ({ ...prev, page: 1 }));
                                        }}
                                        options={entityTypeOptions}
                                    />
                                </div>
                                {filters.entityType && (
                                    <div className="oztransui-filter-item">
                                        <FilterDropdown
                                            label={
                                                filters.entityType === 'customer' ? t('selectCustomer') :
                                                    filters.entityType === 'supplier' ? t('selectSupplier') :
                                                        filters.entityType === 'partner' ? t('selectPartner') :
                                                            t('selectEmployee')
                                            }
                                            icon={
                                                filters.entityType === 'customer' ? faUsers :
                                                    filters.entityType === 'supplier' ? faTruck :
                                                        filters.entityType === 'partner' ? faHandshake :
                                                            faUserTie
                                            }
                                            value={filters.entityId}
                                            onChange={(value) => {
                                                setFilters(prev => ({
                                                    ...prev,
                                                    entityId: value,
                                                    type: ''
                                                }));
                                                setPagination(prev => ({ ...prev, page: 1 }));
                                            }}
                                            options={entityOptions}
                                            disabled={entityOptions.length <= 1}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        <div className="oztransui-filter-item">
                            <FilterDropdown
                                label="Type"
                                icon={faTag}
                                value={filters.type}
                                onChange={(val) => setFilters(prev => ({ ...prev, type: val }))}
                                options={typeOptions}
                            />
                        </div>
                       
                        <div className="oztransui-filter-item">
                            <div className="oztransui-date-input">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                <input
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                                    placeholder="Start"
                                />
                            </div>
                        </div>
                        <div className="oztransui-filter-item">
                            <div className="oztransui-date-input">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                <input
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                                    placeholder="End"
                                />
                            </div>
                        </div>
                       
                        <button className="oztransui-btn-reset" onClick={resetFilters}>
                            <FontAwesomeIcon icon={faTimes} /> {t('resetFilters')}
                        </button>
                        {activeTab === 'all' && (
                            <button
                                className="oztransui-btn-reset"
                                onClick={exportTransactionsReport}
                                disabled={pdfLoading || filteredData.length === 0}
                                style={{
                                    background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                                    color: '#ffffff',
                                    borderColor: '#b91c1c'
                                }}
                                title="Export the current All Transactions results as PDF"
                            >
                                <FontAwesomeIcon icon={pdfLoading ? faSpinner : faFilePdf} spin={pdfLoading} />
                                {pdfLoading ? 'Generating...' : 'Report'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {isTransactionTab && renderTransactionSummary()}

            {isProfitLossTab ? renderProfitLoss() : (isTransactionTab ? renderTable() : null)}

            {isTransactionTab && renderPagination()}

            {showAddModal && renderModal(false)}
            {showEditModal && renderModal(true)}
            {showDeleteModal && renderDeleteModal()}
        </div>
    );
};

export default CosmeticsTransactionsManagerSpecial;
